import { API_URL } from '../config/api';
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate, useLocation } from 'react-router-dom';
import TikTokCreatorsSearchModal from './TikTokCreatorsSearchModal';
import InstagramCreatorsSearchModal from './InstagramCreatorsSearchModal';
import YouTubeSearchModal from './YouTubeSearchModal';
import RelatedKeywordsModal from './RelatedKeywordsModal';
import TranslatedKeywordsModal from './TranslatedKeywordsModal';
import AlternativeKeywordsModal from './AlternativeKeywordsModal';
import SearchLogsSummary from './SearchLogsSummary';
import { generateTiktokPostUrl, generateInstagramPostUrl } from '../utils/postUrlHelper';
import { useAuth } from '../context/AuthContext';

interface Keyword {
  _id: string;
  keyword: string;
  group?: string;
  language?: string;
  instagramSearchTime: string | null;
  tiktokSearchTime: string | null;
  youtubeSearchTime?: string | null;
  instagramAccountCount?: number;
  tiktokAccountCount?: number;
  youtubeAccountCount?: number;
  createdAt: string;
  updatedAt: string;
  // Relevancy analysis fields
  relevancy?: boolean | null;
  relevancyScore?: number | null;
  relevancyReason?: string | null;
  relevantTopics?: string[];
  relevancyAnalyzedAt?: string | null;
}

interface Group {
  _id: string;
  name: string;
  count: number;
  instagramCount: number;
  tiktokCount: number;
}


interface PaginationInfo {
  page: number;
  limit: number;
  total: number;
  totalPages: number;
}

type SortField = 'keyword' | 'name' | 'language' | 'instagramSearchTime' | 'tiktokSearchTime' | 'youtubeSearchTime' | 'instagramAccountCount' | 'tiktokAccountCount' | 'youtubeAccountCount' | 'createdAt' | 'relevancy' | 'relevancyScore';
type SortOrder = 'asc' | 'desc';

interface ColumnConfig {
  key: SortField | 'actions' | 'group';
  label: string;
  width: number;
  visible: boolean;
}

const Find: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { token } = useAuth();
  
  // Determine initial tab based on URL
  const getInitialTab = () => {
    const path = location.pathname;
    if (path.includes('/find/keywords')) return 'keywords';
    if (path.includes('/find/automation')) return 'automation';
    if (path.includes('/find/groups')) return 'groups';
    if (path.includes('/find/relevance')) return 'relevance';
    if (path.includes('/find/similar')) return 'similar';
    return 'keywords'; // default
  };

  // Main tab state (Keywords vs Groups vs Relevance vs Automation vs Similar)
  const [mainTab, setMainTab] = useState<'keywords' | 'groups' | 'relevance' | 'automation' | 'similar'>(getInitialTab());
  
  // Keywords state
  const [keywords, setKeywords] = useState<Keyword[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [pagination, setPagination] = useState<PaginationInfo>({
    page: 1,
    limit: 100,
    total: 0,
    totalPages: 0
  });
  
  // Groups state
  const [groups, setGroups] = useState<Group[]>([]);
  const [selectedGroup, setSelectedGroup] = useState<string | null>(null);
  const [availableGroups, setAvailableGroups] = useState<string[]>(['ai', 'sidehustle']);

  // Similar state
  const [similarUsername, setSimilarUsername] = useState('');
  const [similarSearching, setSimilarSearching] = useState(false);
  const [similarResult, setSimilarResult] = useState<any>(null);
  const [similarError, setSimilarError] = useState<string | null>(null);
  const [showJsonModal, setShowJsonModal] = useState(false);
  const [registrationStatus, setRegistrationStatus] = useState<Record<string, boolean>>({});
  const [showRegisterModal, setShowRegisterModal] = useState(false);
  const [registeringUsername, setRegisteringUsername] = useState('');
  const [registerLogs, setRegisterLogs] = useState<Array<{message: string, type: string, timestamp: string}>>([]);
  const [verificationResult, setVerificationResult] = useState<any>(null);
  const [isVerifying, setIsVerifying] = useState(false);

  // Function to run verification
  const runVerification = useCallback(async (username: string) => {
    setIsVerifying(true);
    setVerificationResult(null);
    setRegisterLogs([]);

    try {
      const response = await fetch(`${API_URL}/api/similar/verify-register`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token && { 'Authorization': `Bearer ${token}` })
        },
        credentials: 'include',
        body: JSON.stringify({ username })
      });

      const reader = response.body?.getReader();
      const decoder = new TextDecoder();

      if (!reader) {
        throw new Error('No response body');
      }

      let finalResult: any = null;

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        const chunk = decoder.decode(value);
        const lines = chunk.split('\n\n');

        for (const line of lines) {
          if (line.startsWith('data: ')) {
            try {
              const data = JSON.parse(line.slice(6));

              if (data.done) {
                finalResult = data;
                setVerificationResult(data);

                // Auto-register if successful
                if (data.success) {
                  setRegistrationStatus(prev => ({
                    ...prev,
                    [username]: true
                  }));
                }
              } else {
                setRegisterLogs(prev => [...prev, data]);
              }
            } catch (err) {
              console.error('Parse error:', err);
            }
          }
        }
      }

      setIsVerifying(false);
      return finalResult;
    } catch (error) {
      console.error('Registration error:', error);
      setRegisterLogs(prev => [...prev, {
        message: `Error: ${error}`,
        type: 'error',
        timestamp: new Date().toISOString()
      }]);
      setIsVerifying(false);
      return null;
    }
  }, []);

  // Function to manually register
  const manualRegister = useCallback(async (username: string, email?: string, medianViews?: number, relevancyData?: any) => {
    try {
      const response = await fetch(`${API_URL}/api/similar/manual-register`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token && { 'Authorization': `Bearer ${token}` })
        },
        credentials: 'include',
        body: JSON.stringify({
          username,
          email,
          medianViews,
          relevancyData
        })
      });

      const data = await response.json();

      if (data.success) {
        setRegistrationStatus(prev => ({
          ...prev,
          [username]: true
        }));
        setRegisterLogs(prev => [...prev, {
          message: `✅ Manually registered successfully!`,
          type: 'success',
          timestamp: new Date().toISOString()
        }]);
      } else {
        setRegisterLogs(prev => [...prev, {
          message: `❌ Manual registration failed: ${data.error}`,
          type: 'error',
          timestamp: new Date().toISOString()
        }]);
      }

      return data;
    } catch (error) {
      console.error('Manual registration error:', error);
      setRegisterLogs(prev => [...prev, {
        message: `❌ Error: ${error}`,
        type: 'error',
        timestamp: new Date().toISOString()
      }]);
      return null;
    }
  }, []);

  // Function to search for similar profiles
  const searchSimilarProfiles = useCallback(async (username: string) => {
    if (!username.trim()) return;

    setSimilarSearching(true);
    setSimilarResult(null);
    setSimilarError(null);

    try {
      const response = await fetch(`${API_URL}/api/similar/profile`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token && { 'Authorization': `Bearer ${token}` })
        },
        credentials: 'include',
        body: JSON.stringify({
          username: username.trim()
        })
      });

      const data = await response.json();

      if (data.success) {
        setSimilarResult(data.data);

        // Check registration status for all profiles
        const profiles = data.data[0]?.relatedProfiles || [];
        if (profiles.length > 0) {
          const usernames = profiles.map((p: any) => p.username);
          const statusResponse = await fetch(`${API_URL}/api/similar/check-registration`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              ...(token && { 'Authorization': `Bearer ${token}` })
            },
            credentials: 'include',
            body: JSON.stringify({ usernames })
          });

          const statusData = await statusResponse.json();
          if (statusData.success) {
            const statusMap: Record<string, boolean> = {};
            statusData.data.forEach((item: any) => {
              statusMap[item.username] = item.isRegistered;
            });
            setRegistrationStatus(statusMap);
          }
        }
      } else {
        setSimilarError(data.error || 'Failed to fetch profile');
      }
    } catch (error) {
      console.error('Similar search error:', error);
      setSimilarError('Network error. Please try again.');
    } finally {
      setSimilarSearching(false);
    }
  }, []);

  // Check URL parameters for auto-search
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const usernameParam = params.get('username');

    if (usernameParam && mainTab === 'similar') {
      setSimilarUsername(usernameParam);
      searchSimilarProfiles(usernameParam);
    }
  }, [location.search, mainTab, searchSimilarProfiles]);

  // Column configuration state for keywords
  const [columns, setColumns] = useState<ColumnConfig[]>([
    { key: 'keyword', label: 'Keyword', width: 200, visible: true },
    { key: 'language', label: 'Language', width: 100, visible: true },
    { key: 'relevancy', label: 'Relevancy', width: 100, visible: true },
    { key: 'relevancyScore', label: 'Score', width: 80, visible: true },
    { key: 'instagramSearchTime', label: 'Instagram Search', width: 150, visible: true },
    { key: 'instagramAccountCount', label: 'IG Accounts', width: 100, visible: true },
    { key: 'tiktokSearchTime', label: 'TikTok Search', width: 150, visible: true },
    { key: 'tiktokAccountCount', label: 'TT Accounts', width: 100, visible: true },
    { key: 'youtubeSearchTime', label: 'YouTube Search', width: 150, visible: true },
    { key: 'youtubeAccountCount', label: 'YT Accounts', width: 100, visible: true },
    { key: 'createdAt', label: 'Created At', width: 120, visible: true },
    { key: 'group', label: 'Group', width: 100, visible: true },
    { key: 'actions', label: 'Actions', width: 600, visible: true }
  ]);
  
  // Column customization state
  const [showColumnCustomizer, setShowColumnCustomizer] = useState(false);
  const [draggedColumn, setDraggedColumn] = useState<number | null>(null);
  
  // Topics management state
  const [topics, setTopics] = useState<string[]>([]);
  const [topicsLoading, setTopicsLoading] = useState(false);
  const [newTopic, setNewTopic] = useState('');
  
  // Group deletion modal state
  const [showDeleteGroupModal, setShowDeleteGroupModal] = useState(false);
  const [groupToDelete, setGroupToDelete] = useState<{id: string, name: string} | null>(null);
  const [selectedTargetGroup, setSelectedTargetGroup] = useState('');
  
  // Pagination page input state
  const [pageInput, setPageInput] = useState<string>('');
  

  const tableRef = useRef<HTMLTableElement>(null);
  
  // Sort state - Load from localStorage or use defaults
  const [sortField, setSortField] = useState<SortField>(() => {
    const saved = localStorage.getItem('find_sortField');
    return (saved as SortField) || 'tiktokAccountCount';
  });
  const [sortOrder, setSortOrder] = useState<SortOrder>(() => {
    const saved = localStorage.getItem('find_sortOrder');
    return (saved as SortOrder) || 'desc';
  });
  // Search state
  const [searchTerm, setSearchTerm] = useState('');
  const [searchInput, setSearchInput] = useState('');
  
  // CRUD states for keywords
  const [showKeywordModal, setShowKeywordModal] = useState(false);
  const [editingKeyword, setEditingKeyword] = useState<Keyword | null>(null);
  const [keywordForm, setKeywordForm] = useState({
    keyword: '',
    group: 'ai'
  });
  const [isDragging, setIsDragging] = useState(false);
  const [selectedKeywords, setSelectedKeywords] = useState<string[]>([]);
  const [showGroupEditModal, setShowGroupEditModal] = useState(false);
  const [selectedNewGroup, setSelectedNewGroup] = useState<string>('ai');
  const [fetchingTrendingKeywords, setFetchingTrendingKeywords] = useState(false);
  const [fetchingAllTrending, setFetchingAllTrending] = useState(false);
  const [trendingOptions, setTrendingOptions] = useState({
    countryCode: 'US',
    period: '7',
    industry: '15000000000'
  });
  


  // TikTok search state
  const [showTikTokModal, setShowTikTokModal] = useState(false);
  const [tiktokSearching, setTiktokSearching] = useState(false);
  const [tiktokSearchResult, setTiktokSearchResult] = useState<any>(null);
  const [tiktokSearchKeyword, setTiktokSearchKeyword] = useState('');
  
  // Search progress logs
  const [searchLogs, setSearchLogs] = useState<string[]>([]);
  const [showSearchLogs, setShowSearchLogs] = useState(true); // Always show logs
  
  // Relevancy details modal
  const [showRelevancyModal, setShowRelevancyModal] = useState(false);
  const [selectedRelevancyData, setSelectedRelevancyData] = useState<any>(null);

  // YouTube search state
  const [showYouTubeModal, setShowYouTubeModal] = useState(false);
  const [youtubeSearching, setYoutubeSearching] = useState(false);
  const [youtubeSearchResult, setYoutubeSearchResult] = useState<any>(null);
  const [youtubeSearchKeyword, setYoutubeSearchKeyword] = useState('');
  const [tiktokSearchType, setTiktokSearchType] = useState<'keyword' | 'hashtag' | 'full-hashtag' | 'full-keyword'>('full-hashtag');
  
  // Instagram search state
  const [showInstagramModal, setShowInstagramModal] = useState(false);
  const [instagramSearching, setInstagramSearching] = useState(false);
  const [instagramSearchResult, setInstagramSearchResult] = useState<any>(null);
  const [instagramSearchKeyword, setInstagramSearchKeyword] = useState('');
  const [instagramMinViews, setInstagramMinViews] = useState<number>(1000);
  const [instagramMaxItems, setInstagramMaxItems] = useState<number>(100);
  
  // Related keywords modal state
  const [showRelatedKeywordsModal, setShowRelatedKeywordsModal] = useState(false);
  const [relatedKeywordTarget, setRelatedKeywordTarget] = useState<string>('');
  
  // Translated keywords modal state
  const [showTranslatedKeywordsModal, setShowTranslatedKeywordsModal] = useState(false);
  const [translatedKeywordTarget, setTranslatedKeywordTarget] = useState<string>('');

  // Alternative keywords modal state
  const [showAlternativeKeywordsModal, setShowAlternativeKeywordsModal] = useState(false);
  const [alternativeKeywordTarget, setAlternativeKeywordTarget] = useState<string>('');
  const [instagramEmailStatus, setInstagramEmailStatus] = useState<Record<string, { status: 'loading' | 'found' | 'not-found', email?: string }>>({});
  const [instagramEmailFilter, setInstagramEmailFilter] = useState(false);
  const [addingCreators, setAddingCreators] = useState<Set<string>>(new Set());
  const [addedCreators, setAddedCreators] = useState<Set<string>>(new Set());
  const [existingCreators, setExistingCreators] = useState<Set<string>>(new Set());
  // Instagram specific creator states
  const [instagramAddingCreators, setInstagramAddingCreators] = useState<Set<string>>(new Set());
  const [instagramAddedCreators, setInstagramAddedCreators] = useState<Set<string>>(new Set());
  const [instagramExistingCreators, setInstagramExistingCreators] = useState<Set<string>>(new Set());
  // YouTube specific creator states
  const [youtubeAddingCreators, setYoutubeAddingCreators] = useState<Set<string>>(new Set());
  const [youtubeAddedCreators, setYoutubeAddedCreators] = useState<Set<string>>(new Set());
  const [youtubeExistingCreators, setYoutubeExistingCreators] = useState<Set<string>>(new Set());
  // Store applied filters with search results
  const [appliedFilters, setAppliedFilters] = useState<{
    minViews: number;
    minLikes: number;
    minComments: number;
  } | null>(null);
  
  // TikTok results sorting and filtering
  const [tiktokSortBy, setTiktokSortBy] = useState<'views' | 'likes' | 'comments' | 'date'>('views');
  // Always use 'desc' (high to low) for sorting
  const tiktokSortOrder = 'desc' as const;
  const [tiktokMinViews, setTiktokMinViews] = useState<number>(5000);
  const [tiktokMinLikes, setTiktokMinLikes] = useState<number>(0);
  const [tiktokMinComments, setTiktokMinComments] = useState<number>(0);
  
  // TikTok bulk selection
  const [selectedPosts, setSelectedPosts] = useState<Set<string>>(new Set());
  const [bulkAddingPosts, setBulkAddingPosts] = useState(false);
  const [bulkAddingProgress, setBulkAddingProgress] = useState({ current: 0, total: 0 });
  // YouTube bulk selection
  const [selectedYouTubeVideos, setSelectedYouTubeVideos] = useState<Set<string>>(new Set());
  const [bulkAddingYouTubeVideos, setBulkAddingYouTubeVideos] = useState(false);
  
  // Transcript modal state
  const [showTranscriptModal, setShowTranscriptModal] = useState(false);
  const [transcriptData, setTranscriptData] = useState<any>(null);
  const [transcriptLoading, setTranscriptLoading] = useState(false);
  const [transcriptError, setTranscriptError] = useState<string | null>(null);
  const [transcriptVideoUrl, setTranscriptVideoUrl] = useState<string>('');
  
  // Search cache
  
  // Search cancellation
  const [currentSearchController, setCurrentSearchController] = useState<AbortController | null>(null);
  const [tiktokSearchParams, setTiktokSearchParams] = useState({
    period: '30',
    days: 30, // for full-hashtag search
    sorting: '0',
    country: '',
    get_author_stats: true, // Always true - author stats always included
    exclude_existing: true, // Changed to true by default
    email_filter: true, // Default to true - only show creators with emails
    // relevance_filter removed - always performed automatically
    max_cursor: 10000, // for full-hashtag search
    remap_output: false,
    // Additional parameters for different search types
    cursor: 0, // for hashtag and keyword search pagination
    match_exactly: false, // for keyword searches
    enable_multi_cursor: true, // for automatic multi-cursor search
    multi_cursor_max: 1000 // maximum cursor value for multi-cursor search
  });

  // TikTok Automation Modal States
  const [showTikTokAutomationModal, setShowTikTokAutomationModal] = useState(false);
  const [tiktokAutomationMinViews, setTiktokAutomationMinViews] = useState<number>(5000);
  const [tiktokAutomationPeriod, setTiktokAutomationPeriod] = useState<string>('180');
  const [tiktokAutomationSkipDays, setTiktokAutomationSkipDays] = useState<number>(14);
  const [tiktokAutomationSearchType, setTiktokAutomationSearchType] = useState<'keyword' | 'hashtag' | 'full-hashtag' | 'full-keyword'>('full-hashtag');
  const [tiktokAutomationMaxCursor, setTiktokAutomationMaxCursor] = useState<number>(10000);
  const [tiktokAutomationDays, setTiktokAutomationDays] = useState<number>(180);
  const [tiktokAutomationKeywords, setTiktokAutomationKeywords] = useState<any[]>([]);
  const [selectedAutomationKeywords, setSelectedAutomationKeywords] = useState<Set<string>>(new Set());
  const [loadingAutomationKeywords, setLoadingAutomationKeywords] = useState(false);
  const [automationSearchResults, setAutomationSearchResults] = useState<any[]>([]);
  const [isAutomationRunning, setIsAutomationRunning] = useState(false);
  const [currentAutomationIndex, setCurrentAutomationIndex] = useState(0);
  const [totalAutomationKeywords, setTotalAutomationKeywords] = useState(0);
  const [currentAutomationKeyword, setCurrentAutomationKeyword] = useState<string | null>(null);
  const [loadingSearchHistory, setLoadingSearchHistory] = useState(false);
  const [automationBatchId, setAutomationBatchId] = useState<string | null>(null);
  const automationStopRef = useRef(false);

  // Automation history pagination
  const [historyPagination, setHistoryPagination] = useState({
    page: 1,
    limit: 50,
    total: 0,
    totalPages: 0
  });

  // Modals for automation results
  const [showAddedAccountsModal, setShowAddedAccountsModal] = useState(false);
  const [selectedAddedAccounts, setSelectedAddedAccounts] = useState<any>(null);
  const [showKeywordResultsModal, setShowKeywordResultsModal] = useState(false);
  const [selectedKeywordResult, setSelectedKeywordResult] = useState<any>(null);
  const [creatorAddingStatus, setCreatorAddingStatus] = useState<Record<string, 'waiting' | 'adding' | 'added' | 'error'>>({});

  // AI Keyword Generator states for Create Keyword Modal
  const [loadingAiSuggestions, setLoadingAiSuggestions] = useState(false);
  const [customPrompt, setCustomPrompt] = useState('');

  // Fetch keywords with proper pagination
  const fetchKeywords = useCallback(async (page?: number) => {
    setLoading(true);
    setError(null);

    const currentPage = page || pagination.page;

    const params = new URLSearchParams({
      page: currentPage.toString(),
      limit: '100',
      sortBy: sortField,
      sortOrder: sortOrder,
      search: searchTerm
    });

    // Add group filter if selected
    if (selectedGroup) {
      params.append('group', selectedGroup);
    }

    const url = `${API_URL}/api/keywords?${params}`;

    try {
      const response = await fetch(url, {
        headers: {
          ...(token && { 'Authorization': `Bearer ${token}` })
        }
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();

      if (data.success && data.pagination) {
        setKeywords(data.data);
        setPagination(data.pagination);
      } else {
        setError(data.error || 'Failed to fetch keywords');
      }
    } catch (err) {
      setError('Failed to connect to server');
      console.error('Error fetching keywords:', err);
    } finally {
      setLoading(false);
    }
  }, [sortField, sortOrder, searchTerm, selectedGroup, token]); // Remove pagination.page from dependencies
  
  // Fetch groups
  const fetchGroups = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/api/groups`, {
        headers: {
          ...(token && { 'Authorization': `Bearer ${token}` })
        }
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();

      if (data.success) {
        setGroups(data.data);
      }
    } catch (err) {
      console.error('Error fetching groups:', err);
    }
  }, [token]);


  // Fetch trending keywords from TikTok
  const fetchTrendingKeywords = async () => {
    setFetchingTrendingKeywords(true);
    try {
      const response = await fetch(`${API_URL}/api/keywords/trending`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token && { 'Authorization': `Bearer ${token}` })
        },
        body: JSON.stringify({
          countryCode: trendingOptions.countryCode,
          industry: trendingOptions.industry,
          maxItems: 200,
          period: trendingOptions.period
        })
      });

      const data = await response.json();
      
      if (data.success && data.hashtags && data.hashtags.length > 0) {
        // Extract hashtag names and join with commas
        const hashtagNames = data.hashtags
          .filter((h: any) => h.hashtag_name)
          .map((h: any) => h.hashtag_name)
          .slice(0, 20) // Limit to first 20 hashtags
          .join(', ');
        
        // Append to existing keywords or replace
        setKeywordForm(prev => ({
          ...prev,
          keyword: prev.keyword 
            ? `${prev.keyword}, ${hashtagNames}`
            : hashtagNames
        }));
        
        setSuccessMessage(`✅ Fetched ${data.hashtags.length} trending keywords`);
        setTimeout(() => setSuccessMessage(null), 3000);
      } else {
        setError('No trending keywords found');
        setTimeout(() => setError(null), 3000);
      }
    } catch (error) {
      console.error('Error fetching trending keywords:', error);
      setError('Failed to fetch trending keywords');
      setTimeout(() => setError(null), 3000);
    } finally {
      setFetchingTrendingKeywords(false);
    }
  };

  // Fetch keyword automation logs


  // Fetch trending keywords from all countries
  const fetchAllTrendingKeywords = async () => {
    setFetchingAllTrending(true);
    try {
      const response = await fetch(`${API_URL}/api/keywords/trending-all`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token && { 'Authorization': `Bearer ${token}` })
        },
        body: JSON.stringify({
          industry: trendingOptions.industry,
          maxItems: 200,
          period: trendingOptions.period
        })
      });

      const data = await response.json();
      
      if (data.success && data.hashtags && data.hashtags.length > 0) {
        // Extract unique hashtag names and join with commas
        const hashtagNames = data.hashtags
          .filter((h: any) => h.hashtag_name)
          .map((h: any) => h.hashtag_name)
          .slice(0, 50) // Limit to first 50 hashtags due to larger dataset
          .join(', ');
        
        // Append to existing keywords or replace
        setKeywordForm(prev => ({
          ...prev,
          keyword: prev.keyword 
            ? `${prev.keyword}, ${hashtagNames}`
            : hashtagNames
        }));
        
        setSuccessMessage(`✅ Fetched ${data.count} unique keywords from ${data.stats?.successfulCountries || 0} countries`);
        setTimeout(() => setSuccessMessage(null), 3000);
      } else {
        setError('No trending keywords found from any country');
        setTimeout(() => setError(null), 3000);
      }
    } catch (error) {
      console.error('Error fetching trending keywords from all countries:', error);
      setError('Failed to fetch trending keywords from all countries');
      setTimeout(() => setError(null), 3000);
    } finally {
      setFetchingAllTrending(false);
    }
  };

  // CRUD functions for keywords
  const createKeyword = async () => {
    try {
      // Split keywords by comma, clean each one, filter out empty ones, and remove duplicates
      const keywords = Array.from(new Set(keywordForm.keyword
        .split(',')
        .map(k => k.toLowerCase().replace(/\s+/g, '').replace(/#/g, '').trim())
        .filter(k => k.length > 0)));

      if (keywords.length === 0) {
        setError('Please enter at least one keyword');
        return;
      }

      // Create all keywords
      const results = await Promise.all(
        keywords.map(async (keyword) => {
          try {
            const response = await fetch(`${API_URL}/api/keywords`, {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                ...(token && { 'Authorization': `Bearer ${token}` })
              },
              body: JSON.stringify({
                keyword,
                group: keywordForm.group
              }),
            });
            const data = await response.json();
            return { keyword, success: response.ok, response, data };
          } catch (error) {
            return { keyword, success: false, error: error instanceof Error ? error.message : 'Unknown error' };
          }
        })
      );

      // Check results
      const failed = results.filter(r => !r.success);
      const successful = results.filter(r => r.success);
      const duplicates = failed.filter(f => f.data?.error === 'Keyword already exists');
      const actualErrors = failed.filter(f => f.data?.error !== 'Keyword already exists');
      
      // Handle duplicates as info, not error
      if (duplicates.length > 0 && actualErrors.length === 0 && successful.length === 0) {
        setError(`All keywords already exist: ${duplicates.map(d => d.keyword).join(', ')}`);
      } else {
        // Handle actual errors
        if (actualErrors.length > 0) {
          const errorDetails = actualErrors.map(f => {
            const errorMsg = f.data?.error || f.error || 'Unknown error';
            return `${f.keyword} (${errorMsg})`;
          }).join(', ');
          setError(`Failed to create keywords: ${errorDetails}`);
        }
        
        // Show success message with info about duplicates
        if (successful.length > 0) {
          const successKeywords = successful.map(s => s.keyword).join(', ');
          let message = `✅ Successfully created: ${successKeywords}`;
          if (duplicates.length > 0) {
            message += `. Already existed: ${duplicates.map(d => d.keyword).join(', ')}`;
          }
          setSuccessMessage(message);
          setError(''); // Clear any errors
          
          // Clear success message after 5 seconds
          setTimeout(() => setSuccessMessage(null), 5000);
        }
      }
      
      if (successful.length > 0) {
        fetchKeywords();
        if (actualErrors.length === 0) { // Close modal if no actual errors
          setShowKeywordModal(false);
          resetKeywordForm();
        }
      }
    } catch (error) {
      setError('Failed to create keywords');
      console.error('Error creating keyword:', error);
    }
  };

  const updateKeyword = async () => {
    if (!editingKeyword) return;
    
    try {
      const response = await fetch(`${API_URL}/api/keywords/${editingKeyword._id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          ...(token && { 'Authorization': `Bearer ${token}` })
        },
        body: JSON.stringify({
          keyword: keywordForm.keyword.toLowerCase().replace(/\s+/g, '').replace(/#/g, '').trim(),
          group: keywordForm.group
        }),
      });

      if (response.ok) {
        setShowKeywordModal(false);
        setEditingKeyword(null);
        resetKeywordForm();
        fetchKeywords();
      } else {
        const data = await response.json();
        setError(data.error || 'Failed to update keyword');
      }
    } catch (error) {
      setError('Failed to update keyword');
      console.error('Error updating keyword:', error);
    }
  };

  const deleteKeyword = async (id: string) => {
    if (!window.confirm('Are you sure you want to delete this keyword?')) return;
    
    try {
      const response = await fetch(`${API_URL}/api/keywords/${id}`, {
        method: 'DELETE',
        headers: {
          ...(token && { 'Authorization': `Bearer ${token}` })
        }
      });

      if (response.ok) {
        fetchKeywords();
      } else {
        const data = await response.json();
        setError(data.error || 'Failed to delete keyword');
      }
    } catch (error) {
      setError('Failed to delete keyword');
      console.error('Error deleting keyword:', error);
    }
  };

  const deleteSelectedKeywords = async () => {
    if (selectedKeywords.length === 0) return;
    if (!window.confirm(`Are you sure you want to delete ${selectedKeywords.length} keyword(s)?`)) return;
    
    try {
      for (const id of selectedKeywords) {
        await fetch(`${API_URL}/api/keywords/${id}`, {
          method: 'DELETE',
          headers: {
            ...(token && { 'Authorization': `Bearer ${token}` })
          }
        });
      }
      setSelectedKeywords([]);
      fetchKeywords();
    } catch (error) {
      setError('Failed to delete keywords');
      console.error('Error deleting keywords:', error);
    }
  };


  const updateSelectedKeywordsGroup = (newGroup: string) => {
    if (selectedKeywords.length === 0) return;
    
    const keywordsToUpdate = [...selectedKeywords]; // Copy the array before clearing
    
    console.log(`Updating ${keywordsToUpdate.length} keywords to group: ${newGroup}`);
    
    // Optimistically update the UI immediately
    setKeywords(prevKeywords => 
      prevKeywords.map(keyword => 
        keywordsToUpdate.includes(keyword._id) 
          ? { ...keyword, group: newGroup }
          : keyword
      )
    );
    
    // Clear selection immediately
    setSelectedKeywords([]);
    
    // Close modal immediately
    setShowGroupEditModal(false);
    
    // Update in background without any await or promise handling that might trigger re-renders
    setTimeout(() => {
      keywordsToUpdate.forEach(id => {
        fetch(`${API_URL}/api/keywords/${id}`, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            ...(token && { 'Authorization': `Bearer ${token}` })
          },
          body: JSON.stringify({ group: newGroup }),
        })
        .then(response => {
          if (!response.ok) {
            console.error(`Failed to update keyword ${id}`);
          }
        })
        .catch(error => {
          console.error(`Error updating keyword ${id}:`, error);
        });
      });
    }, 0);
  };

  const resetKeywordForm = () => {
    setKeywordForm({
      keyword: '',
      group: 'ai'
    });
  };

  const openCreateModal = () => {
    setEditingKeyword(null);
    resetKeywordForm();
    setShowKeywordModal(true);
    // Reset AI generator state
    setCustomPrompt('');
  };

  // Fetch AI suggestions and append to keyword field
  const fetchAiSuggestions = async () => {
    if (!customPrompt.trim()) {
      alert('Please enter a prompt to generate keywords');
      return;
    }

    setLoadingAiSuggestions(true);

    try {
      // Use the current keyword as base if exists, otherwise use first word from prompt
      const baseKeyword = keywordForm.keyword.trim() || customPrompt.split(' ')[0];

      const response = await fetch(`${API_URL}/api/keywords/ai-suggestions`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token && { 'Authorization': `Bearer ${token}` })
        },
        body: JSON.stringify({
          baseKeyword: baseKeyword,
          customPrompt: customPrompt.trim(),
          excludeKeywords: []
        }),
      });

      const data = await response.json();

      if (data.success) {
        // Get only new (non-existing) keywords
        const newKeywords = data.data.suggestions
          .filter((s: any) => !s.isExisting)
          .map((s: any) => s.keyword);

        if (newKeywords.length > 0) {
          // Append to existing keywords with comma separation
          const currentKeywords = keywordForm.keyword.trim();
          const updatedKeywords = currentKeywords
            ? `${currentKeywords}, ${newKeywords.join(', ')}`
            : newKeywords.join(', ');

          setKeywordForm(prev => ({ ...prev, keyword: updatedKeywords }));
          setSuccessMessage(`Added ${newKeywords.length} keywords to the field`);
        } else {
          alert('No new keywords found. All suggested keywords already exist.');
        }
      } else {
        alert(data.error || 'Failed to get AI suggestions');
      }
    } catch (err) {
      console.error('Error fetching AI suggestions:', err);
      alert('Failed to fetch AI suggestions');
    } finally {
      setLoadingAiSuggestions(false);
    }
  };


  const openEditModal = (keyword: Keyword) => {
    setEditingKeyword(keyword);
    setKeywordForm({
      keyword: keyword.keyword,
      group: keyword.group || 'ai'
    });
    setShowKeywordModal(true);
  };
  
  // Topics management functions
  const fetchTopics = useCallback(async () => {
    setTopicsLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/relevant-topics`, {
        headers: {
          ...(token && { 'Authorization': `Bearer ${token}` })
        }
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const data = await response.json();
      setTopics(data);
    } catch (err) {
      console.error('Error fetching topics:', err);
      setError('Failed to fetch topics');
    } finally {
      setTopicsLoading(false);
    }
  }, []);

  const addTopic = async () => {
    if (!newTopic.trim()) return;

    // Split by comma and filter empty strings
    const words = newTopic.split(',').map(w => w.trim()).filter(w => w.length > 0);
    if (words.length === 0) return;

    try {
      let latestTopics = topics;
      for (const word of words) {
        const response = await fetch(`${API_URL}/api/relevant-topics`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...(token && { 'Authorization': `Bearer ${token}` })
          },
          body: JSON.stringify({ word }),
        });

        if (response.ok) {
          latestTopics = await response.json();
        } else {
          const data = await response.json();
          setError(data.error || `Failed to add topic: ${word}`);
        }
      }
      setTopics(latestTopics);
      setNewTopic('');
    } catch (error) {
      setError('Failed to add topic');
      console.error('Error adding topic:', error);
    }
  };

  const deleteTopic = async (word: string) => {
    try {
      const response = await fetch(`${API_URL}/api/relevant-topics/${encodeURIComponent(word)}`, {
        method: 'DELETE',
        headers: {
          ...(token && { 'Authorization': `Bearer ${token}` })
        }
      });

      if (response.ok) {
        const data = await response.json();
        setTopics(data);
      } else {
        const data = await response.json();
        setError(data.error || 'Failed to delete topic');
      }
    } catch (error) {
      setError('Failed to delete topic');
      console.error('Error deleting topic:', error);
    }
  };

  // Groups management functions
  const initiateDeleteGroup = (groupId: string, groupName: string) => {
    const remainingGroups = groups.filter(g => g._id !== groupId);
    
    // Prevent deleting the last group
    if (remainingGroups.length === 0) {
      alert('Cannot delete the last group. At least one group must exist.');
      return;
    }
    
    setGroupToDelete({id: groupId, name: groupName});
    setSelectedTargetGroup(remainingGroups[0]?._id || ''); // Pre-select first group
    setShowDeleteGroupModal(true);
  };

  const confirmDeleteGroup = async () => {
    if (!groupToDelete || !selectedTargetGroup) return;
    
    try {
      const response = await fetch(`${API_URL}/api/groups/${groupToDelete.id}`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          ...(token && { 'Authorization': `Bearer ${token}` })
        },
        body: JSON.stringify({ reassignTo: selectedTargetGroup }),
      });

      if (response.ok) {
        // Refresh groups and keywords after deletion
        fetchGroups();
        fetchKeywords();
        // Clear selected group if it was the deleted one
        if (selectedGroup === groupToDelete.name) {
          setSelectedGroup(null);
        }
        // Close modal
        setShowDeleteGroupModal(false);
        setGroupToDelete(null);
        setSelectedTargetGroup('');
      } else {
        const data = await response.json();
        setError(data.error || 'Failed to delete group');
      }
    } catch (error) {
      setError('Failed to delete group');
      console.error('Error deleting group:', error);
    }
  };
  











  // Add log message with timestamp
  const addSearchLog = (message: string) => {
    const timestamp = new Date().toLocaleTimeString();
    setSearchLogs(prev => [...prev, `[${timestamp}] ${message}`]);
  };

  // Clear search logs
  const clearSearchLogs = () => {
    setSearchLogs([]);
  };

  // Open TikTok search modal with keyword name
  const openTikTokSearchModal = (keyword: string) => {
    setTiktokSearchKeyword(keyword);
    setTiktokSearchResult(null);
    setSelectedPosts(new Set());
    setShowTikTokModal(true);
    clearSearchLogs();
    setShowSearchLogs(false);
  };

  // Open Instagram search modal with keyword
  const openInstagramSearchModal = (keyword: string) => {
    setInstagramSearchKeyword(keyword);
    setInstagramSearchResult(null);
    setShowInstagramModal(true);
    clearSearchLogs();
  };

  // Open YouTube search modal with keyword
  const openYouTubeSearchModal = (keyword: string) => {
    setYoutubeSearchKeyword(keyword);
    setYoutubeSearchResult(null);
    setShowYouTubeModal(true);
    clearSearchLogs();
    setShowSearchLogs(false);
  };
  
  // Open related keywords modal
  const openRelatedKeywordsModal = (keyword: string) => {
    setRelatedKeywordTarget(keyword);
    setShowRelatedKeywordsModal(true);
  };
  
  // Open translated keywords modal
  const openTranslatedKeywordsModal = (keyword: string) => {
    setTranslatedKeywordTarget(keyword);
    setShowTranslatedKeywordsModal(true);
  };

  // Open alternative keywords modal
  const openAlternativeKeywordsModal = (keyword: string) => {
    setAlternativeKeywordTarget(keyword);
    setShowAlternativeKeywordsModal(true);
  };
  
  // Fetch keywords for TikTok automation based on tiktokSearchTime
  const fetchAutomationKeywords = async () => {
    setLoadingAutomationKeywords(true);
    try {
      const skipDate = new Date();
      skipDate.setDate(skipDate.getDate() - tiktokAutomationSkipDays);

      const response = await fetch(`${API_URL}/api/keywords?` + new URLSearchParams({
        limit: '1000',
        sortBy: 'tiktokSearchTime',
        sortOrder: 'desc'
      }), {
        headers: {
          ...(token && { 'Authorization': `Bearer ${token}` })
        }
      });

      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          // Filter keywords based on tiktokSearchTime and skipDays
          const filteredKeywords = (data.data || []).filter((keyword: any) => {
            if (!keyword.tiktokSearchTime) return true; // Include keywords never searched
            const searchTime = new Date(keyword.tiktokSearchTime);
            return searchTime < skipDate; // Only include if searched before skip date
          });
          setTiktokAutomationKeywords(filteredKeywords);
        }
      }
    } catch (error) {
      console.error('Error fetching automation keywords:', error);
    } finally {
      setLoadingAutomationKeywords(false);
    }
  };

  // Fetch search history from database
  const fetchSearchHistory = async (showLoading = false, page?: number) => {
    const currentPage = page || historyPagination.page;
    console.log(`Fetching search history from database (page ${currentPage})...`);
    // Only show loading on initial fetch, not on polling updates
    if (showLoading) {
      setLoadingSearchHistory(true);
    }
    try {
      // Fetch with pagination
      const response = await fetch(`${API_URL}/api/keywords/search-history?searchType=automation&platform=tiktok&limit=${historyPagination.limit}&page=${currentPage}`, {
        headers: {
          ...(token && { 'Authorization': `Bearer ${token}` })
        }
      });
      const data = await response.json();
      console.log('Search history response:', data);
      if (data.success) {
        // Update pagination info from pagination object
        setHistoryPagination({
          page: data.pagination.page,
          limit: data.pagination.limit,
          total: data.pagination.total,
          totalPages: data.pagination.pages
        });

        // Transform search history data to match the existing format
        const transformedResults = data.data.map((item: any) => ({
          keyword: item.keyword,
          platform: item.platform,
          status: item.status,
          startTime: new Date(item.searchStartTime),
          endTime: item.searchEndTime ? new Date(item.searchEndTime) : null,
          foundCount: item.results?.totalFound || 0,
          addedCount: item.results?.addedToDatabase || 0,
          withEmails: item.results?.withEmails || 0,
          processedCreators: item.results?.processedCreators || 0, // Add progress tracking
          error: item.error,
          searchHistoryId: item._id,
          automationBatchId: item.automationBatchId,
          searchParams: item.searchParams,
          data: item.searchData || [], // Include actual search data
          addedCreators: item.addedCreators || [], // Include added creators list
          searchLogs: item.searchLogs || [], // Include search logs
          _id: item._id // Add _id for deletion
        }));
        // Sort by startTime descending (newest first)
        const sortedResults = transformedResults.sort((a: any, b: any) => {
          const timeA = a.startTime ? new Date(a.startTime).getTime() : 0;
          const timeB = b.startTime ? new Date(b.startTime).getTime() : 0;
          return timeB - timeA; // Descending order (newest first)
        });
        setAutomationSearchResults(sortedResults);
      }
    } catch (error) {
      console.error('Error fetching search history:', error);
    }
    if (showLoading) {
      setLoadingSearchHistory(false);
    }
  };

  // Handle delete automation result
  const handleDeleteAutomationResult = async (searchHistoryId: string, index: number) => {
    if (!searchHistoryId) {
      console.error('No search history ID provided');
      return;
    }

    const confirmed = window.confirm('이 automation 기록을 삭제하시겠습니까?');
    if (!confirmed) return;

    try {
      const response = await fetch(`${API_URL}/api/keywords/search-history/${searchHistoryId}`, {
        method: 'DELETE',
        headers: {
          ...(token && { 'Authorization': `Bearer ${token}` })
        }
      });
      
      if (response.status === 404) {
        // Record not found - likely already deleted
        alert('해당 기록이 이미 삭제되었거나 존재하지 않습니다. 목록을 새로고침합니다.');
        setAutomationSearchResults(prev => prev.filter((_, i) => i !== index));
        fetchSearchHistory(false);
        return;
      }
      
      const data = await response.json();
      
      if (data.success) {
        // Remove the item from the current results
        setAutomationSearchResults(prev => prev.filter((_, i) => i !== index));
        console.log('Automation result deleted successfully');
        // Also refresh the data to ensure consistency
        setTimeout(() => fetchSearchHistory(false), 1000);
      } else {
        console.error('Failed to delete automation result:', data.error);
        alert('삭제에 실패했습니다: ' + data.error);
      }
    } catch (error) {
      console.error('Error deleting automation result:', error);
      if (error instanceof Error && error.message?.includes('404')) {
        alert('해당 기록이 이미 삭제되었거나 존재하지 않습니다. 페이지를 새로고침합니다.');
        // Refresh data if record not found
        fetchSearchHistory(false);
      } else {
        alert('삭제 중 오류가 발생했습니다.');
      }
    }
  };

  // Run TikTok automation search
  // Start TikTok automation using backend API
  const runTikTokAutomation = async () => {
    // Filter keywords to only selected ones
    const selectedKeywordsList = tiktokAutomationKeywords.filter(k => selectedAutomationKeywords.has(k._id));

    if (selectedKeywordsList.length === 0) {
      alert('Please select at least one keyword');
      return;
    }

    try {
      const response = await fetch(`${API_URL}/api/automation/start-tiktok`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token && { 'Authorization': `Bearer ${token}` })
        },
        body: JSON.stringify({
          minViews: tiktokAutomationMinViews,
          period: tiktokAutomationPeriod,
          skipDays: tiktokAutomationSkipDays,
          searchType: tiktokAutomationSearchType,
          maxCursor: tiktokAutomationMaxCursor,
          days: tiktokAutomationDays,
          selectedKeywordIds: Array.from(selectedAutomationKeywords) // Send selected keyword IDs
        }),
      });

      const data = await response.json();

      if (data.success) {
        console.log('Automation started:', data);
        setIsAutomationRunning(true);
        setAutomationBatchId(data.batchId);
        setTotalAutomationKeywords(data.totalKeywords || selectedKeywordsList.length);
        setCurrentAutomationIndex(0);
        setCurrentAutomationKeyword(null);
        // Close modal and show stop button
        setShowTikTokAutomationModal(false);
        // Start polling for status
        startPollingAutomationStatus();
      } else {
        console.error('Failed to start automation:', data.error || data.message);
        alert(data.error || data.message || 'Failed to start automation');
      }
    } catch (error) {
      console.error('Error starting automation:', error);
      alert('Failed to start automation');
    }
  };

  // Stop TikTok automation
  const stopTikTokAutomation = async () => {
    try {
      const response = await fetch(`${API_URL}/api/automation/stop`, {
        method: 'POST',
        headers: {
          ...(token && { 'Authorization': `Bearer ${token}` })
        }
      });

      const data = await response.json();

      if (data.success) {
        console.log('Automation stop requested');
        setIsAutomationRunning(false);
        stopPollingAutomationStatus();
        // Refresh data
        fetchKeywords();
        fetchSearchHistory();
      }
    } catch (error) {
      console.error('Error stopping automation:', error);
    }
  };

  // Check automation status
  const checkAutomationStatus = async (shouldRefreshHistory = false) => {
    console.log('[Automation] Checking status...');
    try {
      const response = await fetch(`${API_URL}/api/automation/status`, {
        headers: {
          ...(token && { 'Authorization': `Bearer ${token}` })
        }
      });
      const data = await response.json();
      console.log('[Automation] Status response:', data);

      if (data.success && data.status === 'running') {
        const wasRunning = isAutomationRunning;
        setIsAutomationRunning(true);
        setAutomationBatchId(data.batchId);
        setCurrentAutomationIndex(data.processedKeywords);
        setTotalAutomationKeywords(data.totalKeywords);
        setCurrentAutomationKeyword(data.currentKeyword);

        // Always fetch ALL history results, not just current batch
        fetchSearchHistory(false);

        // Start polling if not already
        if (!pollingIntervalRef.current) {
          startPollingAutomationStatus();
        }
      } else if (data.status === 'completed' || data.status === 'stopped') {
        const wasRunning = isAutomationRunning;
        setIsAutomationRunning(false);
        setCurrentAutomationKeyword(null);
        stopPollingAutomationStatus();
        // Only refresh data when automation just completed
        if (wasRunning || shouldRefreshHistory) {
          fetchKeywords();
          fetchSearchHistory(false);
        }
      }
    } catch (error) {
      console.error('Error checking automation status:', error);
    }
  };

  // Polling management
  const pollingIntervalRef = useRef<any>(null);

  const startPollingAutomationStatus = () => {
    console.log('[Automation] Starting polling...');
    // Clear any existing interval first
    if (pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current);
    }
    // Poll every 3 seconds
    pollingIntervalRef.current = setInterval(() => {
      checkAutomationStatus();
    }, 3000);
    // Also check immediately
    checkAutomationStatus();
  };

  const stopPollingAutomationStatus = () => {
    if (pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current);
      pollingIntervalRef.current = null;
    }
  };

  // Search YouTube creators for keyword
  const searchYouTubeCreators = async () => {
    // Cancel any existing search
    if (currentSearchController) {
      currentSearchController.abort();
    }
    
    // Clear added creators state when doing a new search
    setAddedCreators(new Set());
    setExistingCreators(new Set());

    // Create new AbortController for this search
    const abortController = new AbortController();
    setCurrentSearchController(abortController);

    // Immediately show loading state and transition to result view
    setYoutubeSearching(true);
    setYoutubeSearchResult({ loading: true }); // Set a loading state to show the results view immediately

    try {
      const response = await fetch(`${API_URL}/api/keywords/search-youtube`, {
        signal: abortController.signal,
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token && { 'Authorization': `Bearer ${token}` })
        },
        body: JSON.stringify({
          keyword: youtubeSearchKeyword,
          depth: 1,
          start_cursor: '',
          period: 'overall',
          sorting: 'relevance',
          get_additional_info: false,
          min_views: 0
        }),
      });

      const data = await response.json();
      
      // Simple logging for debugging
      console.log('YouTube API Response:', {
        success: data.success,
        count: data.count,
        dataLength: Array.isArray(data.data) ? data.data.length : 0
      });
      
      if (data.success) {
        const resultWithMetadata = {
          ...data,
          searchTime: Date.now(),
          searchTerm: youtubeSearchKeyword
        };
        
        setYoutubeSearchResult(resultWithMetadata);
      } else {
        const errorResult = { error: data.error || 'Failed to search YouTube creators' };
        setYoutubeSearchResult(errorResult);
      }
    } catch (error: any) {
      if (error.name === 'AbortError') {
        console.log('YouTube search cancelled');
        return;
      }
      
      console.error('Error searching YouTube creators:', error);
      setYoutubeSearchResult({ error: error.message || 'Failed to search YouTube creators' });
    } finally {
      setYoutubeSearching(false);
      setCurrentSearchController(null);
    }
  };

  // Add multiple TikTok creators with concurrent processing
  const addMultipleTikTokCreators = async (posts: any[]) => {
    setBulkAddingPosts(true);
    setBulkAddingProgress({ current: 0, total: posts.length });

    // Mark all creators as "adding" initially
    const creatorNames = posts.map((post: any) => {
      const author = post.authorMeta || post.author || post.authorInfos || {};
      return author.name || author.unique_id || author.uniqueId || post.author?.unique_id || post.author_meta?.unique_id || post.authorInfos?.uniqueId;
    }).filter(Boolean);

    // Add all creators to addingCreators set
    setAddingCreators(prev => {
      const newSet = new Set(prev);
      creatorNames.forEach(name => newSet.add(name));
      return newSet;
    });

    const results = {
      success: 0,
      failed: 0,
      errors: [] as string[]
    };

    // Process in batches of 15 concurrent requests (increased from 10)
    const BATCH_SIZE = 15;
    const batches = [];

    for (let i = 0; i < posts.length; i += BATCH_SIZE) {
      batches.push(posts.slice(i, i + BATCH_SIZE));
    }

    console.log(`Processing ${posts.length} creators in ${batches.length} batches of ${BATCH_SIZE}`);
    let processedCount = 0;

    for (let batchIndex = 0; batchIndex < batches.length; batchIndex++) {
      const batch = batches[batchIndex];
      console.log(`Processing batch ${batchIndex + 1}/${batches.length} (${batch.length} creators)`);

      // Process current batch concurrently
      const batchPromises = batch.map(async (post) => {
        try {
          const author = post.authorMeta || post.author || post.authorInfos || {};
          // Use the same identifier as the modal for consistency
          const creatorName = author.name || author.unique_id || author.uniqueId || post.author?.unique_id || post.author_meta?.unique_id || post.authorInfos?.uniqueId;

          const requestBody = {
            username: creatorName,
            platform: 'tiktok',
            discoveryPostUrl: generateTiktokPostUrl(creatorName, getPostId(post)),
            keyword: tiktokSearchKeyword || 'manual_add' // Send the search keyword
          };

          console.log('Adding creator:', requestBody);

          const response = await fetch(`${API_URL}/api/creators/add-creator`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              ...(token && { 'Authorization': `Bearer ${token}` })
            },
            body: JSON.stringify(requestBody),
          });

          const data = await response.json();

          if (!response.ok) {
            console.error('Add creator failed:', data);
          }

          if (data.success) {
            // Move from adding to added
            if (creatorName) {
              setAddingCreators(prev => {
                const newSet = new Set(prev);
                newSet.delete(creatorName);
                return newSet;
              });
              setAddedCreators(prev => new Set(prev).add(creatorName));
            }
            return { success: true, creator: `@${creatorName}` };
          } else {
            // Remove from adding set on failure
            if (creatorName) {
              setAddingCreators(prev => {
                const newSet = new Set(prev);
                newSet.delete(creatorName);
                return newSet;
              });
              // If creator already exists, add to existing set
              if (data.error === 'Creator already exists') {
                setExistingCreators(prev => new Set(prev).add(creatorName));
              }
            }
            return {
              success: false,
              creator: `@${creatorName}`,
              error: data.error
            };
          }
        } catch (error) {
          const author = post.authorMeta || post.author || post.authorInfos || {};
          const creatorName = author.name || author.unique_id || author.uniqueId || post.author?.unique_id || post.author_meta?.unique_id || post.authorInfos?.uniqueId || 'unknown';
          // Remove from adding set on error
          if (creatorName !== 'unknown') {
            setAddingCreators(prev => {
              const newSet = new Set(prev);
              newSet.delete(creatorName);
              return newSet;
            });
          }
          return {
            success: false,
            creator: `@${creatorName}`,
            error: String(error)
          };
        } finally {
          // Update progress after each request completes
          processedCount++;
          setBulkAddingProgress({ current: processedCount, total: posts.length });
        }
      });

      // Wait for current batch to complete
      const batchResults = await Promise.all(batchPromises);
      
      // Process results
      for (const result of batchResults) {
        if (result.success) {
          results.success++;
        } else {
          results.failed++;
          results.errors.push(`${result.creator}: ${result.error}`);
        }
      }

      // Reduced delay between batches (from 100ms to 50ms)
      if (batchIndex < batches.length - 1) {
        await new Promise(resolve => setTimeout(resolve, 50));
      }
    }

    setBulkAddingPosts(false);
    setBulkAddingProgress({ current: 0, total: 0 });

    // Clear any remaining creators from adding set (in case of errors)
    setAddingCreators(new Set());

    // Show results
    if (results.success > 0) {
      alert(`Successfully added ${results.success} creator(s)${results.failed > 0 ? `, ${results.failed} failed` : ''}`);
    } else {
      alert(`Failed to add creators. ${results.errors.join('\n')}`);
    }

    // Clear selection
    setSelectedPosts(new Set());
  };

  // Add all visible posts
  const addAllVisiblePosts = async () => {
    if (!tiktokSearchResult || !Array.isArray(tiktokSearchResult.data)) return;

    const filteredPosts = tiktokSearchType === 'full-hashtag'
      ? getTopPostsPerCreator(tiktokSearchResult.data)
      : sortAndFilterTikTokResults(tiktokSearchResult.data);
    if (filteredPosts.length === 0) return;
    
    const confirmed = window.confirm(`Add all ${filteredPosts.length} visible creators?`);
    if (confirmed) {
      await addMultipleTikTokCreators(filteredPosts);
    }
  };

  // Helper function to get consistent post ID
  const getPostId = (post: any) => {
    return post.itemInfos?.id || post.aweme_id || post.id || post.video_id ||
           `${post.authorMeta?.name || post.author?.unique_id}-${post.createTime || post.create_time}`;
  };

  // Add selected posts
  const addSelectedPosts = async () => {
    if (!tiktokSearchResult || !Array.isArray(tiktokSearchResult.data) || selectedPosts.size === 0) return;

    const filteredPosts = tiktokSearchType === 'full-hashtag'
      ? getTopPostsPerCreator(tiktokSearchResult.data)
      : sortAndFilterTikTokResults(tiktokSearchResult.data);
    const selectedPostsData = filteredPosts.filter((post: any) =>
      selectedPosts.has(getPostId(post))
    );
    
    if (selectedPostsData.length === 0) return;
    
    const confirmed = window.confirm(`Add ${selectedPostsData.length} selected creator(s)?`);
    if (confirmed) {
      await addMultipleTikTokCreators(selectedPostsData);
    }
  };

  // Toggle post selection
  const togglePostSelection = (postUrl: string) => {
    setSelectedPosts(prev => {
      const newSet = new Set(prev);
      if (newSet.has(postUrl)) {
        newSet.delete(postUrl);
      } else {
        newSet.add(postUrl);
      }
      return newSet;
    });
  };

  // Select all visible posts
  const toggleSelectAllPosts = () => {
    if (!tiktokSearchResult || !Array.isArray(tiktokSearchResult.data)) return;

    const filteredPosts = tiktokSearchType === 'full-hashtag'
      ? getTopPostsPerCreator(tiktokSearchResult.data)
      : sortAndFilterTikTokResults(tiktokSearchResult.data);

    // Filter out posts from creators that are already added or exist in database
    const selectablePosts = filteredPosts.filter((post: any) => {
      const author = post.authorMeta || post.author || post.authorInfos || {};
      // Use consistent identifier
      const creatorName = author.name || author.unique_id || author.uniqueId || post.author?.unique_id || post.author_meta?.unique_id || post.authorInfos?.uniqueId;
      const isAdded = addedCreators.has(creatorName);
      const isExisting = existingCreators.has(creatorName);
      return !isAdded && !isExisting;
    });

    const allPostIds = selectablePosts.map((post: any) => getPostId(post));

    const allSelected = allPostIds.length > 0 && allPostIds.every((id: any) => selectedPosts.has(id));

    if (allSelected) {
      // Deselect all
      setSelectedPosts(new Set());
    } else {
      // Select only posts that are not already added
      setSelectedPosts(new Set(allPostIds));
    }
  };

  // Fetch transcript for video
  const fetchTranscript = async (videoUrl: string) => {
    setShowTranscriptModal(true);
    setTranscriptLoading(true);
    setTranscriptError(null);
    setTranscriptVideoUrl(videoUrl);
    
    try {
      const response = await fetch(
        `${API_URL}/api/transcript?url=${encodeURIComponent(videoUrl)}&text=true`
      );
      
      if (!response.ok) {
        throw new Error('Failed to fetch transcript');
      }
      
      const data = await response.json();
      setTranscriptData(data);
    } catch (error) {
      console.error('Error fetching transcript:', error);
      setTranscriptError(error instanceof Error ? error.message : 'Failed to fetch transcript');
    } finally {
      setTranscriptLoading(false);
    }
  };

  // Add TikTok creator
  const addTikTokCreator = async (post: any) => {
    const author = post.authorMeta || post.author || post.authorInfos || {};
    // Use the same identifier as the modal for consistency
    const creatorName = author.name || author.unique_id || author.uniqueId || post.author?.unique_id || post.author_meta?.unique_id || post.authorInfos?.uniqueId;
    
    if (!creatorName) {
      console.error('No creator name found for post');
      return;
    }

    setAddingCreators(prev => new Set(prev).add(creatorName));

    try {
      const response = await fetch(`${API_URL}/api/creators/add-creator`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token && { 'Authorization': `Bearer ${token}` })
        },
        body: JSON.stringify({
          username: creatorName,
          platform: 'tiktok',
          discoveryPostUrl: generateTiktokPostUrl(creatorName, getPostId(post)),
          keyword: tiktokSearchKeyword || 'manual_add', // Send the search keyword
          source: 'manual_search_tiktok'
        }),
      });

      const data = await response.json();
      
      if (data.success) {
        console.log('Creator added successfully:', data.message);
        // Add to success set and keep it permanently
        setAddedCreators(prev => new Set(prev).add(creatorName));
        
        // If creator was updated (already existed), show different message
        if (data.data?.updated) {
          alert(`Creator @${creatorName} already existed. Discovery post updated successfully!`);
        }
      } else {
        console.error('Failed to add creator:', data.error);
        // Only show error if it's not about existing creator with discovery post already set
        if (data.error === 'Creator already exists' && data.data) {
          setExistingCreators(prev => new Set(prev).add(creatorName));
          alert(`Creator @${creatorName} already exists with discovery post already set.`);
        } else {
          alert('Failed to add creator: ' + data.error);
        }
      }
    } catch (error) {
      console.error('Error adding creator:', error);
    } finally {
      setAddingCreators(prev => {
        const newSet = new Set(prev);
        newSet.delete(creatorName);
        return newSet;
      });
    }
  };

  // Add Instagram creator
  const addInstagramCreator = async (post: any) => {
    const creatorId = post.ownerUsername || post.ownerId;
    
    if (!creatorId) {
      console.error('No creator ID found for Instagram post');
      return;
    }

    setInstagramAddingCreators(prev => new Set(prev).add(creatorId));

    try {
      const emailStatus = instagramEmailStatus[creatorId];
      
      const response = await fetch(`${API_URL}/api/creators/add-creator`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token && { 'Authorization': `Bearer ${token}` })
        },
        body: JSON.stringify({
          username: post.ownerUsername,
          platform: 'instagram',
          fullName: post.ownerFullName,
          email: emailStatus?.email || post.email || null,
          discoveryPostUrl: post.url || generateInstagramPostUrl(post.shortCode),
          keyword: instagramSearchKeyword || 'manual_add',
          source: 'manual_search_instagram'
        }),
      });

      const data = await response.json();
      
      if (data.success) {
        console.log('Instagram creator added successfully:', data.message);
        // Add to success set
        setInstagramAddedCreators(prev => new Set(prev).add(creatorId));
        
        // If creator was updated (already existed), show different message
        if (data.data?.updated) {
          alert(`Creator @${post.ownerUsername} already existed. Discovery post updated successfully!`);
        }
      } else {
        console.error('Failed to add Instagram creator:', data.error);
        // Handle existing creator case
        if (data.error === 'Creator already exists' && data.data) {
          setInstagramExistingCreators(prev => new Set(prev).add(creatorId));
          alert(`Creator @${post.ownerUsername} already exists with discovery post already set.`);
        } else {
          alert('Failed to add Instagram creator: ' + data.error);
        }
      }
    } catch (error) {
      console.error('Error adding Instagram creator:', error);
      alert('Failed to add Instagram creator');
    } finally {
      setInstagramAddingCreators(prev => {
        const newSet = new Set(prev);
        newSet.delete(creatorId);
        return newSet;
      });
    }
  };

  // Add YouTube creator
  const addYouTubeCreator = async (video: any) => {
    const channelId = video.channelId;

    if (!channelId) {
      console.error('No channel ID found for YouTube video');
      return;
    }

    setYoutubeAddingCreators(prev => new Set(prev).add(channelId));

    try {
      const response = await fetch(`${API_URL}/api/youtube/accounts`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token && { 'Authorization': `Bearer ${token}` })
        },
        body: JSON.stringify({
          channelId: video.channelId,
          channelName: video.channel,
          channelUrl: `https://www.youtube.com/channel/${video.channelId}`,
          subscriberCount: 0, // Will be fetched later if needed
          videoCount: 0, // Will be fetched later if needed
          viewCount: video.viewCount,
          keyword: youtubeSearchKeyword || 'manual_add',
          discoveryVideoUrl: video.url,
          thumbnail: video.thumbnail,
          email: video.email || null, // Pass email if available
          source: 'manual_search_youtube'
        }),
      });

      const data = await response.json();

      if (data.success) {
        if (data.alreadyExists) {
          // Channel already exists in DB - mark it as existing
          console.log(`Channel ${video.channel} already exists in database`);
          setYoutubeExistingCreators(prev => new Set(prev).add(channelId));
        } else {
          console.log('YouTube creator added successfully:', data);
          // Add to success set
          setYoutubeAddedCreators(prev => new Set(prev).add(channelId));
        }
      } else {
        console.error('Failed to add YouTube creator:', data.error);
        alert('Failed to add YouTube creator: ' + data.error);
      }
    } catch (error) {
      console.error('Error adding YouTube creator:', error);
      alert('Failed to add YouTube creator');
    } finally {
      setYoutubeAddingCreators(prev => {
        const newSet = new Set(prev);
        newSet.delete(channelId);
        return newSet;
      });
    }
  };

  // Toggle select all YouTube videos
  const toggleSelectAllYouTubeVideos = () => {
    if (!youtubeSearchResult || !Array.isArray(youtubeSearchResult.videos)) return;

    const selectableVideos = youtubeSearchResult.videos.filter((video: any) => {
      const channelId = video.channelId;
      return !youtubeAddingCreators.has(channelId) &&
             !youtubeAddedCreators.has(channelId) &&
             !youtubeExistingCreators.has(channelId);
    });

    if (selectableVideos.length === 0) return;

    const allSelected = selectableVideos.every((video: any) =>
      selectedYouTubeVideos.has(video.channelId)
    );

    if (allSelected) {
      // Deselect all
      setSelectedYouTubeVideos(new Set());
    } else {
      // Select all selectable videos
      const newSelected = new Set<string>();
      selectableVideos.forEach((video: any) => {
        newSelected.add(video.channelId);
      });
      setSelectedYouTubeVideos(newSelected);
    }
  };

  // Toggle individual YouTube video selection
  const toggleYouTubeVideoSelection = (channelId: string) => {
    setSelectedYouTubeVideos(prev => {
      const newSet = new Set(prev);
      if (newSet.has(channelId)) {
        newSet.delete(channelId);
      } else {
        newSet.add(channelId);
      }
      return newSet;
    });
  };

  // Add selected YouTube videos
  const addSelectedYouTubeVideos = async () => {
    if (!youtubeSearchResult || !Array.isArray(youtubeSearchResult.videos) || selectedYouTubeVideos.size === 0) return;

    const selectedVideosData = youtubeSearchResult.videos.filter((video: any) =>
      selectedYouTubeVideos.has(video.channelId)
    );

    if (selectedVideosData.length === 0) return;

    setBulkAddingYouTubeVideos(true);

    for (const video of selectedVideosData) {
      await addYouTubeCreator(video);
    }

    // Clear selection after adding
    setSelectedYouTubeVideos(new Set());
    setBulkAddingYouTubeVideos(false);
  };

  // Cancel current search
  const cancelCurrentSearch = useCallback(() => {
    if (currentSearchController) {
      console.log('Cancelling current TikTok search...');
      currentSearchController.abort();
      setCurrentSearchController(null);
      setTiktokSearching(false);
      setTiktokSearchResult(null);
    }
  }, [currentSearchController]);

  // Function to get top posts per creator for Full Hashtag Search
  const getTopPostsPerCreator = useCallback((posts: any[]) => {
    if (!Array.isArray(posts)) return [];

    // Group posts by creator
    const creatorPosts = new Map<string, any[]>();

    posts.forEach(post => {
      const creatorId = post.authorInfos?.uniqueId || post.authorMeta?.name || post.author?.unique_id || '';
      if (creatorId) {
        if (!creatorPosts.has(creatorId)) {
          creatorPosts.set(creatorId, []);
        }
        creatorPosts.get(creatorId)!.push(post);
      }
    });

    // Get the highest view count post for each creator
    const topPosts: any[] = [];
    creatorPosts.forEach((posts, creatorId) => {
      const topPost = posts.reduce((best, current) => {
        const bestViews = best.itemInfos?.playCount || best.videoMeta?.playCount || best.playCount || best.statistics?.play_count || 0;
        const currentViews = current.itemInfos?.playCount || current.videoMeta?.playCount || current.playCount || current.statistics?.play_count || 0;
        return currentViews > bestViews ? current : best;
      });
      topPosts.push(topPost);
    });

    // Sort by views descending
    topPosts.sort((a, b) => {
      const aViews = a.itemInfos?.playCount || a.videoMeta?.playCount || a.playCount || a.statistics?.play_count || 0;
      const bViews = b.itemInfos?.playCount || b.videoMeta?.playCount || b.playCount || b.statistics?.play_count || 0;
      return bViews - aViews;
    });

    return topPosts;
  }, []);

  // Sort and filter TikTok results
  const sortAndFilterTikTokResults = useCallback((posts: any[]) => {
    if (!Array.isArray(posts)) return [];

    // Use applied filters if they exist (from when search was performed)
    // Otherwise use current filter values
    const minViews = appliedFilters?.minViews ?? tiktokMinViews;
    const minLikes = appliedFilters?.minLikes ?? tiktokMinLikes;
    const minComments = appliedFilters?.minComments ?? tiktokMinComments;

    // Apply all filters including views (user can change min views in modal)
    let filteredPosts = posts.filter(post => {
      // Handle different data structures for views
      const views = post.itemInfos?.playCount || post.videoMeta?.playCount || post.playCount || post.statistics?.play_count || 0;
      const likes = post.itemInfos?.diggCount || post.videoMeta?.diggCount || post.diggCount || post.statistics?.digg_count || 0;
      const comments = post.itemInfos?.commentCount || post.videoMeta?.commentCount || post.commentCount || post.statistics?.comment_count || 0;

      return views >= minViews &&
             likes >= minLikes &&
             comments >= minComments;
    });
    
    // Apply sorting
    filteredPosts.sort((a, b) => {
      let aValue: number, bValue: number;

      switch (tiktokSortBy) {
        case 'views':
          aValue = a.itemInfos?.playCount || a.videoMeta?.playCount || a.playCount || a.statistics?.play_count || 0;
          bValue = b.itemInfos?.playCount || b.videoMeta?.playCount || b.playCount || b.statistics?.play_count || 0;
          break;
        case 'likes':
          aValue = a.itemInfos?.diggCount || a.videoMeta?.diggCount || a.diggCount || a.statistics?.digg_count || 0;
          bValue = b.itemInfos?.diggCount || b.videoMeta?.diggCount || b.diggCount || b.statistics?.digg_count || 0;
          break;
        case 'comments':
          aValue = a.itemInfos?.commentCount || a.videoMeta?.commentCount || a.commentCount || a.statistics?.comment_count || 0;
          bValue = b.itemInfos?.commentCount || b.videoMeta?.commentCount || b.commentCount || b.statistics?.comment_count || 0;
          break;
        case 'date':
          aValue = a.itemInfos?.createTime || a.createTime || a.create_time || 0;
          bValue = b.itemInfos?.createTime || b.createTime || b.create_time || 0;
          break;
        default: // views as default
          aValue = a.itemInfos?.playCount || a.videoMeta?.playCount || a.playCount || a.statistics?.play_count || 0;
          bValue = b.itemInfos?.playCount || b.videoMeta?.playCount || b.playCount || b.statistics?.play_count || 0;
          break;
      }
      
      return tiktokSortOrder === 'desc' ? bValue - aValue : aValue - bValue;
    });
    
    return filteredPosts;
  }, [tiktokSortBy, tiktokSortOrder, appliedFilters]);

  // Search Instagram creators
  const searchInstagramCreators = async () => {
    // Validate search keyword
    if (!instagramSearchKeyword || !instagramSearchKeyword.trim()) {
      alert('Please enter a search term');
      return;
    }

    clearSearchLogs();
    setShowSearchLogs(true);
    addSearchLog(`🔍 Starting: Instagram creator search for keyword "${instagramSearchKeyword}"`);
    addSearchLog(`⚙️ Settings: Min views ${instagramMinViews?.toLocaleString() || '1,000'}, Max results ${instagramMaxItems}`);

    setInstagramSearching(true);
    setInstagramSearchResult({ loading: true }); // Set loading state to show results view immediately
    setInstagramEmailStatus({}); // Clear previous email status

    try {
      const response = await fetch(`${API_URL}/api/keywords/search-instagram`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token && { 'Authorization': `Bearer ${token}` })
        },
        body: JSON.stringify({
          name: instagramSearchKeyword,
          maxItems: instagramMaxItems,
          min_views: instagramMinViews,
          emailFilter: false,
          exclude_existing: true
        }),
      });
      
      addSearchLog(`📡 Response: API response received (status: ${response.status})`);
      
      const data = await response.json();
      
      addSearchLog(`📊 Data Processing: Received ${data.count || 0} posts`);
      
      if (data.success) {
        // Show simplified filtering pipeline
        if (data.filterStats) {
          const stats = data.filterStats;
          addSearchLog(`📊 Filtering Pipeline:`);

          // Step 1: Initial results
          addSearchLog(`✅ Initial results: ${stats.apifyData || 0} posts`);

          // Step 2: Min views filter
          if (instagramMinViews > 0 && stats.afterMinViewsFilter !== undefined) {
            const beforeCount = stats.apifyData || 0;
            const afterCount = stats.afterMinViewsFilter;
            if (afterCount !== beforeCount) {
              addSearchLog(`🎯 After view count filter: ${beforeCount} → ${afterCount} (${instagramMinViews.toLocaleString()}+ views)`);
            }
          }

          // Step 3: Existing filter
          if (stats.afterExistingFilter !== undefined) {
            const beforeCount = stats.afterMinViewsFilter || stats.apifyData || 0;
            const afterCount = stats.afterExistingFilter;
            if (afterCount !== beforeCount) {
              const removed = beforeCount - afterCount;
              addSearchLog(`🗄️  After existing filter: ${beforeCount} → ${afterCount} (removed ${removed} already in DB)`);
            }
          }

          // Step 4: Final results
          addSearchLog(`✅ Final results: ${stats.final || 0} creators`);
        }

        setInstagramSearchResult(data);
        
        // Check emails for each creator
        if (data.data && data.data.length > 0) {
          addSearchLog(`📧 Checking emails for ${data.data.length} creators...`);
          checkInstagramCreatorEmails(data.data);
        }
      } else {
        throw new Error(data.error || 'Failed to search Instagram creators');
      }
    } catch (error: any) {
      console.error('Instagram search error:', error);
      addSearchLog(`❌ Error: ${error.message || 'Failed to search Instagram creators'}`);
      setInstagramSearchResult({ error: error.message });
    } finally {
      setInstagramSearching(false);
    }
  };
  
  // Fetch email for a single Instagram creator
  const fetchInstagramEmail = async (username: string) => {
    const emailRegex = /[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}/g;
    
    // Set loading status
    setInstagramEmailStatus(prev => ({
      ...prev,
      [username]: { 
        status: 'loading' as const
      }
    }));
    
    try {
      const response = await fetch(`${API_URL}/api/instagram/user/${username}`, {
        headers: {
          ...(token && { 'Authorization': `Bearer ${token}` })
        }
      });

      // Check if response is HTML (404 error page)
      const contentType = response.headers.get('content-type');
      if (contentType && contentType.includes('text/html')) {
        console.warn(`Instagram user API returned HTML for @${username} - endpoint may not exist or user not found`);
        setInstagramEmailStatus(prev => ({
          ...prev,
          [username]: {
            status: 'not-found' as const
          }
        }));
        return;
      }
      
      if (!response.ok) {
        console.warn(`Instagram user API returned ${response.status} for @${username}`);
        setInstagramEmailStatus(prev => ({
          ...prev,
          [username]: { 
            status: 'not-found' as const
          }
        }));
        return;
      }
      
      const userData = await response.json();
      
      if (userData.success && userData.data) {
        const biography = userData.data.biography || '';
        const bioEmails = biography.match(emailRegex);
        
        if (bioEmails && bioEmails.length > 0) {
          setInstagramEmailStatus(prev => ({
            ...prev,
            [username]: { 
              status: 'found' as const,
              email: bioEmails[0]
            }
          }));
        } else {
          setInstagramEmailStatus(prev => ({
            ...prev,
            [username]: { 
              status: 'not-found' as const
            }
          }));
        }
      } else {
        setInstagramEmailStatus(prev => ({
          ...prev,
          [username]: { 
            status: 'not-found' as const
          }
        }));
      }
    } catch (error) {
      // Improved error handling - check if it's a JSON parsing error
      const errorMessage = error instanceof Error ? error.message : String(error);
      if (errorMessage.includes('Unexpected token') && errorMessage.includes('<!DOCTYPE')) {
        console.warn(`Instagram user API returned HTML instead of JSON for @${username} - likely a 404 error`);
      } else {
        console.error(`Failed to fetch email for @${username}:`, errorMessage);
      }
      
      setInstagramEmailStatus(prev => ({
        ...prev,
        [username]: { 
          status: 'not-found' as const
        }
      }));
    }
  };

  // Check Instagram creator emails
  const checkInstagramCreatorEmails = async (posts: any[]) => {
    const emailRegex = /[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}/g;
    
    for (const post of posts) {
      const username = post.ownerUsername;
      if (!username) continue;
      
      // First check if email is already in caption
      if (post.caption) {
        const captionEmails = post.caption.match(emailRegex);
        if (captionEmails && captionEmails.length > 0) {
          setInstagramEmailStatus(prev => ({
            ...prev,
            [username]: { status: 'found', email: captionEmails[0] }
          }));
          continue;
        }
      }
      
      // Set loading status
      setInstagramEmailStatus(prev => ({
        ...prev,
        [username]: { status: 'loading' }
      }));
      
      // Fetch user info to check biography
      try {
        const response = await fetch(`${API_URL}/api/instagram/user/${username}`, {
          headers: {
            ...(token && { 'Authorization': `Bearer ${token}` })
          }
        });

        // Check if response is HTML (404 error page)
        const contentType = response.headers.get('content-type');
        if (contentType && contentType.includes('text/html')) {
          console.warn(`Instagram user API returned HTML for @${username} - endpoint may not exist or user not found`);
          setInstagramEmailStatus(prev => ({
            ...prev,
            [username]: { status: 'not-found' }
          }));
          return;
        }
        
        if (!response.ok) {
          console.warn(`Instagram user API returned ${response.status} for @${username}`);
          setInstagramEmailStatus(prev => ({
            ...prev,
            [username]: { status: 'not-found' }
          }));
          return;
        }
        
        const userData = await response.json();
        
        if (userData.success && userData.data) {
          const biography = userData.data.biography || '';
          const bioEmails = biography.match(emailRegex);
          
          if (bioEmails && bioEmails.length > 0) {
            setInstagramEmailStatus(prev => ({
              ...prev,
              [username]: { status: 'found', email: bioEmails[0] }
            }));
          } else {
            setInstagramEmailStatus(prev => ({
              ...prev,
              [username]: { status: 'not-found' }
            }));
          }
        } else {
          setInstagramEmailStatus(prev => ({
            ...prev,
            [username]: { status: 'not-found' }
          }));
        }
      } catch (error) {
        // Improved error handling - check if it's a JSON parsing error
        const errorMessage = error instanceof Error ? error.message : String(error);
        if (errorMessage.includes('Unexpected token') && errorMessage.includes('<!DOCTYPE')) {
          console.warn(`Instagram user API returned HTML instead of JSON for @${username} - likely a 404 error`);
        } else {
          console.error(`Failed to fetch email for @${username}:`, errorMessage);
        }
        
        setInstagramEmailStatus(prev => ({
          ...prev,
          [username]: { status: 'not-found' }
        }));
      }
    }
  };
  
  // Search TikTok creators for competitor
  const searchTikTokCreators = async () => {
    // Cancel any existing search
    if (currentSearchController) {
      currentSearchController.abort();
    }
    
    // Clear added creators state when doing a new search
    setAddedCreators(new Set());
    setExistingCreators(new Set());

    // Create new AbortController for this search
    const abortController = new AbortController();
    setCurrentSearchController(abortController);

    // Initialize search logs
    clearSearchLogs();
    setShowSearchLogs(true);
    addSearchLog(`🔍 Starting: TikTok creator search for ${tiktokSearchType === 'full-hashtag' ? 'hashtag' : 'keyword'} "${tiktokSearchKeyword}"`);

    // Show appropriate settings based on search type
    if (tiktokSearchType === 'full-hashtag') {
      addSearchLog(`⚙️ Settings: Min views ${tiktokMinViews?.toLocaleString() || '5,000'}, Email filter ${tiktokSearchParams.email_filter ? 'enabled' : 'disabled'}, Days ${tiktokSearchParams.days || 30}`);
    } else {
      addSearchLog(`⚙️ Settings: Min views ${tiktokMinViews?.toLocaleString() || '5,000'}, Email filter ${tiktokSearchParams.email_filter ? 'enabled' : 'disabled'}, Period ${tiktokSearchParams.period || '30'} days`);
    }

    // Immediately show loading state and transition to result view
    setTiktokSearching(true);
    setTiktokSearchResult({ loading: true }); // Set a loading state to show the results view immediately
    
    // Store the current filter values that will be applied to this search
    setAppliedFilters({
      minViews: tiktokMinViews,
      minLikes: tiktokMinLikes,
      minComments: tiktokMinComments
    });

    try {
      // Log EnsembleData API parameters based on search type
      addSearchLog(`📊 EnsembleData API Parameters:`);

      if (tiktokSearchType === 'full-hashtag') {
        addSearchLog(`   - Hashtag: "${tiktokSearchKeyword}"`);
        addSearchLog(`   - Days: ${tiktokSearchParams.days || 30}`);
        addSearchLog(`   - Max Cursor: ${tiktokSearchParams.max_cursor || 10000}`);
      } else if (tiktokSearchType === 'hashtag') {
        addSearchLog(`   - Hashtag: "${tiktokSearchKeyword}"`);
        addSearchLog(`   - Cursor: ${tiktokSearchParams.cursor || 0}`);
        if (tiktokSearchParams.enable_multi_cursor) {
          addSearchLog(`   - Multi-cursor: Enabled (max: ${tiktokSearchParams.multi_cursor_max || 1000})`);
        }
      } else {
        // keyword or full-keyword
        addSearchLog(`   - Keyword: "${tiktokSearchKeyword}"`);
        addSearchLog(`   - Period: ${tiktokSearchParams.period || '30'} days`);
        addSearchLog(`   - Sorting: ${tiktokSearchParams.sorting === '0' ? 'Latest' : tiktokSearchParams.sorting === '1' ? 'Most Liked' : tiktokSearchParams.sorting}`);
        addSearchLog(`   - Country: ${tiktokSearchParams.country || 'All (empty)'}`);
        if (tiktokSearchParams.match_exactly) {
          addSearchLog(`   - Match: Exact`);
        }
      }

      addSearchLog(`   - Email Filter: ${tiktokSearchParams.email_filter ? 'Enabled' : 'Disabled'}`);
      addSearchLog(`   - Min Views: ${tiktokMinViews?.toLocaleString() || '5,000'}`);

      // Log actual API endpoint with request body
      const apiEndpoint = `${API_URL}/api/keywords/search-tiktok`;

      const requestBody = {
        name: tiktokSearchKeyword,
        searchType: tiktokSearchType,
        period: tiktokSearchParams.period || '30',
        days: tiktokSearchParams.days || 30,
        sorting: tiktokSearchParams.sorting,
        country: tiktokSearchParams.country,
        get_author_stats: tiktokSearchParams.get_author_stats,
        exclude_existing: tiktokSearchParams.exclude_existing,
        emailFilter: tiktokSearchParams.email_filter,
        min_views: tiktokMinViews || 5000,
        max_cursor: tiktokSearchParams.max_cursor,
        remap_output: tiktokSearchParams.remap_output,
        cursor: tiktokSearchParams.cursor || 0,
        match_exactly: tiktokSearchParams.match_exactly,
        enable_multi_cursor: tiktokSearchParams.enable_multi_cursor,
        multi_cursor_max: tiktokSearchParams.multi_cursor_max
      };

      const response = await fetch(apiEndpoint, {
        signal: abortController.signal,
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token && { 'Authorization': `Bearer ${token}` })
        },
        body: JSON.stringify(requestBody),
      });

      // Check if response is HTML (server error)
      const contentType = response.headers.get('content-type');
      if (!response.ok || (contentType && !contentType.includes('application/json'))) {
        const errorText = await response.text();
        console.error('TikTok API Error Response:', errorText);

        if (contentType && contentType.includes('text/html')) {
          addSearchLog(`💥 Server Error: Received HTML instead of JSON (Status: ${response.status})`);
          addSearchLog(`❌ This usually means the backend server encountered an error`);
          throw new Error(`Server returned HTML error page (Status: ${response.status})`);
        }

        throw new Error(`API request failed with status ${response.status}`);
      }

      const data = await response.json();

      // Log actual EnsembleData API URL from backend response
      if (data.ensembleApiUrl) {
        addSearchLog(`🔗 API URL: ${data.ensembleApiUrl}`);
      } else {
        addSearchLog(`🌐 API Call: POST ${apiEndpoint}`);
      }
      
      // Enhanced logging with user-friendly messages
      console.log('TikTok API Response:', {
        success: data.success,
        count: data.count,
        dataLength: Array.isArray(data.data) ? data.data.length : 0,
        apifyRunId: data.apifyRunId
      });
      
      if (data.apifyRunId) {
        addSearchLog(`📡 Response: API response received (${Array.isArray(data.data) ? data.data.length : 0} posts after filtering)`);
        // Determine if this is EnsembleData or Apify
        const isEnsembleData = data.apifyRunId.startsWith('ensemble_');
        if (isEnsembleData) {
          addSearchLog(`   Run ID: ${data.apifyRunId} (EnsembleData)`);
        } else {
          addSearchLog(`   Run ID: ${data.apifyRunId} (Apify Fallback)`);
        }
      } else {
        addSearchLog(`📡 Response: API response received`);
      }
      
      if (data.success) {
        // Show simplified filtering pipeline
        if (data.filterStats || data.creatorFilterStats) {
          const stats = data.filterStats || {};
          const creatorStats = data.creatorFilterStats || {};
          console.log('FilterStats received:', stats);
          console.log('CreatorFilterStats received:', creatorStats);

          addSearchLog(`📊 Filtering Pipeline:`);

          // Step 1: Initial results
          if (tiktokSearchType === 'full-hashtag' && creatorStats.originalPosts) {
            addSearchLog(`✅ Initial results: ${creatorStats.originalPosts} posts`);
          } else {
            const postsReceived = stats.originalCount || stats.apifyData || data.count || 0;
            addSearchLog(`✅ Initial results: ${postsReceived} posts`);
          }

          // Step 2: Min views filter (if applied)
          if (tiktokMinViews > 0) {
            if (tiktokSearchType === 'full-hashtag') {
              const originalPosts = creatorStats.originalPosts || 0;
              const afterMinViews = creatorStats.afterMinViews || originalPosts;
              if (afterMinViews !== originalPosts) {
                addSearchLog(`🎯 After view count filter: ${originalPosts} → ${afterMinViews} posts (${tiktokMinViews.toLocaleString()}+ views)`);
              }
            } else {
              const originalCount = stats.originalCount || stats.apifyData || 0;
              const afterMinViews = stats.afterMinViewsFilter ?? stats.afterMinViews ?? originalCount;
              if (afterMinViews !== originalCount) {
                addSearchLog(`🎯 After view count filter: ${originalCount} → ${afterMinViews} (${tiktokMinViews.toLocaleString()}+ views)`);
              }
            }
          }

          // Step 3: Email filter (if enabled)
          if (tiktokSearchParams.email_filter) {
            if (tiktokSearchType === 'full-hashtag') {
              const beforeEmail = creatorStats.uniqueCreators || 0;
              const afterEmail = creatorStats.afterEmailFilter || 0;
              if (afterEmail !== beforeEmail) {
                addSearchLog(`📧 After email filter: ${beforeEmail} → ${afterEmail} (with email only)`);
              }
            } else {
              const beforeEmail = stats.afterMinViewsFilter ?? stats.afterMinViews ?? stats.originalCount ?? 0;
              const afterEmail = stats.afterEmailFilter ?? beforeEmail;
              if (afterEmail !== beforeEmail) {
                addSearchLog(`📧 After email filter: ${beforeEmail} → ${afterEmail} (with email only)`);
              }
            }
          }

          // Step 4: Existing creators filter
          if (tiktokSearchType === 'full-hashtag') {
            const beforeExisting = creatorStats.afterEmailFilter || creatorStats.uniqueCreators || 0;
            const afterExisting = creatorStats.afterExistingFilter ?? beforeExisting;
            const excludedExisting = creatorStats.excludedExisting?.length || 0;

            if (excludedExisting > 0) {
              addSearchLog(`🗄️  After existing filter: ${beforeExisting} → ${afterExisting} (removed ${excludedExisting} already in DB)`);
            }
          } else {
            const beforeExisting = stats.afterEmailFilter ?? stats.afterMinViewsFilter ?? stats.afterMinViews ?? stats.originalCount ?? 0;
            const afterExisting = stats.afterExistingFilter ?? beforeExisting;

            if (afterExisting !== beforeExisting) {
              const excludedExisting = beforeExisting - afterExisting;
              addSearchLog(`🗄️  After existing filter: ${beforeExisting} → ${afterExisting} (removed ${excludedExisting} already in DB)`);
            }
          }

          // Step 5: Final result count
          const finalCount = tiktokSearchType === 'full-hashtag' ?
            (creatorStats.final ?? creatorStats.afterExistingFilter ?? data.creatorCount ?? data.count ?? 0) :
            (stats.final ?? stats.afterExistingFilter ?? data.data?.length ?? data.count ?? 0);

          addSearchLog(`✅ Final results: ${finalCount} ${tiktokSearchType === 'full-hashtag' ? 'creators' : 'posts'}`);
        }

        // Debug log for Full Hashtag Search
        if (tiktokSearchType === 'full-hashtag') {
          console.log('Full Hashtag Search Result:', {
            hasCreators: !!data.creators,
            creatorCount: data.creatorCount,
            dataLength: data.data?.length,
            filterStats: data.filterStats,
            creatorFilterStats: data.creatorFilterStats,
            fullData: data
          });
        }

        // v2 API returns processed data with count
        const resultWithMetadata = {
          ...data,
          searchTime: Date.now(),
          searchTerm: tiktokSearchKeyword,
          searchType: tiktokSearchType,
          searchParams: { ...tiktokSearchParams }
        };

        setTiktokSearchResult(resultWithMetadata);

        // Refresh keywords data after successful search
        fetchKeywords();
      } else {
        addSearchLog(`❌ Search Failed: ${data.error || 'Failed to search TikTok creators'}`);
        const errorResult = { error: data.error || 'Failed to search TikTok creators' };
        setTiktokSearchResult(errorResult);
        // Don't cache error results
      }
    } catch (error: any) {
      if (error.name === 'AbortError') {
        addSearchLog(`⏹️ Search Cancelled: User cancelled the search`);
        console.log('TikTok search was cancelled');
        // Don't update state if cancelled, just clear the controller
      } else {
        addSearchLog(`💥 Network Error: ${error.message || 'Unknown error occurred'}`);
        console.error('Error searching TikTok:', error);
        setTiktokSearchResult({ error: String(error) });
      }
    } finally {
      setTiktokSearching(false);
      // Clear the controller if it's the current one
      setCurrentSearchController(prev => prev === abortController ? null : prev);
    }
  };

  // Fetch data when dependencies change
  // Only fetch on initial mount and when explicitly needed
  // Fetch keywords when dependencies change
  useEffect(() => {
    fetchKeywords();
  }, [fetchKeywords]);

  // Check automation status on mount and set up cleanup
  useEffect(() => {
    console.log('[Automation] Component mounted, checking status...');
    checkAutomationStatus(false); // Check status without forcing history refresh
    fetchSearchHistory(true); // Fetch history with loading indicator on mount

    // Cleanup on unmount
    return () => {
      stopPollingAutomationStatus();
    };
  }, []); // Empty dependency array - only run on mount/unmount

  // Handle page changes separately
  useEffect(() => {
    if (pagination.page > 1) {
      fetchKeywords(pagination.page);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pagination.page]);
  
  // Fetch groups on mount
  useEffect(() => {
    fetchGroups();
  }, [fetchGroups]);

  // Fetch topics when relevance tab is active
  useEffect(() => {
    if (mainTab === 'relevance') {
      fetchTopics();
    }
  }, [mainTab, fetchTopics]);

  // Reset page to 1 when sort or search changes for keywords
  useEffect(() => {
    setPagination(prev => ({ ...prev, page: 1 }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sortField, sortOrder, searchTerm, selectedGroup]);
  
  
  // Fetch automation keywords when modal opens or skip days change
  useEffect(() => {
    if (showTikTokAutomationModal) {
      fetchAutomationKeywords();
    }
  }, [showTikTokAutomationModal, tiktokAutomationSkipDays]);

  // Update mainTab when location changes
  useEffect(() => {
    const path = location.pathname;
    if (path.includes('/find/keywords')) setMainTab('keywords');
    else if (path.includes('/find/automation')) setMainTab('automation');
    else if (path.includes('/find/groups')) setMainTab('groups');
    else if (path.includes('/find/relevance')) setMainTab('relevance');
  }, [location.pathname]);

  // Fetch search history when automation tab is active
  useEffect(() => {
    console.log('mainTab changed to:', mainTab);
    if (mainTab === 'automation') {
      console.log('Automation tab active, fetching search history...');
      fetchSearchHistory(true);
      // Check if automation is running in backend (with refresh flag for initial load)
      checkAutomationStatus(true);
    }
  }, [mainTab]);

  // Cancel search and polling on component unmount
  useEffect(() => {
    return () => {
      if (currentSearchController) {
        currentSearchController.abort();
      }
      // Stop polling when component unmounts
      stopPollingAutomationStatus();
    };
  }, [currentSearchController]);

  // Handle sort
  const handleSort = (field: SortField | 'actions' | 'group') => {
    if (field === 'actions' || field === 'group') return;
    
    const sortableField = field as SortField;
    let newSortOrder: SortOrder;
    
    if (sortField === sortableField) {
      newSortOrder = sortOrder === 'asc' ? 'desc' : 'asc';
      setSortOrder(newSortOrder);
      localStorage.setItem('find_sortOrder', newSortOrder);
    } else {
      setSortField(sortableField);
      setSortOrder('desc');
      localStorage.setItem('find_sortField', sortableField);
      localStorage.setItem('find_sortOrder', 'desc');
    }
  };

  // Handle search for keywords
  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setSearchTerm(searchInput);
    setPagination(prev => ({ ...prev, page: 1 }));
  };
  
  // Handle sort for creators

  // Format date
  const formatDate = (date: string | null) => {
    if (!date) return '-';
    return new Date(date).toLocaleDateString('ko-KR', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Handle page change
  const handlePageChange = (newPage: number) => {
    setPagination(prev => ({ ...prev, page: newPage }));
  };

  // Handle direct page input
  const handlePageInputSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const page = parseInt(pageInput);
    if (page >= 1 && page <= pagination.totalPages) {
      handlePageChange(page);
      setPageInput('');
    }
  };

  // Column customization handlers
  const toggleColumnVisibility = (index: number) => {
    const newColumns = [...columns];
    newColumns[index].visible = !newColumns[index].visible;
    setColumns(newColumns);
  };

  const handleCustomizerDragStart = (e: React.DragEvent, index: number) => {
    setDraggedColumn(index);
  };

  const handleCustomizerDragOver = (e: React.DragEvent) => {
    e.preventDefault();
  };

  const handleCustomizerDrop = (e: React.DragEvent, dropIndex: number) => {
    e.preventDefault();
    if (draggedColumn === null || draggedColumn === dropIndex) return;

    const newColumns = [...columns];
    const [movedColumn] = newColumns.splice(draggedColumn, 1);
    newColumns.splice(dropIndex, 0, movedColumn);
    setColumns(newColumns);
    setDraggedColumn(null);
  };

  // Get sort icon
  const getSortIcon = (field: SortField) => {
    if (sortField !== field) return '↕️';
    return sortOrder === 'asc' ? '↑' : '↓';
  };

  return (
    <div className="tab-content">
      <div className="page-header">
        <h1 className="page-title">Find</h1>
        <p className="page-description">
          Automatically find creators using keywords and groups
        </p>
      </div>

      {/* Success message */}
      {successMessage && (
        <div style={{
          backgroundColor: '#10b981',
          color: 'white',
          padding: '12px 16px',
          borderRadius: '8px',
          marginBottom: '16px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between'
        }}>
          <span>{successMessage}</span>
          <button 
            onClick={() => setSuccessMessage(null)}
            style={{
              background: 'none',
              border: 'none',
              color: 'white',
              cursor: 'pointer',
              fontSize: '20px'
            }}
          >
            ✕
          </button>
        </div>
      )}

      {/* Error message */}
      {error && (
        <div style={{
          backgroundColor: '#ef4444',
          color: 'white',
          padding: '12px 16px',
          borderRadius: '8px',
          marginBottom: '16px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between'
        }}>
          <span>{error}</span>
          <button 
            onClick={() => setError(null)}
            style={{
              background: 'none',
              border: 'none',
              color: 'white',
              cursor: 'pointer',
              fontSize: '20px'
            }}
          >
            ✕
          </button>
        </div>
      )}

      {/* Main tabs for Keywords, Automation, Automation Logs, Relevant Topics, and Groups */}
      <div className="flex gap-2" style={{ marginBottom: 'var(--space-6)' }}>
        <button
          className={`btn ${mainTab === 'keywords' ? 'btn-primary' : 'btn-secondary'} flex items-center gap-2`}
          onClick={() => {
            setMainTab('keywords');
            navigate('/find/keywords');
          }}
        >
          <span>🔑</span>
          <span>Keywords</span>
          {mainTab === 'keywords' && pagination?.total > 0 && (
            <span className="bg-white text-primary px-2 py-1 rounded text-xs font-medium">
              {pagination.total}
            </span>
          )}
        </button>
        <button
          className={`btn ${mainTab === 'automation' ? 'btn-primary' : 'btn-secondary'} flex items-center gap-2`}
          onClick={() => {
            setMainTab('automation');
            navigate('/find/automation');
          }}
        >
          <span>🤖</span>
          <span>Automation</span>
        </button>
        <button
          className={`btn ${mainTab === 'relevance' ? 'btn-primary' : 'btn-secondary'} flex items-center gap-2`}
          onClick={() => {
            setMainTab('relevance');
            navigate('/find/relevance');
          }}
        >
          <span>🎯</span>
          <span>Relevant Topics</span>
          {mainTab === 'relevance' && topics.length > 0 && (
            <span className="bg-white text-primary px-2 py-1 rounded text-xs font-medium">
              {topics.length}
            </span>
          )}
        </button>
        <button
          className={`btn ${mainTab === 'groups' ? 'btn-primary' : 'btn-secondary'} flex items-center gap-2`}
          onClick={() => {
            setMainTab('groups');
            navigate('/find/groups');
          }}
        >
          <span>📁</span>
          <span>Groups</span>
          {mainTab === 'groups' && groups.length > 0 && (
            <span className="bg-white text-primary px-2 py-1 rounded text-xs font-medium">
              {groups.length}
            </span>
          )}
        </button>
        <button
          className={`btn ${mainTab === 'similar' ? 'btn-primary' : 'btn-secondary'} flex items-center gap-2`}
          onClick={() => {
            setMainTab('similar');
            navigate('/find/similar');
          }}
        >
          <span>🔍</span>
          <span>Similar</span>
        </button>
      </div>

      {/* Keywords tab content */}
      {mainTab === 'keywords' && (
        <div className="flex justify-between items-center gap-4" style={{ marginBottom: 'var(--space-6)' }}>
          <form onSubmit={handleSearch} className="flex gap-3 items-center">
            {/* Group filter dropdown */}
            <select
              value={selectedGroup || ''}
              onChange={async (e) => {
                if (e.target.value === '__create_new__') {
                  const newGroupName = prompt('Enter new group name:');
                  if (newGroupName && newGroupName.trim()) {
                    const trimmedName = newGroupName.trim().toLowerCase();
                    
                    // Create the new group in the database
                    try {
                      const createResponse = await fetch(`${API_URL}/api/groups`, {
                        method: 'POST',
                        headers: {
                          'Content-Type': 'application/json',
                          ...(token && { 'Authorization': `Bearer ${token}` })
                        },
                        body: JSON.stringify({
                          name: trimmedName
                        }),
                      });

                      if (!createResponse.ok) {
                        const error = await createResponse.json();
                        if (error.error === 'Group already exists') {
                          // Group exists, just use it
                        } else {
                          alert('Failed to create group: ' + error.error);
                          setSelectedGroup(null);
                          return;
                        }
                      }

                      // Add to local availableGroups if not already there
                      if (!availableGroups.includes(trimmedName)) {
                        setAvailableGroups([...availableGroups, trimmedName]);
                      }
                      setSelectedGroup(trimmedName);
                      setPagination(prev => ({ ...prev, page: 1 }));
                    } catch (error) {
                      console.error('Error creating group:', error);
                      alert('Failed to create group');
                      setSelectedGroup(null);
                      return;
                    }
                  } else {
                    setSelectedGroup(null);
                  }
                } else {
                  setSelectedGroup(e.target.value || null);
                  setPagination(prev => ({ ...prev, page: 1 }));
                }
              }}
              className="input"
              style={{ minWidth: '150px' }}
            >
              <option value="">All Groups</option>
              {groups.map(group => (
                <option key={group.name} value={group.name}>
                  {group.name}
                </option>
              ))}
              <option value="__create_new__" style={{ fontStyle: 'italic', color: '#667eea' }}>
                + Create new group...
              </option>
            </select>
            
            <div className="relative">
              <input
                type="text"
                placeholder="Search keywords..."
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                className="input"
                style={{ 
                  paddingLeft: 'var(--space-3)',
                  minWidth: '300px'
                }}
              />
            </div>
            <button type="submit" className="btn btn-primary">
              Search
            </button>
            {searchTerm && (
              <button
                type="button"
                onClick={() => {
                  setSearchTerm('');
                  setSearchInput('');
                }}
                className="btn btn-secondary"
              >
                Clear
              </button>
            )}
          </form>
          <div className="flex gap-3">
            <button
              type="button"
              onClick={() => setShowColumnCustomizer(!showColumnCustomizer)}
              className="btn btn-secondary flex items-center gap-2"
            >
              <span>⚙️</span>
              <span>Columns</span>
            </button>
            <button
              type="button"
              onClick={openCreateModal}
              className="btn btn-primary flex items-center gap-2"
            >
              <span>➕</span>
              <span>New Keyword</span>
            </button>
          </div>
        </div>
      )}
      
      {/* Column customizer */}
      {showColumnCustomizer && (
        <div className="column-customizer" style={{ 
          maxHeight: '300px',
          overflowY: 'auto',
          marginBottom: 'var(--space-6)'
        }}>
          <h4 className="text-lg font-semibold" style={{ margin: '0 0 var(--space-4) 0' }}>Column Settings</h4>
          {columns.map((column, index) => (
            <div 
              key={column.key} 
              draggable
              onDragStart={(e) => handleCustomizerDragStart(e, index)}
              onDragOver={handleCustomizerDragOver}
              onDrop={(e) => handleCustomizerDrop(e, index)}
              className="column-item"
            >
              <span className="text-sm text-secondary" style={{ cursor: 'move', userSelect: 'none' }}>
                ☰
              </span>
              <label className="flex items-center gap-3 flex-1 text-sm cursor-pointer">
                <input
                  type="checkbox"
                  checked={column.visible}
                  onChange={() => toggleColumnVisibility(index)}
                  className="rounded"
                />
                <span style={{ 
                  fontWeight: column.visible ? 'var(--font-medium)' : 'var(--font-normal)', 
                  color: column.visible ? 'var(--text-primary)' : 'var(--text-secondary)'
                }}>
                  {column.label}
                </span>
              </label>
              <span className="text-xs text-secondary">
                {column.width}px
              </span>
            </div>
          ))}
          <button
            onClick={() => setShowColumnCustomizer(false)}
            className="btn btn-primary text-sm"
            style={{ marginTop: 'var(--space-4)' }}
          >
            Done
          </button>
        </div>
      )}

      {/* Keywords Content */}
      {mainTab === 'keywords' && (
        <>
          {loading && <div className="loading">Loading...</div>}
          {error && <div className="error">❌ {error}</div>}

          {!loading && !error && (
            <div>
              {/* Pagination controls - Top */}
              <div className="pagination" style={{ marginBottom: 'var(--space-4)' }}>
                <div className="pagination-info">
                  Showing {keywords.length} of {pagination?.total || 0} keywords
                </div>
                <div className="pagination-controls">
                  <button
                    onClick={() => handlePageChange(1)}
                    disabled={(pagination?.page || 1) === 1}
                    className="pagination-button"
                  >
                    ⏮️ First
                  </button>
                  <button
                    onClick={() => handlePageChange((pagination?.page || 1) - 1)}
                    disabled={(pagination?.page || 1) === 1}
                    className="pagination-button"
                  >
                    ◀️ Previous
                  </button>
                  <span className="page-info" style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                    Page
                    <form onSubmit={handlePageInputSubmit} style={{ display: 'inline' }}>
                      <input
                        type="number"
                        value={pageInput || (pagination?.page || 1)}
                        onChange={(e) => setPageInput(e.target.value)}
                        min="1"
                        max={pagination?.totalPages || 1}
                        style={{
                          width: '50px',
                          padding: '2px 4px',
                          borderRadius: '4px',
                          border: '1px solid var(--border-color)',
                          backgroundColor: 'var(--bg-secondary)',
                          color: 'var(--text-primary)',
                          textAlign: 'center'
                        }}
                      />
                    </form>
                    of {pagination?.totalPages || 1}
                  </span>
                  <button
                    onClick={() => handlePageChange((pagination?.page || 1) + 1)}
                    disabled={(pagination?.page || 1) === (pagination?.totalPages || 1)}
                    className="pagination-button"
                  >
                    Next ▶️
                  </button>
                  <button
                    onClick={() => handlePageChange(pagination?.totalPages || 1)}
                    disabled={(pagination?.page || 1) === (pagination?.totalPages || 1)}
                    className="pagination-button"
                  >
                    Last ⏭️
                  </button>
                </div>
              </div>

              <div style={{ margin: 'var(--space-6) 0', overflowX: 'auto' }}>
                <table className="modern-table" ref={tableRef}>
                  <thead>
                    <tr>
                      <th style={{ width: '50px' }}>
                        <input
                          type="checkbox"
                          checked={selectedKeywords.length === keywords.length && keywords.length > 0}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setSelectedKeywords(keywords.map(k => k._id));
                            } else {
                              setSelectedKeywords([]);
                            }
                          }}
                        />
                      </th>
                      {columns.filter(col => col.visible).map((column) => (
                        <th
                          key={column.key}
                          style={{ width: `${column.width}px`, cursor: 'pointer' }}
                          onClick={() => handleSort(column.key)}
                        >
                          <div className="header-content">
                            <span className="header-text">
                              {column.label} {getSortIcon(column.key as SortField)}
                            </span>
                          </div>
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {keywords.map((keyword) => (
                      <tr key={keyword._id}>
                        <td style={{ width: '50px' }}>
                          <input
                            type="checkbox"
                            checked={selectedKeywords.includes(keyword._id)}
                            onChange={(e) => {
                              if (e.target.checked) {
                                setSelectedKeywords([...selectedKeywords, keyword._id]);
                              } else {
                                setSelectedKeywords(selectedKeywords.filter(id => id !== keyword._id));
                              }
                            }}
                          />
                        </td>
                        {columns.filter(col => col.visible).map((column) => (
                          <td 
                            key={`${keyword._id}-${column.key}`}
                            style={{ width: `${column.width}px`, cursor: column.key === 'keyword' ? 'pointer' : 'default' }}
                            onClick={() => {
                              if (column.key === 'keyword') {
                                openEditModal(keyword);
                              }
                            }}
                          >
                            {column.key === 'keyword' && (
                              <span style={{ fontWeight: 'bold', color: '#4dabf7' }}>
                                {keyword.keyword}
                              </span>
                            )}
                            {column.key === 'group' && (
                              <select
                                value={keyword.group || 'ai'}
                                onClick={(e) => e.stopPropagation()}
                                onChange={async (e) => {
                                  let newGroup = e.target.value;
                                  
                                  if (newGroup === '__create_new__') {
                                    const newGroupName = prompt('Enter new group name:');
                                    if (newGroupName && newGroupName.trim()) {
                                      newGroup = newGroupName.trim().toLowerCase();
                                      
                                      // Create the new group in the database
                                      try {
                                        const createResponse = await fetch(`${API_URL}/api/groups`, {
                                          method: 'POST',
                                          headers: {
                                            'Content-Type': 'application/json',
                                            ...(token && { 'Authorization': `Bearer ${token}` })
                                          },
                                          body: JSON.stringify({
                                            name: newGroup
                                          }),
                                        });

                                        if (!createResponse.ok) {
                                          const error = await createResponse.json();
                                          if (error.error === 'Group already exists') {
                                            // Group exists, just use it
                                          } else {
                                            alert('Failed to create group: ' + error.error);
                                            return;
                                          }
                                        }

                                        // Add to local availableGroups if not already there
                                        if (!availableGroups.includes(newGroup)) {
                                          setAvailableGroups([...availableGroups, newGroup]);
                                        }
                                      } catch (error) {
                                        console.error('Error creating group:', error);
                                        alert('Failed to create group');
                                        return;
                                      }
                                    } else {
                                      return; // Cancel if no name provided
                                    }
                                  }

                                  try {
                                    const response = await fetch(`${API_URL}/api/keywords/${keyword._id}`, {
                                      method: 'PUT',
                                      headers: {
                                        'Content-Type': 'application/json',
                                        ...(token && { 'Authorization': `Bearer ${token}` })
                                      },
                                      body: JSON.stringify({
                                        keyword: keyword.keyword,
                                        group: newGroup
                                      }),
                                    });
                                    
                                    if (response.ok) {
                                      fetchKeywords();
                                      fetchGroups();
                                    }
                                  } catch (error) {
                                    console.error('Error updating group:', error);
                                  }
                                }}
                                className="input"
                                style={{ 
                                  padding: '4px 8px',
                                  fontSize: '12px',
                                  minWidth: '80px'
                                }}
                              >
                                {availableGroups.map(g => (
                                  <option key={g} value={g}>{g}</option>
                                ))}
                                <option value="__create_new__" style={{ fontStyle: 'italic', color: '#667eea' }}>
                                  + New Group
                                </option>
                              </select>
                            )}
                            {column.key === 'language' && (
                              <span style={{ 
                                fontSize: '12px',
                                fontWeight: 'bold',
                                color: '#495057',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '4px'
                              }}>
                                {(() => {
                                  const lang = keyword.language || 'en';
                                  const flagMap: Record<string, string> = {
                                    'en': '🇺🇸',
                                    'es': '🇪🇸', 
                                    'pt': '🇧🇷',
                                    'ru': '🇷🇺',
                                    'ja': '🇯🇵',
                                    'de': '🇩🇪',
                                    'fr': '🇫🇷',
                                    'it': '🇮🇹',
                                    'nl': '🇳🇱',
                                    'pl': '🇵🇱',
                                    'sv': '🇸🇪',
                                    'ko': '🇰🇷'
                                  };
                                  return (
                                    <>
                                      <span style={{ fontSize: '16px' }}>{flagMap[lang] || '🌐'}</span>
                                      <span style={{ textTransform: 'uppercase' }}>{lang}</span>
                                    </>
                                  );
                                })()}
                              </span>
                            )}
                            {column.key === 'relevancy' && (
                              <span 
                                style={{ 
                                  fontWeight: 'bold',
                                  color: keyword.relevancy === true ? '#51cf66' : '#ff6b6b',
                                  cursor: 'pointer',
                                  fontSize: '12px',
                                  padding: '2px 6px',
                                  borderRadius: '4px',
                                  backgroundColor: keyword.relevancy === true ? '#e7f5e7' : '#ffe0e0',
                                  border: '1px solid transparent',
                                  transition: 'all 0.2s'
                                }}
                                title={`${keyword.relevancyReason || 'No analysis available'}\n\nClick to toggle: YES ↔ NO`}
                                onClick={async (e) => {
                                  e.stopPropagation();
                                  
                                  // Toggle relevancy: true ↔ false (no N/A)
                                  let newRelevancy: boolean;
                                  if (keyword.relevancy === true) {
                                    newRelevancy = false;
                                  } else {
                                    newRelevancy = true;
                                  }
                                  
                                  try {
                                    const response = await fetch(`${API_URL}/api/keywords/${keyword._id}`, {
                                      method: 'PUT',
                                      headers: {
                                        'Content-Type': 'application/json',
                                        ...(token && { 'Authorization': `Bearer ${token}` })
                                      },
                                      body: JSON.stringify({
                                        relevancy: newRelevancy,
                                        relevancyReason: `Manually set to ${newRelevancy ? 'relevant' : 'not relevant'} by user`
                                      }),
                                    });

                                    if (response.ok) {
                                      // Update local state
                                      setKeywords(prev =>
                                        prev.map(k =>
                                          k._id === keyword._id
                                            ? {
                                                ...k,
                                                relevancy: newRelevancy,
                                                relevancyReason: `Manually set to ${newRelevancy ? 'relevant' : 'not relevant'} by user`
                                              }
                                            : k
                                        )
                                      );

                                      // Show feedback
                                      setSuccessMessage(`Relevancy updated for "${keyword.keyword}"`);
                                      setTimeout(() => setSuccessMessage(null), 2000);
                                    } else {
                                      console.error('Failed to update relevancy');
                                      setError('Failed to update relevancy');
                                      setTimeout(() => setError(null), 3000);
                                    }
                                  } catch (error) {
                                    console.error('Error updating relevancy:', error);
                                    setError('Error updating relevancy');
                                    setTimeout(() => setError(null), 3000);
                                  }
                                }}
                                onMouseEnter={(e) => {
                                  e.currentTarget.style.backgroundColor = keyword.relevancy === true ? '#c3e6c3' : '#ffcccc';
                                  e.currentTarget.style.borderColor = '#007bff';
                                }}
                                onMouseLeave={(e) => {
                                  e.currentTarget.style.backgroundColor = keyword.relevancy === true ? '#e7f5e7' : '#ffe0e0';
                                  e.currentTarget.style.borderColor = 'transparent';
                                }}
                              >
                                {keyword.relevancy === true ? '✅ YES' : '❌ NO'}
                              </span>
                            )}
                            {column.key === 'relevancyScore' && (
                              <span 
                                style={{ 
                                  fontWeight: 'bold',
                                  color: keyword.relevancyScore !== null && keyword.relevancyScore !== undefined 
                                    ? keyword.relevancyScore >= 0.8 ? '#51cf66' 
                                    : keyword.relevancyScore >= 0.5 ? '#fab005' 
                                    : '#ff6b6b'
                                    : '#868e96',
                                  fontSize: '12px'
                                }}
                                title={`Relevancy Score: ${keyword.relevancyScore !== null && keyword.relevancyScore !== undefined ? (keyword.relevancyScore * 100).toFixed(0) + '%' : 'N/A'}`}
                              >
                                {keyword.relevancyScore !== null && keyword.relevancyScore !== undefined 
                                  ? (keyword.relevancyScore * 100).toFixed(0) + '%' 
                                  : 'N/A'}
                              </span>
                            )}
                            {column.key === 'instagramSearchTime' && formatDate(keyword.instagramSearchTime)}
                            {column.key === 'tiktokSearchTime' && formatDate(keyword.tiktokSearchTime)}
                            {column.key === 'youtubeSearchTime' && formatDate(keyword.youtubeSearchTime || null)}
                            {column.key === 'createdAt' && formatDate(keyword.createdAt)}
                            {column.key === 'instagramAccountCount' && (
                              <span 
                                style={{ 
                                  fontWeight: 'bold', 
                                  color: keyword.instagramAccountCount ? '#51cf66' : '#868e96',
                                  cursor: keyword.instagramAccountCount ? 'pointer' : 'default',
                                  textDecoration: keyword.instagramAccountCount ? 'underline' : 'none'
                                }}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  if (keyword.instagramAccountCount) {
                                    navigate(`/creators/instagram?keyword=${encodeURIComponent(keyword.keyword)}&exactMatch=true`);
                                  }
                                }}
                                title={keyword.instagramAccountCount ? `View ${keyword.instagramAccountCount} Instagram creators` : ''}
                              >
                                {keyword.instagramAccountCount || 0}
                              </span>
                            )}
                            {column.key === 'tiktokAccountCount' && (
                              <span
                                style={{
                                  fontWeight: 'bold',
                                  color: keyword.tiktokAccountCount ? '#51cf66' : '#868e96',
                                  cursor: keyword.tiktokAccountCount ? 'pointer' : 'default',
                                  textDecoration: keyword.tiktokAccountCount ? 'underline' : 'none'
                                }}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  if (keyword.tiktokAccountCount) {
                                    navigate(`/creators/tiktok?keyword=${encodeURIComponent(keyword.keyword)}&exactMatch=true`);
                                  }
                                }}
                                title={keyword.tiktokAccountCount ? `View ${keyword.tiktokAccountCount} TikTok creators` : ''}
                              >
                                {keyword.tiktokAccountCount || 0}
                              </span>
                            )}
                            {column.key === 'youtubeAccountCount' && (
                              <span
                                style={{
                                  fontWeight: 'bold',
                                  color: keyword.youtubeAccountCount ? '#51cf66' : '#868e96',
                                  cursor: keyword.youtubeAccountCount ? 'pointer' : 'default',
                                  textDecoration: keyword.youtubeAccountCount ? 'underline' : 'none'
                                }}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  if (keyword.youtubeAccountCount) {
                                    navigate(`/creators/youtube?keyword=${encodeURIComponent(keyword.keyword)}&exactMatch=true`);
                                  }
                                }}
                                title={keyword.youtubeAccountCount ? `View ${keyword.youtubeAccountCount} YouTube creators` : ''}
                              >
                                {keyword.youtubeAccountCount || 0}
                              </span>
                            )}
                            {column.key === 'actions' && (
                              <div style={{ display: 'flex', gap: '4px', justifyContent: 'center' }}>
                                <button
                                  onClick={() => openTikTokSearchModal(keyword.keyword)}
                                  className="btn btn-primary btn-sm"
                                  style={{ padding: '4px 8px', fontSize: '12px' }}
                                  title="Find TikTok Creators"
                                >
                                  TikTok
                                </button>
                                <button
                                  onClick={() => openInstagramSearchModal(keyword.keyword)}
                                  style={{
                                    padding: '4px 8px',
                                    fontSize: '12px',
                                    background: 'linear-gradient(45deg, #f09433 0%,#e6683c 25%,#dc2743 50%,#cc2366 75%,#bc1888 100%)',
                                    color: 'white',
                                    border: 'none',
                                    borderRadius: '4px',
                                    cursor: 'pointer',
                                    fontWeight: '500'
                                  }}
                                  title="Find Instagram Creators"
                                >
                                  Instagram
                                </button>
                                <button
                                  onClick={() => openYouTubeSearchModal(keyword.keyword)}
                                  style={{
                                    padding: '4px 8px',
                                    fontSize: '12px',
                                    background: 'linear-gradient(135deg, #FF0000 0%, #CC0000 100%)',
                                    color: 'white',
                                    border: 'none',
                                    borderRadius: '4px',
                                    cursor: 'pointer',
                                    fontWeight: '500'
                                  }}
                                  title="Search YouTube Videos"
                                >
                                  YouTube
                                </button>
                                <button
                                  onClick={() => openRelatedKeywordsModal(keyword.keyword)}
                                  style={{
                                    padding: '4px 8px',
                                    fontSize: '12px',
                                    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                                    color: 'white',
                                    border: 'none',
                                    borderRadius: '4px',
                                    cursor: 'pointer',
                                    fontWeight: '500'
                                  }}
                                  title="Find Related Keywords"
                                >
                                  Related
                                </button>
                                <button
                                  onClick={() => openTranslatedKeywordsModal(keyword.keyword)}
                                  style={{
                                    padding: '4px 8px',
                                    fontSize: '12px',
                                    background: 'linear-gradient(135deg, #4ECDC4 0%, #44A08D 100%)',
                                    color: 'white',
                                    border: 'none',
                                    borderRadius: '4px',
                                    cursor: 'pointer',
                                    fontWeight: '500'
                                  }}
                                  title="Get Translated Keywords"
                                >
                                  Translate
                                </button>
                                <button
                                  onClick={() => openAlternativeKeywordsModal(keyword.keyword)}
                                  style={{
                                    padding: '4px 8px',
                                    fontSize: '12px',
                                    background: 'linear-gradient(135deg, #FF6B6B 0%, #4ECDC4 100%)',
                                    color: 'white',
                                    border: 'none',
                                    borderRadius: '4px',
                                    cursor: 'pointer',
                                    fontWeight: '500'
                                  }}
                                  title="Get Alternative Keywords"
                                >
                                  Alternatives
                                </button>
                                <button
                                  onClick={() => deleteKeyword(keyword._id)}
                                  className="btn btn-error btn-sm"
                                  style={{ padding: '4px 8px', fontSize: '12px' }}
                                  title="Delete Keyword"
                                >
                                  Delete
                                </button>
                              </div>
                            )}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              
              {/* Pagination controls */}
              <div className="pagination">
                <div className="pagination-info">
                  Showing {keywords.length} of {pagination?.total || 0} keywords
                </div>
                <div className="pagination-controls">
                  <button
                    onClick={() => handlePageChange(1)}
                    disabled={(pagination?.page || 1) === 1}
                    className="pagination-button"
                  >
                    ⏮️ First
                  </button>
                  <button
                    onClick={() => handlePageChange((pagination?.page || 1) - 1)}
                    disabled={(pagination?.page || 1) === 1}
                    className="pagination-button"
                  >
                    ◀️ Previous
                  </button>
                  <span className="page-info" style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                    Page 
                    <form onSubmit={handlePageInputSubmit} style={{ display: 'inline' }}>
                      <input
                        type="number"
                        value={pageInput || (pagination?.page || 1)}
                        onChange={(e) => setPageInput(e.target.value)}
                        min="1"
                        max={pagination?.totalPages || 1}
                        style={{ 
                          width: '50px', 
                          padding: '2px 4px',
                          borderRadius: '4px',
                          border: '1px solid var(--border-color)',
                          backgroundColor: 'var(--bg-secondary)',
                          color: 'var(--text-primary)',
                          textAlign: 'center'
                        }}
                      />
                    </form>
                    of {pagination?.totalPages || 1}
                  </span>
                  <button
                    onClick={() => handlePageChange((pagination?.page || 1) + 1)}
                    disabled={(pagination?.page || 1) === (pagination?.totalPages || 1)}
                    className="pagination-button"
                  >
                    Next ▶️
                  </button>
                  <button
                    onClick={() => handlePageChange(pagination?.totalPages || 1)}
                    disabled={(pagination?.page || 1) === (pagination?.totalPages || 1)}
                    className="pagination-button"
                  >
                    Last ⏭️
                  </button>
                </div>
              </div>
            </div>
          )}
        </>
      )}
      
      {/* Groups tab content */}
      {mainTab === 'groups' && (
        <div>
          <div style={{ marginBottom: 'var(--space-6)' }}>
            <h2 className="text-xl font-semibold mb-4">Keyword Groups</h2>
            <p className="text-secondary mb-6">Click on a group to view all keywords in that group.</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {groups.map(group => (
              <div
                key={group.name}
                onClick={() => {
                  setSelectedGroup(group.name);
                  setMainTab('keywords');
                }}
                className="card cursor-pointer hover:shadow-lg transition-shadow"
                style={{
                  padding: 'var(--space-6)',
                  borderRadius: 'var(--radius-lg)',
                  backgroundColor: 'var(--bg-primary)',
                  border: '1px solid var(--border-color)',
                  cursor: 'pointer'
                }}
              >
                <div className="flex justify-between items-start">
                  <h3 className="text-lg font-semibold capitalize">{group.name}</h3>
                  <div className="flex items-center gap-2">
                    <span className="badge badge-primary">{group.count}</span>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        setSelectedGroup(group.name);
                        setMainTab('keywords');
                      }}
                      className="btn btn-sm btn-primary"
                    >
                      View Keywords →
                    </button>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        initiateDeleteGroup(group._id, group.name);
                      }}
                      className="btn btn-sm btn-danger"
                      style={{
                        backgroundColor: '#ef4444',
                        color: 'white',
                        border: 'none',
                        padding: '4px 8px'
                      }}
                      title={`Delete group "${group.name}"`}
                    >
                      🗑️
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {groups.length === 0 && (
            <div className="text-center py-12">
              <p className="text-secondary">No groups found. Create your first keyword to get started.</p>
            </div>
          )}
        </div>
      )}

      {/* Similar tab content */}
      {mainTab === 'similar' && (
        <div>
          <div style={{ marginBottom: 'var(--space-6)' }}>
            <h2 className="text-xl font-semibold mb-4">Instagram Profile Scraper</h2>
            <p className="text-secondary mb-6">Enter an Instagram username to fetch profile information using APIFY.</p>
          </div>

          {/* Search Form */}
          <form
            onSubmit={(e) => {
              e.preventDefault();
              searchSimilarProfiles(similarUsername);
            }}
            className="flex gap-3 items-center mb-6"
          >
            <input
              type="text"
              placeholder="Instagram username (e.g., stevenwommack)"
              value={similarUsername}
              onChange={(e) => setSimilarUsername(e.target.value)}
              className="input flex-1"
              style={{
                padding: 'var(--space-3)',
                borderRadius: 'var(--radius-md)',
                border: '1px solid var(--border-color)',
                fontSize: '14px'
              }}
              disabled={similarSearching}
            />
            <button
              type="submit"
              className="btn btn-primary"
              disabled={similarSearching || !similarUsername.trim()}
            >
              {similarSearching ? '🔍 Searching...' : '🔍 Search'}
            </button>
          </form>

          {/* Error Message */}
          {similarError && (
            <div
              className="mb-4"
              style={{
                padding: 'var(--space-4)',
                backgroundColor: '#fee',
                borderRadius: 'var(--radius-md)',
                border: '1px solid #fcc',
                color: '#c33'
              }}
            >
              ⚠️ {similarError}
            </div>
          )}

          {/* Loading State */}
          {similarSearching && (
            <div className="text-center py-12">
              <div className="text-lg mb-2">🔄 Fetching Instagram profile...</div>
              <p className="text-secondary">This may take up to 5 minutes. Please wait.</p>
            </div>
          )}

          {/* Results */}
          {similarResult && !similarSearching && (
            <div>
              {(() => {
                // Extract relatedProfiles from the result
                // Try different possible structures
                let profiles = [];

                if (Array.isArray(similarResult)) {
                  profiles = similarResult[0]?.relatedProfiles || [];
                } else if (similarResult.relatedProfiles) {
                  profiles = similarResult.relatedProfiles;
                } else if (Array.isArray(similarResult.data)) {
                  profiles = similarResult.data[0]?.relatedProfiles || [];
                }

                console.log('Similar result structure:', similarResult);
                console.log('Extracted profiles:', profiles);

                if (profiles.length === 0) {
                  return (
                    <div className="text-center py-12">
                      <p className="text-secondary">No related profiles found.</p>
                      <button
                        onClick={() => setShowJsonModal(true)}
                        className="btn btn-secondary"
                        style={{ marginTop: '12px' }}
                      >
                        View Raw JSON
                      </button>
                    </div>
                  );
                }

                return (
                  <div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 'var(--space-4)' }}>
                      <h3 className="text-lg font-semibold">
                        Related Profiles ({profiles.length})
                      </h3>
                      <button
                        onClick={() => setShowJsonModal(true)}
                        className="btn btn-secondary"
                        style={{ fontSize: '14px', padding: '8px 16px' }}
                      >
                        📋 View JSON
                      </button>
                    </div>
                    <div
                      style={{
                        display: 'grid',
                        gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))',
                        gap: '12px'
                      }}
                    >
                      {profiles.map((profile: any) => (
                        <a
                          key={profile.id}
                          href={`https://www.instagram.com/${profile.username}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="card"
                          style={{
                            padding: '12px',
                            borderRadius: '8px',
                            backgroundColor: 'var(--bg-primary)',
                            border: '1px solid var(--border-color)',
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            gap: '8px',
                            textDecoration: 'none',
                            transition: 'all 0.2s',
                            cursor: 'pointer'
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.transform = 'translateY(-2px)';
                            e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.1)';
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.transform = 'translateY(0)';
                            e.currentTarget.style.boxShadow = 'none';
                          }}
                        >
                          {/* Profile Picture */}
                          <div style={{ position: 'relative' }}>
                            <img
                              src={`${API_URL}/api/similar/image-proxy?url=${encodeURIComponent(profile.profile_pic_url)}&username=${profile.username}`}
                              alt={profile.username}
                              style={{
                                width: '50px',
                                height: '50px',
                                borderRadius: '50%',
                                objectFit: 'cover',
                                border: '2px solid var(--border-color)',
                                backgroundColor: '#f0f0f0'
                              }}
                            />
                            {profile.is_verified && (
                              <span
                                title="Verified"
                                style={{
                                  position: 'absolute',
                                  bottom: '-2px',
                                  right: '-2px',
                                  backgroundColor: '#4285f4',
                                  color: 'white',
                                  borderRadius: '50%',
                                  width: '18px',
                                  height: '18px',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  fontSize: '11px',
                                  fontWeight: 'bold',
                                  border: '2px solid white'
                                }}
                              >
                                ✓
                              </span>
                            )}
                          </div>

                          {/* Username */}
                          <div
                            style={{
                              fontSize: '13px',
                              fontWeight: '600',
                              color: 'var(--text-primary)',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                              width: '100%',
                              textAlign: 'center'
                            }}
                            title={profile.username}
                          >
                            @{profile.username}
                          </div>

                          {/* Full Name */}
                          <div
                            style={{
                              fontSize: '11px',
                              color: 'var(--text-secondary)',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                              width: '100%',
                              textAlign: 'center'
                            }}
                            title={profile.full_name}
                          >
                            {profile.full_name}
                          </div>

                          {/* Status Badges */}
                          <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap', justifyContent: 'center', fontSize: '10px' }}>
                            {profile.is_private ? (
                              <span
                                style={{
                                  padding: '2px 6px',
                                  backgroundColor: '#fee',
                                  color: '#c33',
                                  borderRadius: '3px',
                                  fontWeight: '500'
                                }}
                              >
                                🔒
                              </span>
                            ) : (
                              <span
                                style={{
                                  padding: '2px 6px',
                                  backgroundColor: '#efe',
                                  color: '#3c3',
                                  borderRadius: '3px',
                                  fontWeight: '500'
                                }}
                              >
                                🌐
                              </span>
                            )}

                            {/* Registration Status Badge */}
                            {registrationStatus[profile.username] ? (
                              <span
                                style={{
                                  padding: '2px 6px',
                                  backgroundColor: '#e3f2fd',
                                  color: '#1976d2',
                                  borderRadius: '3px',
                                  fontWeight: '500'
                                }}
                                title="Already registered"
                              >
                                ✓ Registered
                              </span>
                            ) : null}
                          </div>

                          {/* Register Button (only if not registered) */}
                          {!registrationStatus[profile.username] && (
                            <button
                              onClick={async (e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                setRegisteringUsername(profile.username);
                                setRegisterLogs([]);
                                setVerificationResult(null);
                                setShowRegisterModal(true);

                                // Auto-start verification
                                setTimeout(() => {
                                  runVerification(profile.username);
                                }, 100);
                              }}
                              className="btn btn-primary"
                              style={{
                                fontSize: '11px',
                                padding: '6px 12px',
                                marginTop: '4px',
                                width: '100%'
                              }}
                            >
                              + Register
                            </button>
                          )}

                          {/* Related Button - Opens similar search in new tab */}
                          {registrationStatus[profile.username] && (
                            <button
                              onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                window.open(`/find/similar?username=${encodeURIComponent(profile.username)}`, '_blank');
                              }}
                              className="btn btn-secondary"
                              style={{
                                fontSize: '11px',
                                padding: '6px 12px',
                                marginTop: '4px',
                                width: '100%',
                                backgroundColor: '#8b5cf6',
                                border: 'none'
                              }}
                            >
                              🔗 Related
                            </button>
                          )}
                        </a>
                      ))}
                    </div>
                  </div>
                );
              })()}
            </div>
          )}

          {/* Empty State */}
          {!similarResult && !similarSearching && !similarError && (
            <div className="text-center py-12">
              <p className="text-secondary">Enter a username above to get started.</p>
            </div>
          )}

          {/* JSON Modal */}
          {showJsonModal && similarResult && createPortal(
            <div
              style={{
                position: 'fixed',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                backgroundColor: 'rgba(0, 0, 0, 0.5)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                zIndex: 10000,
                padding: 'var(--space-4)'
              }}
              onClick={() => setShowJsonModal(false)}
            >
              <div
                onClick={(e) => e.stopPropagation()}
                style={{
                  backgroundColor: 'var(--bg-primary)',
                  borderRadius: 'var(--radius-lg)',
                  padding: 'var(--space-6)',
                  maxWidth: '900px',
                  width: '100%',
                  maxHeight: '90vh',
                  display: 'flex',
                  flexDirection: 'column',
                  boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)'
                }}
              >
                {/* Modal Header */}
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 'var(--space-4)' }}>
                  <h2 className="text-xl font-semibold">JSON Response</h2>
                  <div style={{ display: 'flex', gap: '8px' }}>
                    <button
                      onClick={() => {
                        navigator.clipboard.writeText(JSON.stringify(similarResult, null, 2));
                        alert('JSON copied to clipboard!');
                      }}
                      className="btn btn-secondary"
                      style={{ fontSize: '14px', padding: '8px 16px' }}
                    >
                      📋 Copy
                    </button>
                    <button
                      onClick={() => setShowJsonModal(false)}
                      className="btn btn-secondary"
                      style={{ fontSize: '18px', padding: '8px 12px', lineHeight: '1' }}
                    >
                      ✕
                    </button>
                  </div>
                </div>

                {/* JSON Content */}
                <div
                  style={{
                    flex: 1,
                    overflow: 'auto',
                    backgroundColor: '#f5f5f5',
                    borderRadius: 'var(--radius-md)',
                    padding: 'var(--space-4)',
                    border: '1px solid var(--border-color)'
                  }}
                >
                  <pre
                    style={{
                      margin: 0,
                      fontSize: '12px',
                      lineHeight: '1.6',
                      whiteSpace: 'pre-wrap',
                      wordBreak: 'break-word',
                      fontFamily: 'Monaco, Consolas, "Courier New", monospace'
                    }}
                  >
                    {JSON.stringify(similarResult, null, 2)}
                  </pre>
                </div>
              </div>
            </div>,
            document.body
          )}

          {/* Registration Modal */}
          {showRegisterModal && createPortal(
            <div
              style={{
                position: 'fixed',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                backgroundColor: 'rgba(0, 0, 0, 0.5)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                zIndex: 10001,
                padding: 'var(--space-4)'
              }}
              onClick={() => setShowRegisterModal(false)}
            >
              <div
                onClick={(e) => e.stopPropagation()}
                style={{
                  backgroundColor: 'var(--bg-primary)',
                  borderRadius: 'var(--radius-lg)',
                  padding: 'var(--space-6)',
                  maxWidth: '700px',
                  width: '100%',
                  maxHeight: '80vh',
                  display: 'flex',
                  flexDirection: 'column',
                  boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)'
                }}
              >
                {/* Modal Header */}
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 'var(--space-4)' }}>
                  <h2 className="text-xl font-semibold">Register @{registeringUsername}</h2>
                  <button
                    onClick={() => setShowRegisterModal(false)}
                    className="btn btn-secondary"
                    style={{ fontSize: '18px', padding: '8px 12px', lineHeight: '1' }}
                  >
                    ✕
                  </button>
                </div>

                {/* Manual Register Button - Show if verification failed */}
                {verificationResult && !verificationResult.success && !isVerifying && (
                  <button
                    onClick={async () => {
                      const result = await manualRegister(
                        registeringUsername,
                        verificationResult.email,
                        verificationResult.medianViews,
                        verificationResult.relevancyData
                      );

                      if (result?.success) {
                        setTimeout(() => {
                          setShowRegisterModal(false);
                        }, 2000);
                      }
                    }}
                    className="btn btn-warning"
                    style={{
                      marginBottom: 'var(--space-4)',
                      backgroundColor: '#f59e0b',
                      border: 'none',
                      color: 'white'
                    }}
                  >
                    🔧 Force Register Manually
                  </button>
                )}

                {/* Logs */}
                <div
                  style={{
                    flex: 1,
                    overflow: 'auto',
                    backgroundColor: '#1e1e1e',
                    borderRadius: 'var(--radius-md)',
                    padding: 'var(--space-4)',
                    fontFamily: 'Monaco, Consolas, "Courier New", monospace',
                    fontSize: '12px',
                    lineHeight: '1.6',
                    color: '#d4d4d4'
                  }}
                >
                  {registerLogs.length === 0 && !isVerifying ? (
                    <div style={{ color: '#888', textAlign: 'center', padding: '20px' }}>
                      Verification starting...
                    </div>
                  ) : isVerifying && registerLogs.length === 0 ? (
                    <div style={{ color: '#4ec9b0', textAlign: 'center', padding: '20px' }}>
                      🔄 Running verification...
                    </div>
                  ) : (
                    registerLogs.map((log, idx) => (
                      <div
                        key={idx}
                        style={{
                          marginBottom: '8px',
                          color:
                            log.type === 'error' ? '#f48771' :
                            log.type === 'success' ? '#73c991' :
                            log.type === 'warning' ? '#dcdcaa' :
                            log.type === 'step' ? '#4ec9b0' :
                            '#d4d4d4'
                        }}
                      >
                        <span style={{ color: '#888', marginRight: '8px' }}>
                          {new Date(log.timestamp).toLocaleTimeString()}
                        </span>
                        {log.message}
                      </div>
                    ))
                  )}
                </div>
              </div>
            </div>,
            document.body
          )}
        </div>
      )}

      {/* Keyword CRUD Modal */}
      {showKeywordModal && createPortal(
        <div 
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 10000,
            padding: 'var(--space-4)'
          }}
          onMouseDown={() => setIsDragging(false)}
          onMouseMove={() => setIsDragging(true)}
          onMouseUp={(e) => {
            if (!isDragging && e.target === e.currentTarget) {
              setShowKeywordModal(false);
            }
            setIsDragging(false);
          }}
        >
          <div
            className="bg-primary rounded-xl shadow-xl"
            style={{
              maxWidth: '500px',
              width: '100%',
              maxHeight: '90vh',
              overflow: 'auto',
              padding: 'var(--space-8)'
            }}
            onMouseDown={(e) => e.stopPropagation()}
            onMouseUp={(e) => e.stopPropagation()}
          >
            <div className="flex justify-between items-start mb-6">
              <h2 className="text-2xl font-bold text-primary">
                {editingKeyword ? 'Edit Keyword' : 'Create New Keyword'}
              </h2>
              <button
                className="btn btn-secondary"
                onClick={() => setShowKeywordModal(false)}
              >
                ✕
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-2">
                  Keyword <span className="text-error">*</span>
                </label>
                <textarea
                  value={keywordForm.keyword}
                  onChange={(e) => setKeywordForm(prev => ({ ...prev, keyword: e.target.value }))}
                  className="input w-full"
                  placeholder="Enter keyword(s) - separate multiple with commas"
                  required
                  rows={3}
                  style={{ resize: 'vertical', minHeight: '80px' }}
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">
                  Group
                </label>
                <select
                  value={keywordForm.group}
                  onChange={(e) => setKeywordForm(prev => ({ ...prev, group: e.target.value }))}
                  className="input w-full"
                >
                  {availableGroups.map(g => (
                    <option key={g} value={g}>{g}</option>
                  ))}
                </select>
              </div>

              {/* AI Keyword Generator Section */}
              {!editingKeyword && (
                <div className="border border-purple-300 rounded-lg p-4 bg-purple-50">
                  <h3 className="font-semibold text-md flex items-center gap-2 mb-3">
                    <span>🤖</span>
                    <span>AI Keyword Generator</span>
                  </h3>

                  <div>
                    <label className="block text-sm font-medium mb-2">
                      Enter prompt to generate keywords
                    </label>
                    <div className="flex gap-2">
                      <input
                        type="text"
                        value={customPrompt}
                        onChange={(e) => setCustomPrompt(e.target.value)}
                        className="input flex-1"
                        placeholder="e.g., Find 20 competitors of Nike in sportswear"
                      />
                      <button
                        type="button"
                        onClick={fetchAiSuggestions}
                        disabled={!customPrompt.trim() || loadingAiSuggestions}
                        className="btn btn-primary flex items-center gap-2"
                        style={{
                          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                          minWidth: '120px'
                        }}
                      >
                        {loadingAiSuggestions ? (
                          <>
                            <div className="spinner" style={{ width: '14px', height: '14px' }} />
                            Loading...
                          </>
                        ) : (
                          <>
                            <span>✨</span>
                            <span>Generate</span>
                          </>
                        )}
                      </button>
                    </div>
                    <p className="text-xs text-gray-600 mt-2">
                      Generated keywords will be automatically added to the keyword field above
                    </p>
                  </div>
                </div>
              )}
            </div>

            <div className="flex gap-3 mt-6">
              <button
                className="btn btn-primary flex-1"
                onClick={editingKeyword ? updateKeyword : createKeyword}
                disabled={!keywordForm.keyword.trim()}
              >
                {editingKeyword ? 'Update Keyword' : 'Create Keyword'}
              </button>
              <button
                className="btn btn-secondary"
                onClick={() => setShowKeywordModal(false)}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}



      {/* Floating Loading UI for Adding Creators */}
      {(addingCreators.size > 0 || instagramAddingCreators.size > 0 || bulkAddingPosts || youtubeAddingCreators.size > 0) && createPortal(
        <div 
          style={{
            position: 'fixed',
            top: '20px',
            right: '20px',
            zIndex: 10001,
            backgroundColor: 'rgba(0, 0, 0, 0.9)',
            color: 'white',
            padding: '16px 20px',
            borderRadius: '12px',
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)',
            backdropFilter: 'blur(16px)',
            border: '1px solid rgba(255, 255, 255, 0.1)',
            minWidth: '280px',
            animation: 'fadeInSlideDown 0.3s ease-out'
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <div style={{
              width: '20px',
              height: '20px',
              border: '2px solid #4dabf7',
              borderTop: '2px solid transparent',
              borderRadius: '50%',
              animation: 'spin 1s linear infinite'
            }}></div>
            <div>
              <div style={{ fontWeight: 'bold', fontSize: '14px' }}>
                Adding Creators...
              </div>
              <div style={{ fontSize: '12px', color: '#aaa', marginTop: '2px' }}>
                {bulkAddingPosts
                  ? bulkAddingProgress.total > 0
                    ? `Adding ${bulkAddingProgress.current}/${bulkAddingProgress.total} creators...`
                    : `Adding selected creators...`
                  : (() => {
                      const totalCreators = addingCreators.size + instagramAddingCreators.size;
                      const platforms = [];
                      if (addingCreators.size > 0) platforms.push(`TikTok: ${addingCreators.size}`);
                      if (instagramAddingCreators.size > 0) platforms.push(`Instagram: ${instagramAddingCreators.size}`);
                      return totalCreators === 1 
                        ? `${platforms.join(', ')} creator in progress`
                        : `${totalCreators} creators in progress (${platforms.join(', ')})`;
                    })()
                }
              </div>
              {bulkAddingPosts && bulkAddingProgress.total > 0 && (
                <div style={{ marginTop: '8px' }}>
                  <div style={{
                    width: '100%',
                    height: '4px',
                    backgroundColor: 'rgba(255, 255, 255, 0.2)',
                    borderRadius: '2px',
                    overflow: 'hidden'
                  }}>
                    <div style={{
                      height: '100%',
                      backgroundColor: '#4dabf7',
                      borderRadius: '2px',
                      width: `${(bulkAddingProgress.current / bulkAddingProgress.total) * 100}%`,
                      transition: 'width 0.3s ease'
                    }}></div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* TikTok Search Modal - Now using separate component */}
      <TikTokCreatorsSearchModal
        showTikTokModal={showTikTokModal}
        setShowTikTokModal={setShowTikTokModal}
        tiktokSearchResult={tiktokSearchResult}
        setTiktokSearchResult={setTiktokSearchResult}
        selectedPosts={selectedPosts}
        setSelectedPosts={setSelectedPosts}
        searchLogs={searchLogs}
        clearSearchLogs={clearSearchLogs}
        showSearchLogs={showSearchLogs}
        setShowSearchLogs={setShowSearchLogs}
        tiktokSearchKeyword={tiktokSearchKeyword}
        setTiktokSearchKeyword={setTiktokSearchKeyword}
        tiktokSearchType={tiktokSearchType}
        setTiktokSearchType={setTiktokSearchType}
        tiktokSearchParams={tiktokSearchParams}
        setTiktokSearchParams={setTiktokSearchParams}
        tiktokMinViews={tiktokMinViews}
        setTiktokMinViews={setTiktokMinViews}
        tiktokSearching={tiktokSearching}
        searchTikTokCreators={searchTikTokCreators}
        cancelCurrentSearch={cancelCurrentSearch}
        tiktokSortBy={tiktokSortBy}
        setTiktokSortBy={setTiktokSortBy}
        tiktokMinLikes={tiktokMinLikes}
        setTiktokMinLikes={setTiktokMinLikes}
        tiktokMinComments={tiktokMinComments}
        setTiktokMinComments={setTiktokMinComments}
        sortAndFilterTikTokResults={sortAndFilterTikTokResults}
        addedCreators={addedCreators}
        existingCreators={existingCreators}
        toggleSelectAllPosts={toggleSelectAllPosts}
        togglePostSelection={togglePostSelection}
        addingCreators={addingCreators}
        addTikTokCreator={addTikTokCreator}
        fetchTranscript={fetchTranscript}
        addSelectedPosts={addSelectedPosts}
        bulkAddingPosts={bulkAddingPosts}
      />
      
      {/* Original comment preserved to keep same structure */}
      {false && createPortal(
        <div 
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 10000,
            padding: 'var(--space-4)'
          }}
          onClick={() => {
            cancelCurrentSearch();
            setShowTikTokModal(false);
            setTiktokSearchResult(null);
            setSelectedPosts(new Set());
            clearSearchLogs();
            setShowSearchLogs(false);
          }}
        >
          <div 
            className="bg-primary rounded-xl shadow-xl"
            style={{
              maxWidth: '90%',
              width: '900px',
              maxHeight: '90vh',
              overflow: 'hidden',
              display: 'flex',
              flexDirection: 'column'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{ padding: 'var(--space-8)', borderBottom: '1px solid var(--border-color)' }}>
              {!tiktokSearchResult ? (
                /* 검색 전 - 검색 UI 표시 */
                <>
                  <div className="flex justify-between items-start mb-4">
                    <div>
                      <h2 className="text-2xl font-bold text-primary">
                        TikTok Creators Search
                      </h2>
                      <div className="mt-3">
                        <label className="block text-sm font-medium mb-1">Search Term</label>
                        <input
                          type="text"
                          value={tiktokSearchKeyword}
                          onChange={(e) => {
                            let value = e.target.value;
                            setTiktokSearchKeyword(value);
                          }}
                          className="input w-full"
                          placeholder="Enter keyword or competitor name"
                          disabled={tiktokSearching}
                        />
                      </div>
                    </div>
                    <button 
                      className="btn btn-secondary"
                      onClick={() => {
                        cancelCurrentSearch();
                        setShowTikTokModal(false);
                        setTiktokSearchResult(null);
                        setSelectedPosts(new Set());
                        clearSearchLogs();
                        setShowSearchLogs(false);
                      }}
                    >
                      ✕
                    </button>
                  </div>

                  {/* Search Type Selection */}
                  <div className="mt-4">
                    <label className="block text-sm font-medium mb-1">Search Type</label>
                    <select
                      value={tiktokSearchType}
                      onChange={(e) => setTiktokSearchType(e.target.value as 'keyword' | 'hashtag' | 'full-hashtag' | 'full-keyword')}
                      className="input w-full"
                      disabled={tiktokSearching}
                    >
                      <option value="keyword">Keyword Search</option>
                      <option value="hashtag">Hashtag Search</option>
                      <option value="full-keyword">Full Keyword Search (All Pages)</option>
                      <option value="full-hashtag">Full Hashtag Search (All Pages)</option>
                    </select>
                    <p className="text-xs text-gray-500 mt-1">
                      {tiktokSearchType === 'keyword' && 'Search for posts containing keywords'}
                      {tiktokSearchType === 'hashtag' && 'Search for posts with specific hashtag'}
                      {tiktokSearchType === 'full-keyword' && 'Get all posts with keyword (auto-pagination)'}
                      {tiktokSearchType === 'full-hashtag' && 'Get all posts with hashtag within specified days'}
                    </p>
                  </div>

                  {/* Parameter Selection */}
                  <div className="grid grid-cols-2 gap-4 mt-4">
                    {/* Period/Days parameter - shown for all search types */}
                    {(tiktokSearchType === 'keyword' || tiktokSearchType === 'full-keyword') ? (
                      <div>
                        <label className="block text-sm font-medium mb-1">Period</label>
                        <select
                          value={tiktokSearchParams.period}
                          onChange={(e) => setTiktokSearchParams(prev => ({ ...prev, period: e.target.value }))}
                          className="input w-full"
                          disabled={tiktokSearching}
                        >
                          <option value="0">Today</option>
                          <option value="1">Yesterday</option>
                          <option value="7">Last 7 days</option>
                          <option value="30">Last 30 days</option>
                          <option value="90">Last 90 days</option>
                          <option value="180">Last 180 days</option>
                        </select>
                      </div>
                    ) : tiktokSearchType === 'full-hashtag' ? (
                      <div>
                        <label className="block text-sm font-medium mb-1">Days Filter</label>
                        <input
                          type="number"
                          value={tiktokSearchParams.days || 30}
                          onChange={(e) => setTiktokSearchParams(prev => ({ ...prev, days: parseInt(e.target.value) || 30 }))}
                          className="input w-full"
                          placeholder="e.g., 30"
                          disabled={tiktokSearching}
                        />
                      </div>
                    ) : null}

                    {/* Sorting - only for keyword searches */}
                    {(tiktokSearchType === 'keyword' || tiktokSearchType === 'full-keyword') && (
                      <div>
                        <label className="block text-sm font-medium mb-1">Sorting</label>
                        <select
                          value={tiktokSearchParams.sorting}
                          onChange={(e) => setTiktokSearchParams(prev => ({ ...prev, sorting: e.target.value }))}
                          className="input w-full"
                          disabled={tiktokSearching}
                        >
                          <option value="0">By Relevance</option>
                          <option value="1">Most Liked</option>
                        </select>
                      </div>
                    )}

                    {/* Multi-cursor option for hashtag and keyword search */}
                    {(tiktokSearchType === 'hashtag' || tiktokSearchType === 'keyword') && (
                      <div className="col-span-2">
                        <label className="flex items-center gap-2">
                          <input
                            type="checkbox"
                            checked={tiktokSearchParams.enable_multi_cursor}
                            onChange={(e) => setTiktokSearchParams(prev => ({ ...prev, enable_multi_cursor: e.target.checked }))}
                            disabled={tiktokSearching}
                          />
                          <span className="text-sm">Multi-cursor search (fetch multiple pages automatically)</span>
                        </label>
                      </div>
                    )}

                    {/* Match exactly - only for keyword searches */}
                    {(tiktokSearchType === 'keyword' || tiktokSearchType === 'full-keyword') && (
                      <div>
                        <label className="flex items-center gap-2">
                          <input
                            type="checkbox"
                            checked={tiktokSearchParams.match_exactly}
                            onChange={(e) => setTiktokSearchParams(prev => ({ ...prev, match_exactly: e.target.checked }))}
                            disabled={tiktokSearching}
                          />
                          <span className="text-sm">Match exactly in caption</span>
                        </label>
                      </div>
                    )}

                    {/* Minimum Views - for all types */}
                    <div>
                      <label className="block text-sm font-medium mb-1">Minimum Views <span className="text-xs text-gray-500">(optional)</span></label>
                      <input
                        type="number"
                        value={tiktokMinViews || ''}
                        onChange={(e) => setTiktokMinViews(e.target.value ? parseInt(e.target.value) : 0)}
                        className="input w-full"
                        placeholder="Enter minimum views"
                        disabled={tiktokSearching}
                      />
                    </div>

                    {/* Email filter - for all types */}
                    <div>
                      <label className="flex items-center gap-2">
                        <input
                          type="checkbox"
                          checked={tiktokSearchParams.email_filter}
                          onChange={(e) => setTiktokSearchParams(prev => ({ ...prev, email_filter: e.target.checked }))}
                          disabled={tiktokSearching}
                        />
                        <span className="text-sm">Only creators with emails</span>
                      </label>
                    </div>
                  </div>

                  <button
                    className="btn btn-primary w-full mt-4"
                    onClick={() => searchTikTokCreators()}
                    disabled={tiktokSearching || !tiktokSearchKeyword.trim()}
                  >
                    {tiktokSearching ? 'Searching...' :
                      tiktokSearchType === 'hashtag' ? 'Search Hashtag' :
                      tiktokSearchType === 'full-hashtag' ? 'Search Full Hashtag' :
                      tiktokSearchType === 'full-keyword' ? 'Search Full Keyword' :
                      'Search Keyword'
                    }
                  </button>

                  {/* Search Logs Section */}
                  {showSearchLogs && searchLogs.length > 0 && (
                    <div className="mt-4 p-4 bg-gray-800 rounded-lg border border-gray-700">
                      <div className="flex justify-between items-center mb-3">
                        <h3 className="text-sm font-semibold text-white">Search Progress Logs</h3>
                        <button
                          onClick={clearSearchLogs}
                          className="btn btn-secondary text-xs px-3 py-1"
                        >
                          Clear Logs
                        </button>
                      </div>
                      <div 
                        className="text-xs text-gray-300 space-y-1 max-h-40 overflow-y-auto"
                        style={{ fontFamily: 'monospace' }}
                      >
                        {searchLogs.map((log, index) => (
                          <div key={index} className="whitespace-pre-wrap">
                            {log}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </>
              ) : (
                /* 검색 후 - 뒤로가기 버튼과 결과 정보만 표시 */
                <div className="flex justify-between items-center">
                  <div className="flex items-center gap-4">
                    <button 
                      className="btn btn-secondary"
                      onClick={() => {
                        setTiktokSearchResult(null);
                        setSearchLogs([]); // Clear logs when going back
                      }}
                      style={{ padding: '8px 20px' }}
                    >
                      ← Back
                    </button>
                    <div className="flex-1">
                      <h2 className="text-xl font-bold text-primary">
                        Search Results: {tiktokSearchKeyword}
                      </h2>
                      {!tiktokSearching && tiktokSearchResult && !tiktokSearchResult.error && (
                        <>
                          <div className="flex items-center gap-4 mt-2 flex-wrap">
                            <p className="text-sm text-secondary">
                              Found {tiktokSearchResult.count || (Array.isArray(tiktokSearchResult.data) ? tiktokSearchResult.data.length : 0)} posts
                              {Array.isArray(tiktokSearchResult.data) && tiktokSearchType !== 'full-hashtag' && (
                                <span className="ml-2">
                                  ({sortAndFilterTikTokResults(tiktokSearchResult.data).length} shown after filters)
                                </span>
                              )}
                            </p>
                            
                            {/* Existing creators indicator */}
                            {existingCreators.size > 0 && (
                              <div className="flex items-center gap-2 text-xs" style={{ color: '#51cf66' }}>
                                <span>✓</span>
                                <span>{existingCreators.size} creators already in database</span>
                              </div>
                            )}
                          </div>
                          
                          {/* Download Raw Data Button */}
                          {tiktokSearchResult && (
                            <div className="mt-2">
                              <button 
                                onClick={() => {
                                  const dataStr = JSON.stringify(tiktokSearchResult, null, 2);
                                  const dataUri = 'data:application/json;charset=utf-8,'+ encodeURIComponent(dataStr);
                                  const exportFileDefaultName = `tiktok-search-${tiktokSearchKeyword}-${new Date().toISOString()}.json`;
                                  
                                  const linkElement = document.createElement('a');
                                  linkElement.setAttribute('href', dataUri);
                                  linkElement.setAttribute('download', exportFileDefaultName);
                                  linkElement.click();
                                }}
                                className="text-xs text-gray-600 hover:text-gray-800 underline"
                              >
                                📥 Download Raw Data
                              </button>
                            </div>
                          )}
                        </>
                      )}
                    </div>
                  </div>
                  <button 
                    className="btn btn-secondary"
                    onClick={() => {
                      cancelCurrentSearch();
                      setShowTikTokModal(false);
                      setTiktokSearchResult(null);
                      setSelectedPosts(new Set());
                      clearSearchLogs();
                      setShowSearchLogs(false);
                    }}
                  >
                    ✕
                  </button>
                </div>
              )}
            </div>

            <div style={{ 
              flex: 1, 
              overflow: 'auto', 
              padding: 'var(--space-8)'
            }}>
              {/* Search Logs Section for Results View */}
              {showSearchLogs && searchLogs.length > 0 && (
                <div className="mb-6 p-4 bg-gray-800 rounded-lg border border-gray-700">
                  <div className="flex justify-between items-center mb-3">
                    <h3 className="text-sm font-semibold text-white">Search Progress Logs</h3>
                    <button
                      onClick={clearSearchLogs}
                      className="btn btn-secondary text-xs px-3 py-1"
                    >
                      Clear Logs
                    </button>
                  </div>
                  <div
                    className="text-xs text-gray-300 space-y-1 max-h-40 overflow-y-auto"
                    style={{ fontFamily: 'monospace' }}
                  >
                    {searchLogs.map((log, index) => (
                      <div key={index} className="whitespace-pre-wrap">
                        {log}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Sorting and Filtering Controls */}
              {!tiktokSearching && tiktokSearchResult && !tiktokSearchResult.loading && !tiktokSearchResult.error && Array.isArray(tiktokSearchResult.data) && tiktokSearchResult.data.length > 0 && (
                <div style={{
                  marginBottom: 'var(--space-6)',
                  padding: 'var(--space-4)',
                  backgroundColor: 'var(--bg-secondary)',
                  borderRadius: 'var(--radius-lg)',
                  border: '1px solid var(--border-color)'
                }}>
                  <div className="flex flex-wrap gap-4 items-end">
                    {/* Sort Controls */}
                    <div className="flex gap-3 items-end">
                      <div>
                        <label className="block text-sm font-medium mb-1">Sort By</label>
                        <select
                          value={tiktokSortBy}
                          onChange={(e) => setTiktokSortBy(e.target.value as any)}
                          className="input"
                          style={{ minWidth: '120px' }}
                        >
                          <option value="views">Views</option>
                          <option value="likes">Likes</option>
                          <option value="comments">Comments</option>
                          <option value="date">Date</option>
                        </select>
                      </div>
                    </div>
                    
                    {/* Filter Controls */}
                    <div className="flex gap-3 items-end">
                      <div>
                        <label className="block text-sm font-medium mb-1">Min Views</label>
                        <input
                          type="number"
                          value={tiktokMinViews || ''}
                          onChange={(e) => setTiktokMinViews(parseInt(e.target.value) || 0)}
                          className="input"
                          style={{ minWidth: '100px' }}
                          placeholder="0"
                          min="0"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium mb-1">Min Likes</label>
                        <input
                          type="number"
                          value={tiktokMinLikes || ''}
                          onChange={(e) => setTiktokMinLikes(parseInt(e.target.value) || 0)}
                          className="input"
                          style={{ minWidth: '100px' }}
                          placeholder="0"
                          min="0"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium mb-1">Min Comments</label>
                        <input
                          type="number"
                          value={tiktokMinComments || ''}
                          onChange={(e) => setTiktokMinComments(parseInt(e.target.value) || 0)}
                          className="input"
                          style={{ minWidth: '100px' }}
                          placeholder="0"
                          min="0"
                        />
                      </div>
                    </div>
                    
                    {/* Bulk Actions */}
                    <div className="flex gap-2 items-end">
                      <button
                        onClick={toggleSelectAllPosts}
                        className="btn btn-secondary"
                        style={{ padding: '8px 12px', fontSize: '12px' }}
                      >
                        {(() => {
                          const filteredPosts = tiktokSearchType === 'full-hashtag'
                            ? getTopPostsPerCreator(tiktokSearchResult.data)
                            : sortAndFilterTikTokResults(tiktokSearchResult.data);
                          // Filter out posts from creators that are already added or exist in database
                          const selectablePosts = filteredPosts.filter((post: any) => {
                            const author = post.authorMeta || post.author || {};
                            const authorId = author.id || author.sec_uid || author.uid || author.name || author.unique_id;
                            const isAdded = addedCreators.has(authorId);
                            const isExisting = existingCreators.has((author.name || author.unique_id)?.toLowerCase());
                            return !isAdded && !isExisting;
                          });
                          const allPostIds = selectablePosts.map((post: any) => {
                            const author = post.authorMeta || post.author || {};
                            return post.id || `${author.name || author.unique_id}-${post.createTime || post.create_time}`;
                          });
                          const allSelected = allPostIds.length > 0 && allPostIds.every((id: any) => selectedPosts.has(id));
                          return allSelected ? 'Deselect All' : 'Select All New';
                        })()}
                      </button>
                      
                    </div>


                    {/* Reset Filters */}
                    {(tiktokMinViews !== 5000 || tiktokMinLikes > 0 || tiktokMinComments > 0 || tiktokSortBy !== 'views') && (
                      <button
                        onClick={() => {
                          setTiktokSortBy('views');
                          setTiktokMinViews(5000);
                          setTiktokMinLikes(0);
                          setTiktokMinComments(0);
                        }}
                        className="btn btn-secondary"
                        style={{ padding: '8px 16px' }}
                      >
                        Reset Filters
                      </button>
                    )}
                  </div>
                </div>
              )}

              {tiktokSearching && (
                <div className="text-center py-8">
                  <div className="loading" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '12px' }}>
                    <div style={{
                      width: '24px',
                      height: '24px',
                      border: '3px solid #333',
                      borderTop: '3px solid #4dabf7',
                      borderRadius: '50%',
                      animation: 'spin 1s linear infinite'
                    }}></div>
                    <span>Searching TikTok creators...</span>
                  </div>
                </div>
              )}

              {!tiktokSearching && tiktokSearchResult && !tiktokSearchResult.loading && (
                <div style={{ 
                  padding: '20px',
                  borderRadius: '8px',
                  overflow: 'auto'
                }}>
                  {tiktokSearchResult.error ? (
                    <div style={{ color: '#ff6b6b', padding: '40px', textAlign: 'center', fontSize: '18px' }}>
                      ❌ {tiktokSearchResult.error}
                    </div>
                  ) : (
                    <div>
                      {Array.isArray(tiktokSearchResult.data) && tiktokSearchResult.data.length > 0 ? (
                        <div style={{ 
                          display: 'grid',
                          gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))',
                          gap: '20px'
                        }}>
                          {(tiktokSearchType === 'full-hashtag'
                            ? getTopPostsPerCreator(tiktokSearchResult.data)
                            : sortAndFilterTikTokResults(tiktokSearchResult.data)).map((post: any, index: number) => {
                        // APIFY 데이터 구조 사용
                        const stats = post.statistics || post.stats || {};
                        const author = post.authorMeta || post.author || {};
                        const video = post.videoMeta || post.video || {};
                        const thumbnail = video.coverUrl || video.cover || '';
                        const postUrl = post.webVideoUrl || post.share_url || `https://www.tiktok.com/@${author.name || author.unique_id}/video/${post.id}`;
                        const authorId = author.id || author.sec_uid || author.uid || author.name || author.unique_id;
                        const isAdding = addingCreators.has(authorId);
                        const isAdded = addedCreators.has(authorId);
                        const isExisting = existingCreators.has((author.name || author.unique_id)?.toLowerCase());
                        const isSelected = selectedPosts.has(postUrl);
                        
                        return (
                          <div key={index} style={{ 
                            backgroundColor: isExisting ? '#1a2a1a' : '#2a2a2a',
                            borderRadius: '12px',
                            overflow: 'hidden',
                            transition: 'transform 0.2s, box-shadow 0.2s',
                            border: isSelected ? '2px solid #4dabf7' : isExisting ? '2px solid #51cf66' : '1px solid #333',
                            position: 'relative'
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.transform = 'translateY(-4px)';
                            e.currentTarget.style.boxShadow = '0 8px 16px rgba(0,0,0,0.4)';
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.transform = 'translateY(0)';
                            e.currentTarget.style.boxShadow = 'none';
                          }}
                          >
                            {/* Selection Checkbox */}
                            <div style={{
                              position: 'absolute',
                              top: '8px',
                              left: '8px',
                              zIndex: 2,
                              backgroundColor: 'rgba(0, 0, 0, 0.7)',
                              borderRadius: '4px',
                              padding: '4px'
                            }}>
                              <input
                                type="checkbox"
                                checked={isSelected}
                                onChange={(e) => {
                                  e.stopPropagation();
                                  togglePostSelection(postUrl);
                                }}
                                style={{
                                  width: '16px',
                                  height: '16px',
                                  cursor: 'pointer'
                                }}
                              />
                            </div>
                            
                            <a href={postUrl} target="_blank" rel="noopener noreferrer" 
                               style={{ textDecoration: 'none', color: 'inherit' }}>
                              {thumbnail ? (
                                <div style={{
                                  width: '100%',
                                  paddingBottom: '177.78%',
                                  position: 'relative',
                                  backgroundColor: '#000'
                                }}>
                                  <img 
                                    src={thumbnail} 
                                    alt="TikTok Thumbnail" 
                                    style={{ 
                                      position: 'absolute',
                                      top: 0,
                                      left: 0,
                                      width: '100%',
                                      height: '100%',
                                      objectFit: 'cover'
                                    }} 
                                    onError={(e) => {
                                      e.currentTarget.style.display = 'none';
                                      e.currentTarget.parentElement!.style.backgroundColor = '#333';
                                    }}
                                  />
                                  <div style={{
                                    position: 'absolute',
                                    bottom: '8px',
                                    left: '8px',
                                    right: '8px',
                                    backgroundColor: 'rgba(0, 0, 0, 0.8)',
                                    color: '#fff',
                                    padding: '4px 8px',
                                    borderRadius: '4px',
                                    fontSize: '10px',
                                    display: 'flex',
                                    justifyContent: 'space-around',
                                    alignItems: 'center',
                                    gap: '2px'
                                  }}>
                                    <span style={{ display: 'flex', alignItems: 'center', gap: '2px' }}>
                                      👁️ {((post.playCount || stats.play_count || 0)) >= 1000000 
                                        ? `${((post.playCount || stats.play_count) / 1000000).toFixed(1)}M` 
                                        : ((post.playCount || stats.play_count || 0)) >= 1000 
                                        ? `${((post.playCount || stats.play_count) / 1000).toFixed(1)}K`
                                        : (post.playCount || stats.play_count || 0)}
                                    </span>
                                    <span style={{ display: 'flex', alignItems: 'center', gap: '2px' }}>
                                      ❤️ {((post.diggCount || stats.digg_count || 0)) >= 1000000 
                                        ? `${((post.diggCount || stats.digg_count) / 1000000).toFixed(1)}M` 
                                        : ((post.diggCount || stats.digg_count || 0)) >= 1000 
                                        ? `${((post.diggCount || stats.digg_count) / 1000).toFixed(1)}K`
                                        : (post.diggCount || stats.digg_count || 0)}
                                    </span>
                                    <span style={{ display: 'flex', alignItems: 'center', gap: '2px' }}>
                                      💬 {((post.commentCount || stats.comment_count || 0)) >= 1000 
                                        ? `${((post.commentCount || stats.comment_count) / 1000).toFixed(1)}K`
                                        : (post.commentCount || stats.comment_count || 0)}
                                    </span>
                                    <span style={{ display: 'flex', alignItems: 'center', gap: '2px' }}>
                                      🔄 {((post.shareCount || stats.share_count || 0)) >= 1000 
                                        ? `${((post.shareCount || stats.share_count) / 1000).toFixed(1)}K`
                                        : (post.shareCount || stats.share_count || 0)}
                                    </span>
                                  </div>
                                </div>
                              ) : (
                                <div style={{
                                  width: '100%',
                                  paddingBottom: '177.78%',
                                  backgroundColor: '#333',
                                  position: 'relative'
                                }}>
                                  <span style={{ 
                                    position: 'absolute',
                                    top: '50%',
                                    left: '50%',
                                    transform: 'translate(-50%, -50%)',
                                    color: '#666'
                                  }}>No Thumbnail</span>
                                </div>
                              )}
                              
                              <div style={{ padding: '12px' }}>
                                <div style={{ 
                                  fontSize: '13px',
                                  fontWeight: 'bold',
                                  color: '#fff',
                                  marginBottom: '4px',
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                  whiteSpace: 'nowrap'
                                }}>
                                  @{author.name || author.unique_id || 'unknown'}
                                </div>
                                <div style={{ 
                                  fontSize: '12px',
                                  color: '#aaa',
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                  whiteSpace: 'nowrap'
                                }}>
                                  {author.nickName || author.nickname || 'Unknown Creator'}
                                </div>
                                
                                {/* Email Status Display */}
                                <div style={{
                                  fontSize: '11px',
                                  marginTop: '4px',
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: '4px'
                                }}>
                                  {author.email ? (
                                    <span style={{ color: '#51cf66' }}>
                                      📧 {author.email.substring(0, 15)}...
                                    </span>
                                  ) : (author.signature && author.signature.includes('@')) ? (
                                    <span style={{ color: '#ffa94d' }}>
                                      📧 Email in bio (not extracted)
                                    </span>
                                  ) : (
                                    <span style={{ color: '#868e96' }}>
                                      ✗ No email found
                                    </span>
                                  )}
                                </div>
                                <div style={{ 
                                  fontSize: '11px',
                                  color: '#888',
                                  marginTop: '8px',
                                  marginBottom: '8px',
                                  overflow: 'hidden',
                                  display: '-webkit-box',
                                  WebkitLineClamp: 2,
                                  WebkitBoxOrient: 'vertical',
                                  lineHeight: '1.4'
                                }}>
                                  {post.text || post.desc || post.itemInfos?.text || video.text || 'No description'}
                                </div>
                                
                                
                                {/* Email Display in Card Body */}
                                {author.email && (
                                  <div style={{
                                    backgroundColor: '#00ff00',
                                    color: '#000',
                                    borderRadius: '4px',
                                    padding: '6px 8px',
                                    fontSize: '11px',
                                    fontWeight: 'bold',
                                    marginTop: '4px',
                                    marginBottom: '8px',
                                    textAlign: 'center',
                                    boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap'
                                  }}
                                  title={author.email}>
                                    📧 {author.email}
                                  </div>
                                )}
                                <div style={{ display: 'flex', gap: '8px', marginBottom: '8px' }}>
                                  <button
                                    onClick={(e) => {
                                      e.preventDefault();
                                      e.stopPropagation();
                                      fetchTranscript(postUrl);
                                    }}
                                    style={{
                                      flex: 1,
                                      padding: '6px 12px',
                                      backgroundColor: '#667eea',
                                      color: 'white',
                                      border: 'none',
                                      borderRadius: '6px',
                                      fontSize: '11px',
                                      fontWeight: 'bold',
                                      cursor: 'pointer',
                                      transition: 'background-color 0.2s'
                                    }}
                                    onMouseEnter={(e) => {
                                      e.currentTarget.style.backgroundColor = '#764ba2';
                                    }}
                                    onMouseLeave={(e) => {
                                      e.currentTarget.style.backgroundColor = '#667eea';
                                    }}
                                  >
                                    📝 Transcript
                                  </button>
                                </div>
                                <button
                                  onClick={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    addTikTokCreator(post);
                                  }}
                                  disabled={isAdding || isAdded || isExisting}
                                  style={{
                                    width: '100%',
                                    padding: '6px 12px',
                                    backgroundColor: isExisting ? '#51cf66' : isAdded ? '#868e96' : isAdding ? '#666' : '#ff6b35',
                                    color: 'white',
                                    border: 'none',
                                    borderRadius: '6px',
                                    fontSize: '11px',
                                    fontWeight: 'bold',
                                    cursor: (isAdding || isAdded || isExisting) ? 'not-allowed' : 'pointer',
                                    transition: 'background-color 0.2s',
                                    opacity: isExisting ? 0.8 : isAdded ? 0.6 : isAdding ? 0.7 : 1
                                  }}
                                  onMouseEnter={(e) => {
                                    if (!isAdding && !isAdded && !isExisting) {
                                      e.currentTarget.style.backgroundColor = '#e55a2b';
                                    }
                                  }}
                                  onMouseLeave={(e) => {
                                    if (!isAdding && !isAdded && !isExisting) {
                                      e.currentTarget.style.backgroundColor = '#ff6b35';
                                    }
                                  }}
                                >
                                  {isExisting ? '✓ Already in Database' : isAdded ? '✓ Already Added' : isAdding ? '⏳ Adding...' : '➕ Add Creator'}
                                </button>
                              </div>
                            </a>
                          </div>
                        );
                        })}
                        </div>
                      ) : Array.isArray(tiktokSearchResult.data) && tiktokSearchResult.data.length > 0 ? (
                        <div style={{ textAlign: 'center', padding: '40px', color: '#888' }}>
                          <div style={{ fontSize: '2rem', marginBottom: '20px' }}>🚫</div>
                          <p>No posts match your filter criteria.</p>
                          <p style={{ fontSize: '14px', color: '#666', marginTop: '10px' }}>
                            Try adjusting your filters or reset them to see all results.
                          </p>
                          <button
                            onClick={() => {
                              setTiktokSortBy('views');
                              setTiktokMinViews(1000);
                              setTiktokMinLikes(0);
                              setTiktokMinComments(0);
                            }}
                            className="btn btn-primary"
                            style={{ marginTop: '16px', padding: '8px 16px' }}
                          >
                            Reset Filters
                          </button>
                        </div>
                      ) : (
                        <div style={{ textAlign: 'center', padding: '40px', color: '#888' }}>
                          <div style={{ fontSize: '2rem', marginBottom: '20px' }}>📱</div>
                          <p>No TikTok posts found for this search.</p>
                          {tiktokSearchResult.data && !Array.isArray(tiktokSearchResult.data) && (
                            <p style={{ fontSize: '12px', color: '#666', marginTop: '10px' }}>
                              Data structure error: expected array but got {typeof tiktokSearchResult.data}
                            </p>
                          )}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>,
        document.body
      )}
      
      {/* Instagram Search Modal - Now using separate component */}
      <InstagramCreatorsSearchModal
        showInstagramModal={showInstagramModal}
        setShowInstagramModal={setShowInstagramModal}
        instagramSearchResult={instagramSearchResult}
        setInstagramSearchResult={setInstagramSearchResult}
        searchLogs={searchLogs}
        clearSearchLogs={clearSearchLogs}
        showSearchLogs={showSearchLogs}
        setShowSearchLogs={setShowSearchLogs}
        instagramSearchKeyword={instagramSearchKeyword}
        setInstagramSearchKeyword={setInstagramSearchKeyword}
        instagramMinViews={instagramMinViews}
        setInstagramMinViews={setInstagramMinViews}
        instagramMaxItems={instagramMaxItems}
        setInstagramMaxItems={setInstagramMaxItems}
        instagramSearching={instagramSearching}
        searchInstagramCreators={searchInstagramCreators}
        instagramAddingCreators={instagramAddingCreators}
        instagramAddedCreators={instagramAddedCreators}
        instagramExistingCreators={instagramExistingCreators}
        addInstagramCreator={addInstagramCreator}
        instagramEmailStatus={instagramEmailStatus}
        fetchInstagramEmail={fetchInstagramEmail}
      />

      {/* YouTube Search Modal */}
      <YouTubeSearchModal
        showYouTubeModal={showYouTubeModal}
        setShowYouTubeModal={setShowYouTubeModal}
        youtubeSearchResult={youtubeSearchResult}
        setYoutubeSearchResult={setYoutubeSearchResult}
        searchLogs={searchLogs}
        clearSearchLogs={clearSearchLogs}
        addSearchLog={addSearchLog}
        showSearchLogs={showSearchLogs}
        setShowSearchLogs={setShowSearchLogs}
        youtubeSearchKeyword={youtubeSearchKeyword}
        setYoutubeSearchKeyword={setYoutubeSearchKeyword}
        youtubeAddingCreators={youtubeAddingCreators}
        youtubeAddedCreators={youtubeAddedCreators}
        youtubeExistingCreators={youtubeExistingCreators}
        setYoutubeExistingCreators={setYoutubeExistingCreators}
        addYouTubeCreator={addYouTubeCreator}
        selectedYouTubeVideos={selectedYouTubeVideos}
        toggleSelectAllYouTubeVideos={toggleSelectAllYouTubeVideos}
        toggleYouTubeVideoSelection={toggleYouTubeVideoSelection}
        addSelectedYouTubeVideos={addSelectedYouTubeVideos}
        bulkAddingYouTubeVideos={bulkAddingYouTubeVideos}
      />

      {/* Transcript Modal */}
      {showTranscriptModal && createPortal(
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.8)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 10001,
            padding: '20px'
          }}
          onClick={() => {
            setShowTranscriptModal(false);
            setTranscriptData(null);
            setTranscriptError(null);
          }}
        >
          <div
            style={{
              backgroundColor: '#2a2a2a',
              borderRadius: '12px',
              maxWidth: '600px',
              maxHeight: '80vh',
              width: '100%',
              display: 'flex',
              flexDirection: 'column',
              boxShadow: '0 20px 40px rgba(0, 0, 0, 0.5)'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Modal Header */}
            <div
              style={{
                padding: '20px',
                borderBottom: '1px solid #444',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center'
              }}
            >
              <h3 style={{ margin: 0, color: '#fff' }}>📝 Video Transcript</h3>
              <button
                onClick={() => {
                  setShowTranscriptModal(false);
                  setTranscriptData(null);
                  setTranscriptError(null);
                }}
                style={{
                  background: 'transparent',
                  border: 'none',
                  color: '#999',
                  fontSize: '24px',
                  cursor: 'pointer',
                  padding: '0',
                  width: '30px',
                  height: '30px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
              >
                ×
              </button>
            </div>
            
            {/* Modal Content */}
            <div
              style={{
                padding: '20px',
                overflowY: 'auto',
                flex: 1
              }}
            >
              {transcriptLoading && (
                <div style={{ textAlign: 'center', color: '#999' }}>
                  Loading transcript...
                </div>
              )}
              
              {transcriptError && (
                <div style={{ color: '#ff6b6b', textAlign: 'center' }}>
                  ❌ {transcriptError}
                </div>
              )}
              
              {transcriptData && !transcriptLoading && (
                <div>
                  {transcriptData.lang && (
                    <div
                      style={{
                        marginBottom: '10px',
                        color: '#888',
                        fontSize: '12px'
                      }}
                    >
                      Language: {transcriptData.lang}
                      {transcriptData.availableLangs && transcriptData.availableLangs.length > 1 && (
                        <span> (Available: {transcriptData.availableLangs.join(', ')})</span>
                      )}
                    </div>
                  )}
                  
                  <div
                    style={{
                      color: '#fff',
                      lineHeight: '1.6',
                      whiteSpace: 'pre-wrap',
                      wordBreak: 'break-word'
                    }}
                  >
                    {typeof transcriptData.content === 'string'
                      ? transcriptData.content
                      : Array.isArray(transcriptData.content)
                      ? transcriptData.content.map((segment: any, index: number) => (
                          <div key={index} style={{ marginBottom: '8px' }}>
                            {segment.text}
                          </div>
                        ))
                      : 'No transcript available'}
                  </div>
                </div>
              )}
            </div>
            
            {/* Modal Footer */}
            <div
              style={{
                padding: '15px 20px',
                borderTop: '1px solid #444',
                display: 'flex',
                justifyContent: 'flex-end',
                gap: '10px'
              }}
            >
              <a
                href={transcriptVideoUrl}
                target="_blank"
                rel="noopener noreferrer"
                style={{
                  padding: '8px 16px',
                  backgroundColor: '#444',
                  color: '#fff',
                  borderRadius: '6px',
                  textDecoration: 'none',
                  fontSize: '14px',
                  transition: 'background-color 0.2s'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = '#555';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = '#444';
                }}
              >
                View Video →
              </a>
              <button
                onClick={() => {
                  setShowTranscriptModal(false);
                  setTranscriptData(null);
                  setTranscriptError(null);
                }}
                style={{
                  padding: '8px 16px',
                  backgroundColor: '#667eea',
                  color: '#fff',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontSize: '14px',
                  transition: 'background-color 0.2s'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = '#764ba2';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = '#667eea';
                }}
              >
                Close
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}

      
      {/* Floating Add Selected Button for TikTok Modal */}
      {showTikTokModal && selectedPosts.size > 0 && createPortal(
        <div 
          style={{
            position: 'fixed',
            bottom: '24px',
            left: '50%',
            transform: 'translateX(-50%)',
            background: 'var(--bg-primary)',
            border: '1px solid var(--border-color)',
            borderRadius: 'var(--radius-xl)',
            padding: 'var(--space-4) var(--space-6)',
            boxShadow: '0 10px 25px rgba(0, 0, 0, 0.15)',
            zIndex: 20001, // Higher than modal
            display: 'flex',
            alignItems: 'center',
            gap: 'var(--space-4)',
            backdropFilter: 'blur(12px)',
            minWidth: '280px'
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)' }}>
            <span style={{ 
              fontSize: '14px', 
              fontWeight: '500',
              color: 'var(--text-primary)'
            }}>
              {selectedPosts.size} selected
            </span>
          </div>
          
          <button
            onClick={addSelectedPosts}
            style={{
              background: 'linear-gradient(135deg, #ff6b35 0%, #ff4528 100%)',
              border: 'none',
              borderRadius: 'var(--radius-lg)',
              padding: 'var(--space-2) var(--space-4)',
              color: 'white',
              fontSize: '14px',
              fontWeight: '500',
              cursor: bulkAddingPosts ? 'not-allowed' : 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: 'var(--space-2)',
              transition: 'all 0.2s ease',
              boxShadow: '0 2px 8px rgba(255, 107, 53, 0.3)',
              opacity: bulkAddingPosts ? 0.7 : 1
            }}
            disabled={bulkAddingPosts}
            onMouseOver={(e) => {
              if (!bulkAddingPosts) {
                e.currentTarget.style.transform = 'translateY(-1px)';
                e.currentTarget.style.boxShadow = '0 4px 12px rgba(255, 107, 53, 0.4)';
              }
            }}
            onMouseOut={(e) => {
              e.currentTarget.style.transform = 'translateY(0)';
              e.currentTarget.style.boxShadow = '0 2px 8px rgba(255, 107, 53, 0.3)';
            }}
          >
            <span>➕</span>
            <span>{bulkAddingPosts ? 'Adding...' : `Add Selected Creators`}</span>
          </button>
          
          <button
            onClick={() => setSelectedPosts(new Set())}
            style={{
              background: 'var(--bg-secondary)',
              border: '1px solid var(--border-color)',
              borderRadius: 'var(--radius-md)',
              padding: 'var(--space-2)',
              color: 'var(--text-secondary)',
              fontSize: '12px',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: '28px',
              height: '28px',
              transition: 'all 0.2s ease'
            }}
            title="Clear selection"
            onMouseOver={(e) => {
              e.currentTarget.style.background = 'var(--bg-tertiary)';
              e.currentTarget.style.color = 'var(--text-primary)';
            }}
            onMouseOut={(e) => {
              e.currentTarget.style.background = 'var(--bg-secondary)';
              e.currentTarget.style.color = 'var(--text-secondary)';
            }}
          >
            ✕
          </button>
        </div>,
        document.body
      )}
      
      {/* Floating Selection UI for Keywords */}
      {selectedKeywords.length > 0 && createPortal(
        <div 
          style={{
            position: 'fixed',
            bottom: '24px',
            left: '50%',
            transform: 'translateX(-50%)',
            background: 'var(--bg-primary)',
            border: '1px solid var(--border-color)',
            borderRadius: 'var(--radius-xl)',
            padding: 'var(--space-4) var(--space-6)',
            boxShadow: '0 10px 25px rgba(0, 0, 0, 0.15)',
            zIndex: 10000,
            display: 'flex',
            alignItems: 'center',
            gap: 'var(--space-4)',
            backdropFilter: 'blur(12px)',
            minWidth: '280px'
          }}
          className="floating-selection-bar"
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)' }}>
            <span style={{ 
              fontSize: '14px', 
              fontWeight: '500',
              color: 'var(--text-primary)'
            }}>
              {selectedKeywords.length} selected
            </span>
          </div>
          
          <button
            onClick={() => setShowGroupEditModal(true)}
            style={{
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              border: 'none',
              borderRadius: 'var(--radius-lg)',
              padding: 'var(--space-2) var(--space-4)',
              color: 'white',
              fontSize: '14px',
              fontWeight: '500',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: 'var(--space-2)',
              transition: 'all 0.2s ease',
              boxShadow: '0 2px 8px rgba(102, 126, 234, 0.3)'
            }}
            onMouseOver={(e) => {
              e.currentTarget.style.transform = 'translateY(-1px)';
              e.currentTarget.style.boxShadow = '0 4px 12px rgba(102, 126, 234, 0.4)';
            }}
            onMouseOut={(e) => {
              e.currentTarget.style.transform = 'translateY(0)';
              e.currentTarget.style.boxShadow = '0 2px 8px rgba(102, 126, 234, 0.3)';
            }}
          >
            <span>✏️</span>
            <span>Edit Group</span>
          </button>
          
          <button
            onClick={deleteSelectedKeywords}
            style={{
              background: 'linear-gradient(135deg, #ff6b6b 0%, #ee5a52 100%)',
              border: 'none',
              borderRadius: 'var(--radius-lg)',
              padding: 'var(--space-2) var(--space-4)',
              color: 'white',
              fontSize: '14px',
              fontWeight: '500',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: 'var(--space-2)',
              transition: 'all 0.2s ease',
              boxShadow: '0 2px 8px rgba(255, 107, 107, 0.3)'
            }}
            onMouseOver={(e) => {
              e.currentTarget.style.transform = 'translateY(-1px)';
              e.currentTarget.style.boxShadow = '0 4px 12px rgba(255, 107, 107, 0.4)';
            }}
            onMouseOut={(e) => {
              e.currentTarget.style.transform = 'translateY(0)';
              e.currentTarget.style.boxShadow = '0 2px 8px rgba(255, 107, 107, 0.3)';
            }}
          >
            <span>🗑️</span>
            <span>Delete Selected</span>
          </button>
          
          <button
            onClick={() => {
              setSelectedKeywords([]);
            }}
            style={{
              background: 'var(--bg-secondary)',
              border: '1px solid var(--border-color)',
              borderRadius: 'var(--radius-md)',
              padding: 'var(--space-2)',
              color: 'var(--text-secondary)',
              fontSize: '12px',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: '28px',
              height: '28px',
              transition: 'all 0.2s ease'
            }}
            title="Clear selection"
            onMouseOver={(e) => {
              e.currentTarget.style.background = 'var(--bg-tertiary)';
              e.currentTarget.style.color = 'var(--text-primary)';
            }}
            onMouseOut={(e) => {
              e.currentTarget.style.background = 'var(--bg-secondary)';
              e.currentTarget.style.color = 'var(--text-secondary)';
            }}
          >
            ✕
          </button>
        </div>,
        document.body
      )}
      
      {/* Group Edit Modal */}
      {showGroupEditModal && createPortal(
        <div 
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 10001
          }}
          onClick={() => setShowGroupEditModal(false)}
        >
          <div 
            style={{
              background: 'var(--bg-primary)',
              borderRadius: 'var(--radius-xl)',
              padding: 'var(--space-6)',
              width: '90%',
              maxWidth: '400px',
              boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)',
              border: '1px solid var(--border-color)'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <h3 style={{
              fontSize: '18px',
              fontWeight: '600',
              color: 'var(--text-primary)',
              marginBottom: 'var(--space-4)'
            }}>
              Change Group for {selectedKeywords.length} Keyword{selectedKeywords.length > 1 ? 's' : ''}
            </h3>
            
            <div style={{ marginBottom: 'var(--space-4)' }}>
              <label style={{
                display: 'block',
                fontSize: '14px',
                color: 'var(--text-secondary)',
                marginBottom: 'var(--space-2)'
              }}>
                Select New Group
              </label>
              <select
                value={selectedNewGroup}
                onChange={async (e) => {
                  if (e.target.value === '__create_new__') {
                    const newGroupName = prompt('Enter new group name:');
                    if (newGroupName && newGroupName.trim()) {
                      const trimmedName = newGroupName.trim().toLowerCase();
                      
                      // Create the new group in the database
                      try {
                        const createResponse = await fetch(`${API_URL}/api/groups`, {
                          method: 'POST',
                          headers: {
                            'Content-Type': 'application/json',
                            ...(token && { 'Authorization': `Bearer ${token}` })
                          },
                          body: JSON.stringify({
                            name: trimmedName
                          }),
                        });

                        if (!createResponse.ok) {
                          const error = await createResponse.json();
                          if (error.error === 'Group already exists') {
                            // Group exists, just use it
                          } else {
                            alert('Failed to create group: ' + error.error);
                            return;
                          }
                        }

                        // Add to local availableGroups if not already there
                        if (!availableGroups.includes(trimmedName)) {
                          setAvailableGroups([...availableGroups, trimmedName]);
                        }
                        setSelectedNewGroup(trimmedName);
                      } catch (error) {
                        console.error('Error creating group:', error);
                        alert('Failed to create group');
                        return;
                      }
                    }
                  } else {
                    setSelectedNewGroup(e.target.value);
                  }
                }}
                style={{
                  width: '100%',
                  padding: 'var(--space-3)',
                  borderRadius: 'var(--radius-lg)',
                  border: '1px solid var(--border-color)',
                  background: 'var(--bg-secondary)',
                  color: 'var(--text-primary)',
                  fontSize: '14px',
                  cursor: 'pointer'
                }}
              >
                {availableGroups.map(group => (
                  <option key={group} value={group}>{group}</option>
                ))}
                <option value="__create_new__" style={{ fontStyle: 'italic', color: '#667eea' }}>
                  + Create new group...
                </option>
              </select>
            </div>
            
            <div style={{
              display: 'flex',
              gap: 'var(--space-3)',
              justifyContent: 'flex-end'
            }}>
              <button
                onClick={() => setShowGroupEditModal(false)}
                style={{
                  padding: 'var(--space-2) var(--space-4)',
                  borderRadius: 'var(--radius-lg)',
                  border: '1px solid var(--border-color)',
                  background: 'var(--bg-secondary)',
                  color: 'var(--text-primary)',
                  fontSize: '14px',
                  fontWeight: '500',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease'
                }}
                onMouseOver={(e) => {
                  e.currentTarget.style.background = 'var(--bg-tertiary)';
                }}
                onMouseOut={(e) => {
                  e.currentTarget.style.background = 'var(--bg-secondary)';
                }}
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  updateSelectedKeywordsGroup(selectedNewGroup); // Modal closes inside the function
                }}
                style={{
                  padding: 'var(--space-2) var(--space-4)',
                  borderRadius: 'var(--radius-lg)',
                  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                  color: 'white',
                  border: 'none',
                  fontSize: '14px',
                  fontWeight: '500',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease',
                  boxShadow: '0 2px 8px rgba(102, 126, 234, 0.3)'
                }}
                onMouseOver={(e) => {
                  e.currentTarget.style.transform = 'translateY(-1px)';
                  e.currentTarget.style.boxShadow = '0 4px 12px rgba(102, 126, 234, 0.4)';
                }}
                onMouseOut={(e) => {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = '0 2px 8px rgba(102, 126, 234, 0.3)';
                }}
              >
                Update Group
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* Relevance tab content */}
      {mainTab === 'relevance' && (
        <div>
          {/* Simple header */}
          <div className="flex justify-between items-center gap-4" style={{ marginBottom: 'var(--space-6)' }}>
            <div>
              <h2 style={{ fontSize: '1.5rem', fontWeight: 'bold', marginBottom: '0.5rem' }}>
                Relevant Topics
              </h2>
              <p style={{ color: '#666', fontSize: '0.9rem' }}>
                Manage simple topic words for AI-powered relevance checking
              </p>
            </div>
          </div>

          {/* Topic Words Management */}
          <div className="card" style={{ padding: '20px', backgroundColor: '#1a1a1a', border: '1px solid #333' }}>
            <div style={{ marginBottom: '20px' }}>
              <div style={{ display: 'flex', gap: '10px', marginBottom: '10px' }}>
                <input
                  type="text"
                  value={newTopic}
                  onChange={(e) => setNewTopic(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && addTopic()}
                  placeholder="Enter a topic word (e.g., ai, marketing, tools)"
                  className="form-input"
                  style={{ flex: 1 }}
                />
                <button
                  onClick={addTopic}
                  className="btn btn-primary"
                  disabled={!newTopic.trim()}
                >
                  Add Topic
                </button>
              </div>
            </div>

            {topicsLoading ? (
              <div className="text-center py-4">
                <div className="loading">Loading topics...</div>
              </div>
            ) : topics.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '40px', color: '#999' }}>
                No topics yet. Add your first topic above.
              </div>
            ) : (
              <>
                <div style={{ marginBottom: '15px', color: '#ccc', fontSize: '14px' }}>
                  <strong>{topics.length}</strong> topic words configured for AI relevance analysis
                </div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px' }}>
                  {topics.map((word) => (
                    <div
                      key={word}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                        padding: '8px 16px',
                        backgroundColor: '#2a2a2a',
                        border: '1px solid #444',
                        borderRadius: '20px'
                      }}
                    >
                      <span style={{ color: '#fff' }}>{word}</span>
                      <button
                        onClick={() => deleteTopic(word)}
                        style={{
                          background: 'none',
                          border: 'none',
                          color: '#ff6b6b',
                          cursor: 'pointer',
                          padding: '0',
                          fontSize: '18px',
                          lineHeight: '1'
                        }}
                        title={`Delete ${word}`}
                      >
                        ×
                      </button>
                    </div>
                  ))}
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* Group Delete Confirmation Modal */}
      {showDeleteGroupModal && groupToDelete && (
        <div className="modal">
          <div className="modal-content" style={{ maxWidth: '500px' }}>
            <div className="modal-header">
              <h2>Delete Group</h2>
              <button
                onClick={() => {
                  setShowDeleteGroupModal(false);
                  setGroupToDelete(null);
                  setSelectedTargetGroup('');
                }}
                className="close-button"
              >
                ✕
              </button>
            </div>
            <div className="modal-body">
              <p style={{ marginBottom: '20px' }}>
                Are you sure you want to delete the group <strong>"{groupToDelete.name}"</strong>?
              </p>
              <p style={{ marginBottom: '20px', color: '#ff6b6b' }}>
                All keywords in this group will be reassigned to the selected group below:
              </p>
              
              <div className="form-group">
                <label>Reassign keywords to:</label>
                <select
                  value={selectedTargetGroup}
                  onChange={(e) => setSelectedTargetGroup(e.target.value)}
                  className="form-input"
                  style={{ width: '100%' }}
                >
                  {groups
                    .filter(g => g._id !== groupToDelete.id)
                    .map(group => (
                      <option key={group._id} value={group._id}>
                        {group.name} ({group.count} keywords)
                      </option>
                    ))
                  }
                </select>
              </div>
            </div>
            <div className="modal-footer">
              <button
                onClick={() => {
                  setShowDeleteGroupModal(false);
                  setGroupToDelete(null);
                  setSelectedTargetGroup('');
                }}
                className="btn btn-secondary"
              >
                Cancel
              </button>
              <button
                onClick={confirmDeleteGroup}
                className="btn btn-danger"
                disabled={!selectedTargetGroup}
              >
                Delete Group
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Relevancy Details Modal */}
      {showRelevancyModal && selectedRelevancyData && createPortal(
        <div className="modal-overlay" onClick={() => setShowRelevancyModal(false)}>
          <div className="modal-content" style={{ maxWidth: '600px' }} onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2 className="text-xl font-semibold">
                AI Relevancy Analysis - @{selectedRelevancyData.username}
              </h2>
              <button onClick={() => setShowRelevancyModal(false)}>✕</button>
            </div>
            
            <div className="modal-body space-y-4">
              {/* Overall Status */}
              <div style={{
                backgroundColor: selectedRelevancyData.isRelevant ? '#4CAF50' : '#666',
                color: '#fff',
                borderRadius: '8px',
                padding: '12px',
                textAlign: 'center',
                fontSize: '16px',
                fontWeight: 'bold'
              }}>
                {selectedRelevancyData.isRelevant ? '✅ RELEVANT' : '❌ NOT RELEVANT'}
                {selectedRelevancyData.score !== undefined && (
                  <span style={{ marginLeft: '8px', opacity: 0.9 }}>
                    ({Math.round(selectedRelevancyData.score * 100)}% confidence)
                  </span>
                )}
              </div>

              {/* AI Reasoning */}
              <div>
                <h3 className="font-semibold mb-2">AI Analysis Reasoning:</h3>
                <div style={{
                  backgroundColor: '#f5f5f5',
                  borderRadius: '6px',
                  padding: '12px',
                  fontSize: '14px',
                  lineHeight: '1.6',
                  color: '#333'
                }}>
                  {selectedRelevancyData.reason || 'No detailed reasoning available.'}
                </div>
              </div>

              {/* Relevant Topics */}
              {selectedRelevancyData.topics && selectedRelevancyData.topics.length > 0 && (
                <div>
                  <h3 className="font-semibold mb-2">Relevant Topics Found:</h3>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                    {selectedRelevancyData.topics.map((topic: string, index: number) => (
                      <span
                        key={index}
                        style={{
                          backgroundColor: '#e3f2fd',
                          color: '#1976d2',
                          padding: '4px 12px',
                          borderRadius: '16px',
                          fontSize: '13px',
                          fontWeight: '500'
                        }}
                      >
                        {topic}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {/* Relevant Keywords */}
              {selectedRelevancyData.keywords && Object.keys(selectedRelevancyData.keywords).length > 0 && (
                <div>
                  <h3 className="font-semibold mb-2">Keywords by Category:</h3>
                  <div style={{
                    backgroundColor: '#f9f9f9',
                    borderRadius: '6px',
                    padding: '12px',
                    fontSize: '13px'
                  }}>
                    {Object.entries(selectedRelevancyData.keywords).map(([category, words]: [string, any]) => (
                      <div key={category} style={{ marginBottom: '8px' }}>
                        <strong style={{ color: '#555' }}>{category}:</strong>
                        <span style={{ marginLeft: '8px', color: '#777' }}>
                          {Array.isArray(words) ? words.join(', ') : words}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Close Button */}
              <button
                className="btn btn-primary w-full"
                onClick={() => setShowRelevancyModal(false)}
              >
                Close
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* Automation tab content */}
      {mainTab === 'automation' && (
        <div style={{ padding: 'var(--space-6)' }}>
          {/* Platform buttons and status */}
          <div style={{ marginBottom: 'var(--space-4)' }}>
            <div style={{ display: 'flex', gap: '1rem', alignItems: 'center' }}>
              {!isAutomationRunning ? (
                <button
                  className="btn btn-primary"
                  onClick={() => {
                    setShowTikTokAutomationModal(true);
                    // Initialize selected keywords with all keywords by default
                    setSelectedAutomationKeywords(new Set(tiktokAutomationKeywords.map(k => k._id)));
                  }}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    padding: '12px 24px',
                    fontSize: '16px',
                    background: 'linear-gradient(135deg, #000000 0%, #333333 100%)',
                    cursor: 'pointer'
                  }}
                >
                  <span style={{ fontSize: '20px' }}>📱</span>
                  <span>TikTok</span>
                </button>
              ) : (
                <button
                  className="btn btn-danger"
                  onClick={stopTikTokAutomation}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    padding: '12px 24px',
                    fontSize: '16px',
                    minWidth: '200px'
                  }}
                >
                  <span style={{ fontSize: '20px' }}>⏹️</span>
                  <span>Stop</span>
                </button>
              )}

              <button
                className="btn btn-primary"
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  padding: '12px 24px',
                  fontSize: '16px',
                  background: 'linear-gradient(135deg, #E1306C 0%, #C13584 50%, #833AB4 100%)'
                }}
              >
                <span style={{ fontSize: '20px' }}>📷</span>
                <span>Instagram</span>
              </button>
            </div>
          </div>

          {/* Loading indicator */}
          {loadingSearchHistory && (
            <div style={{ textAlign: 'center', padding: 'var(--space-4)' }}>
              <span>Loading search history...</span>
            </div>
          )}

          {/* Automation Results Table */}
          {!loadingSearchHistory && (isAutomationRunning || automationSearchResults.length > 0) && (
            <div>
              <h3 style={{ marginBottom: 'var(--space-3)' }}>
                Automation History
                {automationSearchResults.length > 0 && (
                  <span style={{ fontSize: '14px', color: 'var(--text-secondary)', marginLeft: 'var(--space-2)' }}>
                    ({automationSearchResults.length} results)
                  </span>
                )}
              </h3>

              {/* Progress indicator */}
              {isAutomationRunning && (
                <div style={{
                  marginBottom: 'var(--space-4)',
                  padding: 'var(--space-3)',
                  backgroundColor: 'var(--bg-secondary)',
                  borderRadius: '8px',
                  border: '1px solid var(--border-color)'
                }}>
                  <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    marginBottom: 'var(--space-2)'
                  }}>
                    <div>
                      <span style={{
                        fontSize: '14px',
                        fontWeight: '600',
                        color: 'var(--text-primary)'
                      }}>
                        Processing Keywords: {currentAutomationIndex + 1} / {totalAutomationKeywords}
                      </span>
                      {currentAutomationKeyword && (
                        <div style={{
                          fontSize: '12px',
                          color: 'var(--text-secondary)',
                          marginTop: '4px'
                        }}>
                          Current: <span style={{ fontWeight: '500', color: 'var(--primary)' }}>{currentAutomationKeyword}</span>
                        </div>
                      )}
                    </div>
                    <span style={{
                      fontSize: '16px',
                      fontWeight: '600',
                      color: 'var(--primary)'
                    }}>
                      {totalAutomationKeywords > 0
                        ? Math.round(((currentAutomationIndex + 1) / totalAutomationKeywords) * 100)
                        : 0}%
                    </span>
                  </div>
                  <div style={{
                    height: '8px',
                    backgroundColor: 'var(--border-color)',
                    borderRadius: '4px',
                    overflow: 'hidden'
                  }}>
                    <div style={{
                      width: `${totalAutomationKeywords > 0 ? ((currentAutomationIndex + 1) / totalAutomationKeywords) * 100 : 0}%`,
                      height: '100%',
                      backgroundColor: 'var(--primary)',
                      transition: 'width 0.3s ease'
                    }} />
                  </div>
                </div>
              )}

              {/* Results table */}
              <table className="table" style={{ width: '100%' }}>
                <thead>
                  <tr>
                    <th>Keyword</th>
                    <th>Platform</th>
                    <th>Status</th>
                    <th>Start Time</th>
                    <th>Found</th>
                    <th>Added</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {automationSearchResults.map((result, index) => (
                    <tr key={index}>
                      <td>
                        <button
                          className="btn-link"
                          onClick={() => {
                            setSelectedKeywordResult(result);
                            setShowKeywordResultsModal(true);
                            // Initialize status for added creators
                            const statusMap: Record<string, 'added'> = {};
                            result.addedCreators?.forEach((creator: string) => {
                              statusMap[creator] = 'added';
                              statusMap[creator.toLowerCase()] = 'added'; // Also add lowercase version
                            });
                            console.log('Added creators status map:', statusMap);
                            console.log('Added creators from result:', result.addedCreators);
                            setCreatorAddingStatus(statusMap);
                          }}
                          style={{
                            background: 'none',
                            border: 'none',
                            color: 'var(--primary)',
                            cursor: 'pointer',
                            textDecoration: 'underline'
                          }}
                        >
                          {result.keyword}
                        </button>
                      </td>
                      <td style={{ textAlign: 'center' }}>
                        <span style={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: '4px',
                          padding: '2px 8px',
                          borderRadius: '4px',
                          fontSize: '12px',
                          fontWeight: 'bold',
                          background: result.platform === 'tiktok' ?
                            'linear-gradient(135deg, #000000 0%, #333333 100%)' :
                            'linear-gradient(45deg, #f09433 0%,#e6683c 25%,#dc2743 50%,#cc2366 75%,#bc1888 100%)',
                          color: 'white'
                        }}>
                          {result.platform === 'tiktok' ? '📱' : '📷'} {result.platform?.toUpperCase() || 'TIKTOK'}
                        </span>
                      </td>
                      <td>
                        {result.status === 'searching' && (
                          <span style={{ color: '#3b82f6' }}>🔍 Searching...</span>
                        )}
                        {result.status === 'processing' && (
                          <span style={{ color: '#f59e0b' }}>
                            ⚙️ Processing{result.foundCount > 0 ? ` (${result.processedCreators || 0}/${result.foundCount})` : '...'}
                          </span>
                        )}
                        {result.status === 'completed' && (
                          <span style={{ color: '#10b981' }}>✅ Completed</span>
                        )}
                        {result.status === 'failed' && (
                          <span style={{ color: '#ef4444' }}>❌ Failed</span>
                        )}
                      </td>
                      <td>{new Date(result.startTime).toLocaleTimeString()}</td>
                      <td style={{ textAlign: 'center' }}>
                        {(result.status === 'searching' || result.status === 'processing') ? '-' : result.foundCount}
                      </td>
                      <td style={{ textAlign: 'center' }}>
                        <button
                          className="btn-link"
                          onClick={() => {
                            if (result.addedCount > 0) {
                              setSelectedAddedAccounts(result);
                              setShowAddedAccountsModal(true);
                            }
                          }}
                          style={{
                            background: 'none',
                            border: 'none',
                            color: result.addedCount > 0 ? 'var(--primary)' : 'inherit',
                            cursor: result.addedCount > 0 ? 'pointer' : 'default',
                            textDecoration: result.addedCount > 0 ? 'underline' : 'none'
                          }}
                          disabled={!result.addedCount}
                        >
                          {(result.status === 'searching' || result.status === 'processing') ? '-' : result.addedCount || 0}
                        </button>
                      </td>
                      <td>
                        <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                          {result.status === 'completed' && (
                            <button
                              className="btn btn-sm btn-secondary"
                              onClick={() => {
                                // Re-run single keyword
                                console.log('Re-run', result.keyword);
                              }}
                            >
                              Re-run
                            </button>
                          )}
                          <button
                            className="btn btn-sm btn-danger"
                            onClick={() => handleDeleteAutomationResult(result._id || result.searchHistoryId, index)}
                            style={{
                              background: '#ef4444',
                              color: 'white',
                              border: 'none',
                              padding: '4px 8px',
                              borderRadius: '4px',
                              fontSize: '12px'
                            }}
                          >
                            🗑️ Delete
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>

              {/* History Pagination */}
              {historyPagination.totalPages > 1 && (
                <div style={{
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  gap: 'var(--space-2)',
                  marginTop: 'var(--space-4)',
                  padding: 'var(--space-3)',
                  borderTop: '1px solid var(--border-primary)'
                }}>
                  <button
                    className="btn-secondary"
                    onClick={() => {
                      const newPage = 1;
                      setHistoryPagination(prev => ({ ...prev, page: newPage }));
                      fetchSearchHistory(false, newPage);
                    }}
                    disabled={historyPagination.page === 1}
                    style={{ padding: '6px 12px' }}
                  >
                    ««
                  </button>
                  <button
                    className="btn-secondary"
                    onClick={() => {
                      const newPage = historyPagination.page - 1;
                      setHistoryPagination(prev => ({ ...prev, page: newPage }));
                      fetchSearchHistory(false, newPage);
                    }}
                    disabled={historyPagination.page === 1}
                    style={{ padding: '6px 12px' }}
                  >
                    ‹
                  </button>
                  <span style={{
                    fontSize: '14px',
                    color: 'var(--text-secondary)',
                    minWidth: '200px',
                    textAlign: 'center'
                  }}>
                    Page {historyPagination.page} of {historyPagination.totalPages}
                    <span style={{ marginLeft: '8px', fontSize: '12px' }}>
                      ({historyPagination.total} total)
                    </span>
                  </span>
                  <button
                    className="btn-secondary"
                    onClick={() => {
                      const newPage = historyPagination.page + 1;
                      setHistoryPagination(prev => ({ ...prev, page: newPage }));
                      fetchSearchHistory(false, newPage);
                    }}
                    disabled={historyPagination.page === historyPagination.totalPages}
                    style={{ padding: '6px 12px' }}
                  >
                    ›
                  </button>
                  <button
                    className="btn-secondary"
                    onClick={() => {
                      const newPage = historyPagination.totalPages;
                      setHistoryPagination(prev => ({ ...prev, page: newPage }));
                      fetchSearchHistory(false, newPage);
                    }}
                    disabled={historyPagination.page === historyPagination.totalPages}
                    style={{ padding: '6px 12px' }}
                  >
                    »»
                  </button>
                </div>
              )}

            </div>
          )}
        </div>
      )}

      {/* Related Keywords Modal */}
      <RelatedKeywordsModal
        isOpen={showRelatedKeywordsModal}
        onClose={() => setShowRelatedKeywordsModal(false)}
        keyword={relatedKeywordTarget}
        onKeywordsAdded={fetchKeywords}
      />

      {/* Translated Keywords Modal */}
      <TranslatedKeywordsModal
        isOpen={showTranslatedKeywordsModal}
        onClose={() => setShowTranslatedKeywordsModal(false)}
        keyword={translatedKeywordTarget}
        onKeywordsAdded={fetchKeywords}
      />

      {/* Alternative Keywords Modal */}
      <AlternativeKeywordsModal
        isOpen={showAlternativeKeywordsModal}
        onClose={() => setShowAlternativeKeywordsModal(false)}
        keyword={alternativeKeywordTarget}
        onKeywordsAdded={fetchKeywords}
      />

      {/* TikTok Automation Modal */}
      {showTikTokAutomationModal && createPortal(
        <div className="modal-overlay" onClick={() => !isAutomationRunning && setShowTikTokAutomationModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '900px', maxHeight: '90vh', overflow: 'auto' }}>
            <h2 style={{ marginBottom: 'var(--space-4)' }}>TikTok Automation</h2>

            {/* Show settings */}
            {(
              <>
                {/* Search Type Selection - Same style as TikTok Creators Search */}
                <div className="mb-4">
                  <label className="block text-sm font-medium mb-2">Search Type</label>
                  <div className="space-y-2">
                    <label className="flex items-start gap-2 cursor-pointer">
                      <input
                        type="radio"
                        value="keyword"
                        checked={tiktokAutomationSearchType === 'keyword'}
                        onChange={(e) => setTiktokAutomationSearchType(e.target.value as 'keyword')}
                        className="mt-1"
                      />
                      <div>
                        <div className="font-medium">Keyword Search</div>
                        <div className="text-xs text-gray-500">Search for posts containing keywords</div>
                      </div>
                    </label>

                    <label className="flex items-start gap-2 cursor-pointer">
                      <input
                        type="radio"
                        value="hashtag"
                        checked={tiktokAutomationSearchType === 'hashtag'}
                        onChange={(e) => setTiktokAutomationSearchType(e.target.value as 'hashtag')}
                        className="mt-1"
                      />
                      <div>
                        <div className="font-medium">Hashtag Search</div>
                        <div className="text-xs text-gray-500">Search for posts with specific hashtag</div>
                      </div>
                    </label>

                    <label className="flex items-start gap-2 cursor-pointer">
                      <input
                        type="radio"
                        value="full-keyword"
                        checked={tiktokAutomationSearchType === 'full-keyword'}
                        onChange={(e) => setTiktokAutomationSearchType(e.target.value as 'full-keyword')}
                        className="mt-1"
                      />
                      <div>
                        <div className="font-medium">Full Keyword Search</div>
                        <div className="text-xs text-gray-500">Get all posts with keyword (auto-pagination)</div>
                      </div>
                    </label>

                    <label className="flex items-start gap-2 cursor-pointer">
                      <input
                        type="radio"
                        value="full-hashtag"
                        checked={tiktokAutomationSearchType === 'full-hashtag'}
                        onChange={(e) => setTiktokAutomationSearchType(e.target.value as 'full-hashtag')}
                        className="mt-1"
                      />
                      <div>
                        <div className="font-medium">Full Hashtag Search</div>
                        <div className="text-xs text-gray-500">Get all posts with hashtag within specified days</div>
                      </div>
                    </label>
                  </div>
                </div>

                {/* Search Parameters - Grid layout like TikTok Creators Search */}
                <div className="grid grid-cols-2 gap-4 mb-4">
                  {/* Period - for keyword, full-keyword and hashtag searches */}
                  {(tiktokAutomationSearchType === 'keyword' || tiktokAutomationSearchType === 'full-keyword' || tiktokAutomationSearchType === 'hashtag') && (
                    <div>
                      <label className="block text-sm font-medium mb-1">Period</label>
                      <select
                        value={tiktokAutomationPeriod}
                        onChange={(e) => setTiktokAutomationPeriod(e.target.value)}
                        className="input w-full"
                      >
                        <option value="0">Today</option>
                        <option value="1">Yesterday</option>
                        <option value="7">Last 7 days</option>
                        <option value="30">Last 30 days</option>
                        <option value="90">Last 90 days</option>
                        <option value="180">Last 180 days</option>
                      </select>
                    </div>
                  )}

                  {/* Full Hashtag Search parameters */}
                  {tiktokAutomationSearchType === 'full-hashtag' && (
                    <>
                      <div>
                        <label className="block text-sm font-medium mb-1">Days Filter</label>
                        <input
                          type="number"
                          value={tiktokAutomationDays || 180}
                          onChange={(e) => setTiktokAutomationDays(parseInt(e.target.value) || 180)}
                          className="input w-full"
                          placeholder="180"
                          min="1"
                          max="365"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium mb-1">Max Cursor <span className="text-xs text-gray-500">(최대 10000)</span></label>
                        <input
                          type="number"
                          value={tiktokAutomationMaxCursor}
                          onChange={(e) => setTiktokAutomationMaxCursor(Math.min(10000, parseInt(e.target.value) || 10000))}
                          className="input w-full"
                          placeholder="10000"
                          min="100"
                          max="10000"
                          step="100"
                        />
                      </div>
                    </>
                  )}

                  {/* Minimum Views - for all types */}
                  <div>
                    <label className="block text-sm font-medium mb-1">
                      Minimum Views <span className="text-xs text-gray-500">(optional)</span>
                    </label>
                    <input
                      type="number"
                      value={tiktokAutomationMinViews || ''}
                      onChange={(e) => setTiktokAutomationMinViews(e.target.value ? parseInt(e.target.value) : 0)}
                      className="input w-full"
                      placeholder="Enter minimum views"
                    />
                  </div>

                  {/* Email filter - Always enabled for automation */}
                  <div className="flex items-end">
                    <label className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        checked={true}
                        disabled
                      />
                      <span className="text-sm">Only creators with emails</span>
                    </label>
                  </div>
                </div>

                {/* Automation-specific settings */}
                <div className="mb-4">
                  <label className="block text-sm font-medium mb-1">
                    Skip keywords searched in last X days:
                  </label>
                  <input
                    type="number"
                    value={tiktokAutomationSkipDays}
                    onChange={(e) => {
                      setTiktokAutomationSkipDays(Number(e.target.value));
                    }}
                    className="input w-full"
                    placeholder="14"
                  />
                </div>

                <div className="mb-4">
                  <div className="flex justify-between items-center mb-2">
                    <label className="text-sm font-medium">Target Keywords ({tiktokAutomationKeywords.length})</label>
                    {tiktokAutomationKeywords.length > 0 && (
                      <div className="flex items-center gap-2">
                        <label className="flex items-center gap-1 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={selectedAutomationKeywords.size === tiktokAutomationKeywords.length}
                            onChange={(e) => {
                              if (e.target.checked) {
                                // Select all
                                setSelectedAutomationKeywords(new Set(tiktokAutomationKeywords.map(k => k._id)));
                              } else {
                                // Deselect all
                                setSelectedAutomationKeywords(new Set());
                              }
                            }}
                          />
                          <span className="text-sm">
                            {selectedAutomationKeywords.size === tiktokAutomationKeywords.length ? 'Deselect All' : 'Select All'}
                          </span>
                        </label>
                        {selectedAutomationKeywords.size > 0 && (
                          <span className="text-xs text-gray-500">
                            ({selectedAutomationKeywords.size} selected)
                          </span>
                        )}
                      </div>
                    )}
                  </div>
                  {loadingAutomationKeywords ? (
                    <div>Loading keywords...</div>
                  ) : (
                    <div style={{
                      maxHeight: '200px',
                      overflow: 'auto',
                      border: '1px solid var(--border-color)',
                      borderRadius: '4px',
                      padding: 'var(--space-2)'
                    }}>
                      {tiktokAutomationKeywords.length === 0 ? (
                        <div style={{ padding: 'var(--space-3)', textAlign: 'center', color: 'var(--text-secondary)' }}>
                          No keywords found matching criteria
                        </div>
                      ) : (
                        <table style={{ width: '100%', fontSize: '14px' }}>
                          <thead>
                            <tr>
                              <th style={{ width: '40px', textAlign: 'center', padding: '6px' }}>✓</th>
                              <th style={{ textAlign: 'left', padding: '6px' }}>Keyword</th>
                              <th style={{ textAlign: 'left', padding: '6px' }}>Group</th>
                              <th style={{ textAlign: 'left', padding: '6px' }}>Last Searched</th>
                            </tr>
                          </thead>
                          <tbody>
                            {tiktokAutomationKeywords.map((keyword: any) => (
                              <tr key={keyword._id}>
                                <td style={{ textAlign: 'center', padding: '6px' }}>
                                  <input
                                    type="checkbox"
                                    checked={selectedAutomationKeywords.has(keyword._id)}
                                    onChange={(e) => {
                                      const newSelected = new Set(selectedAutomationKeywords);
                                      if (e.target.checked) {
                                        newSelected.add(keyword._id);
                                      } else {
                                        newSelected.delete(keyword._id);
                                      }
                                      setSelectedAutomationKeywords(newSelected);
                                    }}
                                  />
                                </td>
                                <td style={{ padding: '6px' }}>{keyword.keyword}</td>
                                <td style={{ padding: '6px' }}>{keyword.group || 'none'}</td>
                                <td style={{ padding: '6px' }}>
                                  {keyword.tiktokSearchTime
                                    ? new Date(keyword.tiktokSearchTime).toLocaleDateString()
                                    : 'Never'}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      )}
                    </div>
                  )}
                </div>
              </>
            )}


            <div style={{ display: 'flex', gap: 'var(--space-2)', justifyContent: 'flex-end' }}>
              <button
                className="btn btn-secondary"
                onClick={() => {
                  setShowTikTokAutomationModal(false);
                  setAutomationSearchResults([]);
                  setCurrentAutomationIndex(0);
                }}
              >
                Close
              </button>
              <button
                className="btn btn-primary"
                onClick={() => {
                  setShowTikTokAutomationModal(false);
                  runTikTokAutomation();
                }}
                disabled={selectedAutomationKeywords.size === 0}
              >
                Start Automation ({selectedAutomationKeywords.size} keywords)
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* Added Accounts Modal */}
      {showAddedAccountsModal && selectedAddedAccounts && createPortal(
        <div className="modal-overlay" onClick={() => setShowAddedAccountsModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '600px' }}>
            <h2 style={{ marginBottom: 'var(--space-4)' }}>
              Added Accounts for "{selectedAddedAccounts.keyword}"
            </h2>

            <div style={{ marginBottom: 'var(--space-3)' }}>
              <span style={{ color: 'var(--text-secondary)' }}>
                Total Added: {selectedAddedAccounts.addedCount || 0}
              </span>
            </div>

            {selectedAddedAccounts.addedCreators && selectedAddedAccounts.addedCreators.length > 0 ? (
              <div style={{
                maxHeight: '400px',
                overflow: 'auto',
                border: '1px solid var(--border-color)',
                borderRadius: '4px',
                padding: 'var(--space-2)'
              }}>
                <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                  {selectedAddedAccounts.addedCreators.map((creator: string, index: number) => (
                    <li key={index} style={{
                      padding: '8px 12px',
                      borderBottom: '1px solid var(--border-color)',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px'
                    }}>
                      <span style={{ color: '#10b981' }}>✅</span>
                      <a
                        href={`https://www.tiktok.com/@${creator}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{ color: 'var(--primary)', textDecoration: 'none' }}
                      >
                        @{creator}
                      </a>
                    </li>
                  ))}
                </ul>
              </div>
            ) : (
              <p style={{ textAlign: 'center', color: 'var(--text-secondary)' }}>
                No creator details available
              </p>
            )}

            <div style={{ marginTop: 'var(--space-4)', textAlign: 'right' }}>
              <button
                className="btn btn-secondary"
                onClick={() => setShowAddedAccountsModal(false)}
              >
                Close
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* Keyword Results Modal */}
      {showKeywordResultsModal && selectedKeywordResult && createPortal(
        <div className="modal-overlay" onClick={() => setShowKeywordResultsModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '1200px', maxHeight: '90vh', display: 'flex', flexDirection: 'column' }}>
            <div style={{ padding: 'var(--space-4)', borderBottom: '1px solid var(--border-color)' }}>
              <h2 style={{ marginBottom: 'var(--space-3)' }}>
                Search Results: "{selectedKeywordResult.keyword}"
              </h2>

              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 'var(--space-3)' }}>
                <div style={{ padding: 'var(--space-2)', backgroundColor: 'var(--bg-secondary)', borderRadius: '4px' }}>
                  <span style={{ color: 'var(--text-secondary)', fontSize: '12px' }}>Status</span>
                  <div style={{ fontSize: '16px', fontWeight: 'bold' }}>
                    {selectedKeywordResult.status === 'searching' && <span style={{ color: '#3b82f6' }}>🔍 Searching</span>}
                    {selectedKeywordResult.status === 'processing' && (
                      <span style={{ color: '#f59e0b' }}>
                        ⚙️ Processing{selectedKeywordResult.foundCount > 0 ? ` (${selectedKeywordResult.processedCreators || 0}/${selectedKeywordResult.foundCount})` : ''}
                      </span>
                    )}
                    {selectedKeywordResult.status === 'completed' && <span style={{ color: '#10b981' }}>✅ Completed</span>}
                    {selectedKeywordResult.status === 'failed' && <span style={{ color: '#ef4444' }}>❌ Failed</span>}
                  </div>
                </div>
                <div style={{ padding: 'var(--space-2)', backgroundColor: 'var(--bg-secondary)', borderRadius: '4px' }}>
                  <span style={{ color: 'var(--text-secondary)', fontSize: '12px' }}>Found</span>
                  <div style={{ fontSize: '16px', fontWeight: 'bold' }}>{selectedKeywordResult.foundCount || 0}</div>
                </div>
                <div style={{ padding: 'var(--space-2)', backgroundColor: 'var(--bg-secondary)', borderRadius: '4px' }}>
                  <span style={{ color: 'var(--text-secondary)', fontSize: '12px' }}>Added</span>
                  <div style={{ fontSize: '16px', fontWeight: 'bold' }}>{selectedKeywordResult.addedCount || 0}</div>
                </div>
                <div style={{ padding: 'var(--space-2)', backgroundColor: 'var(--bg-secondary)', borderRadius: '4px' }}>
                  <span style={{ color: 'var(--text-secondary)', fontSize: '12px' }}>With Email</span>
                  <div style={{ fontSize: '16px', fontWeight: 'bold' }}>{selectedKeywordResult.withEmails || 0}</div>
                </div>
              </div>

              {/* Search Logs Section - Using new SearchLogsSummary component */}
              <div style={{ marginTop: 'var(--space-3)' }}>
                <h4 style={{ fontSize: '12px', marginBottom: '8px', color: '#888' }}>Search Progress Summary</h4>
                <SearchLogsSummary
                  logs={selectedKeywordResult.searchLogs || []}
                  showRawLogs={true}
                />
              </div>
            </div>

            {/* Search results data */}
            <div style={{ flex: 1, overflow: 'auto', padding: 'var(--space-4)' }}>
              {selectedKeywordResult.data && selectedKeywordResult.data.length > 0 ? (
                <div>
                  <div style={{ marginBottom: 'var(--space-2)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <h3>Creators Found ({selectedKeywordResult.data.length})</h3>
                    <span style={{ fontSize: '12px', color: 'var(--text-secondary)' }}>
                      Showing all results • {selectedKeywordResult.withEmails || 0} with email
                    </span>
                  </div>

                  <div style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))',
                    gap: 'var(--space-4)',
                    marginTop: 'var(--space-4)'
                  }}>
                    {selectedKeywordResult.data.map((post: any, index: number) => {
                      // Support both authorMeta and authorInfos structures
                      const author = post.authorMeta || post.author || post.authorInfos || {};
                      const stats = post.statistics || post.stats || post.authorStats || post.itemInfos || {};
                      const creatorName = author.name || author.unique_id || author.uniqueId || post.author?.unique_id;

                      // Debug logging
                      if (index === 0) {
                        console.log('First creator check:', {
                          creatorName,
                          addedCreators: selectedKeywordResult.addedCreators,
                          statusMap: creatorAddingStatus
                        });
                      }
                      const signature = author.signature || '';
                      const emailMatch = signature.match(/([a-zA-Z0-9._-]+@[a-zA-Z0-9._-]+\.[a-zA-Z0-9_-]+)/);
                      const email = emailMatch ? emailMatch[1] : '';
                      // Check if creator was added (case-insensitive comparison)
                      const wasAdded = selectedKeywordResult.addedCreators?.some((added: string) =>
                        added.toLowerCase() === creatorName?.toLowerCase()
                      );
                      const addingStatus = creatorAddingStatus[creatorName] ||
                                          creatorAddingStatus[creatorName?.toLowerCase()] ||
                                          (wasAdded ? 'added' : 'waiting');
                      // Support both videoMeta and itemInfos structures
                      const video = post.videoMeta || post.video || post.itemInfos || {};
                      const coverUrl = video.coverUrl || video.cover || video.originCover ||
                                      (video.covers && video.covers[0]) ||
                                      (post.itemInfos?.covers && post.itemInfos.covers[0]) ||
                                      post.covers?.default || post.video?.cover?.url_list?.[0] || '';

                      return (
                        <div key={index} style={{
                          backgroundColor: '#1a1a1a',
                          borderRadius: '8px',
                          overflow: 'hidden',
                          border:
                            addingStatus === 'added' ? '2px solid #10b981' :
                            addingStatus === 'adding' ? '2px solid #f59e0b' :
                            addingStatus === 'error' ? '2px solid #ef4444' :
                            '1px solid var(--border-color)',
                          position: 'relative',
                          cursor: 'pointer'
                        }}
                        onClick={() => {
                          const postId = post.id || post.postId || post.itemInfos?.id || video.id;
                          if (creatorName && postId) {
                            const postUrl = generateTiktokPostUrl(creatorName, postId);
                            window.open(postUrl, '_blank');
                          }
                        }}>
                          {/* Status Badge */}
                          <div style={{
                            position: 'absolute',
                            top: '8px',
                            right: '8px',
                            backgroundColor:
                              addingStatus === 'added' ? '#10b981' :
                              addingStatus === 'adding' ? '#f59e0b' :
                              addingStatus === 'error' ? '#ef4444' :
                              '#6b7280',
                            color: 'white',
                            padding: '2px 8px',
                            borderRadius: '4px',
                            fontSize: '10px',
                            fontWeight: 'bold',
                            zIndex: 10,
                            display: 'flex',
                            alignItems: 'center',
                            gap: '4px'
                          }}>
                            {addingStatus === 'added' ? 'ADDED' :
                             addingStatus === 'adding' ? 'ADDING' :
                             addingStatus === 'error' ? 'ERROR' :
                             'WAITING'}
                          </div>

                          {/* Video Thumbnail */}
                          {coverUrl ? (
                            <div style={{ position: 'relative', width: '100%', paddingBottom: '177.78%' }}>
                              <img
                                src={coverUrl}
                                alt="Video thumbnail"
                                loading="lazy"
                                style={{
                                  position: 'absolute',
                                  top: 0,
                                  left: 0,
                                  width: '100%',
                                  height: '100%',
                                  objectFit: 'cover'
                                }}
                                onError={(e) => {
                                  e.currentTarget.style.display = 'none';
                                  e.currentTarget.parentElement!.style.backgroundColor = '#333';
                                }}
                              />
                              <div style={{
                                position: 'absolute',
                                bottom: '8px',
                                left: '8px',
                                right: '8px',
                                backgroundColor: 'rgba(0, 0, 0, 0.8)',
                                color: '#fff',
                                padding: '4px 8px',
                                borderRadius: '4px',
                                fontSize: '10px',
                                display: 'flex',
                                justifyContent: 'space-around',
                                alignItems: 'center',
                                gap: '2px'
                              }}>
                                <span style={{ display: 'flex', alignItems: 'center', gap: '2px' }}>
                                  👁️ {(stats.playCount || post.playCount || stats.play_count || post.itemInfos?.playCount || 0) >= 1000000
                                    ? `${((stats.playCount || post.playCount || stats.play_count || post.itemInfos?.playCount || 0) / 1000000).toFixed(1)}M`
                                    : (stats.playCount || post.playCount || stats.play_count || post.itemInfos?.playCount || 0) >= 1000
                                    ? `${((stats.playCount || post.playCount || stats.play_count || post.itemInfos?.playCount || 0) / 1000).toFixed(1)}K`
                                    : (stats.playCount || post.playCount || stats.play_count || post.itemInfos?.playCount || 0)}
                                </span>
                                <span style={{ display: 'flex', alignItems: 'center', gap: '2px' }}>
                                  ❤️ {(stats.diggCount || post.diggCount || stats.digg_count || post.itemInfos?.diggCount || 0) >= 1000000
                                    ? `${((stats.diggCount || post.diggCount || stats.digg_count || post.itemInfos?.diggCount || 0) / 1000000).toFixed(1)}M`
                                    : (stats.diggCount || post.diggCount || stats.digg_count || post.itemInfos?.diggCount || 0) >= 1000
                                    ? `${((stats.diggCount || post.diggCount || stats.digg_count || post.itemInfos?.diggCount || 0) / 1000).toFixed(1)}K`
                                    : (stats.diggCount || post.diggCount || stats.digg_count || post.itemInfos?.diggCount || 0)}
                                </span>
                                <span style={{ display: 'flex', alignItems: 'center', gap: '2px' }}>
                                  💬 {(stats.commentCount || post.commentCount || stats.comment_count || post.itemInfos?.commentCount || 0) >= 1000
                                    ? `${((stats.commentCount || post.commentCount || stats.comment_count || post.itemInfos?.commentCount || 0) / 1000).toFixed(1)}K`
                                    : (stats.commentCount || post.commentCount || stats.comment_count || post.itemInfos?.commentCount || 0)}
                                </span>
                              </div>
                            </div>
                          ) : (
                            <div style={{
                              width: '100%',
                              paddingBottom: '177.78%',
                              backgroundColor: '#333',
                              position: 'relative'
                            }}>
                              <span style={{
                                position: 'absolute',
                                top: '50%',
                                left: '50%',
                                transform: 'translate(-50%, -50%)',
                                color: '#666'
                              }}>No Thumbnail</span>
                            </div>
                          )}

                          <div style={{ padding: '12px' }}>
                            <div style={{
                              fontSize: '13px',
                              fontWeight: 'bold',
                              color: '#fff',
                              marginBottom: '4px',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap'
                            }}>
                              <a
                                href={`https://www.tiktok.com/@${creatorName}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                style={{ color: '#fff', textDecoration: 'none' }}
                                onClick={(e) => e.stopPropagation()}
                              >
                                @{creatorName}
                              </a>
                            </div>
                            <div style={{
                              fontSize: '12px',
                              color: '#aaa',
                              marginBottom: '8px'
                            }}>
                              {(() => {
                                const followerCount = author.fans || author.followers || stats.followerCount ||
                                                     stats.followers || author.followerCount || post.authorStats?.followerCount;
                                // For Full Hashtag Search, follower data is not available from API
                                if (followerCount === 0 || followerCount === undefined) {
                                  return 'Followers: N/A';
                                }
                                return `${followerCount.toLocaleString()} followers`;
                              })()}
                            </div>

                            {/* Email Display */}
                            {email && (
                              <div style={{
                                backgroundColor: '#00ff00',
                                color: '#000',
                                borderRadius: '4px',
                                padding: '6px 8px',
                                fontSize: '11px',
                                fontWeight: 'bold',
                                marginTop: '4px',
                                textAlign: 'center',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap'
                              }}
                              title={email}>
                                📧 {email}
                              </div>
                            )}

                            <div style={{
                              fontSize: '11px',
                              color: '#888',
                              marginTop: '8px',
                              overflow: 'hidden',
                              display: '-webkit-box',
                              WebkitLineClamp: 2,
                              WebkitBoxOrient: 'vertical',
                              lineHeight: '1.4'
                            }}>
                              {post.text || post.desc || post.itemInfos?.text || video.text || 'No description'}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              ) : (
                <div style={{ textAlign: 'center', padding: 'var(--space-8)', color: 'var(--text-secondary)' }}>
                  <p>No search results available</p>
                  <p style={{ fontSize: '12px', marginTop: '8px' }}>This search may not have completed or no data was saved.</p>
                </div>
              )}
            </div>

            <div style={{ marginTop: 'var(--space-4)', textAlign: 'right' }}>
              <button
                className="btn btn-secondary"
                onClick={() => setShowKeywordResultsModal(false)}
              >
                Close
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}

    </div>
  );
};

export default Find;